import { Link, router } from "@inertiajs/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FlickeringGrid } from "../../components/magicui/flickering-grid";
import { AnimatedBeam } from "../../components/magicui/animated-beam";
import { Iphone } from "../../components/magicui/iphone";
import { MagicCard } from "../../components/magicui/magic-card";
import { Marquee } from "../../components/magicui/marquee";
import { ShimmerButton } from "../../components/magicui/shimmer-button";
import { SparklesText } from "../../components/magicui/sparkles-text";
import WaveformAudioPlayer from "../../components/audio/waveform-audio-player";
import { cn } from "../../lib/utils";

const STORAGE_KEY = "ugc-influencer-playground-react-v2";
const VALID_ROUTES = ["/generation", "/account", "/connections", "/billing", "/settings", "/inspiration"];

const providerConfig = {
  fal: { label: "fal.ai" },
  wavespeed: { label: "Wavespeed" },
  openrouter: { label: "OpenRouter" },
  elevenlabs: { label: "ElevenLabs" }
};

const FAL_IMAGE_MODELS = [
  "fal-ai/nano-banana-pro",
  "fal-ai/nano-banana-2",
  "fal-ai/imagen4/preview",
  "fal-ai/bytedance/seedream/v4.5/text-to-image"
];
const FAL_IMAGE_EDIT_MODELS = [
  "fal-ai/nano-banana-pro/edit",
  "fal-ai/nano-banana-2/edit",
  "fal-ai/bytedance/seedream/v4.5/edit",
  "wan/v2.6/image-to-image"
];
const DEFAULT_FAL_IMAGE_EDIT_MODEL = "fal-ai/nano-banana-pro/edit";
const FAL_VIDEO_MODELS = ["fal-ai/creatify/aurora"];
const VIDEO_ASPECT_RATIOS_BY_MODEL = {
  "fal-ai/creatify/aurora": ["9:16", "16:9"]
};
const VIDEO_RESOLUTION_OPTIONS_BY_MODEL = {
  "fal-ai/creatify/aurora": ["480p", "720p"]
};
const WAVESPEED_IMAGE_MODELS = [
  "google/nano-banana/text-to-image",
  "google/nano-banana-pro/text-to-image",
  "google/nano-banana-2/text-to-image",
  "wavespeed-ai/z-image/turbo",
  "bytedance/seedream-v4.5",
  "bytedance/seedream-v4"
];
const WAVESPEED_IMAGE_EDIT_MODELS = [
  "google/nano-banana-pro/edit",
  "google/nano-banana-2/edit",
  "bytedance/seedream-v4.5/edit",
  "alibaba/wan-2.6/image-edit"
];
const DEFAULT_WAVESPEED_IMAGE_EDIT_MODEL = "google/nano-banana-pro/edit";
const OPENROUTER_COPY_MODELS = [
  "openai/gpt-4o-mini",
  "openai/gpt-4.1-mini",
  "anthropic/claude-3.5-sonnet",
  "google/gemini-2.0-flash-001"
];
const COPY_LANGUAGE_OPTIONS = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "pt", name: "Portuguese" },
  { code: "zh", name: "Chinese (Mandarin)" },
  { code: "ar", name: "Arabic" },
  { code: "hi", name: "Hindi" },
  { code: "ru", name: "Russian" }
];
const TTS_ESTIMATED_WORDS_PER_SECOND = 2.8;
const TTS_ESTIMATED_CHARS_PER_SECOND = 16;
const TTS_MIN_DURATION_RATIO = 0.6;
const TTS_MAX_DURATION_RATIO = 1.05;
const FAL_GUIDANCE_SCALE_MIN = 0;
const FAL_GUIDANCE_SCALE_MAX = 5;
const FAL_GUIDANCE_SCALE_STEP = 0.1;
const TIKTOK_VIDEO_HELP_IMAGE = "/help-images/where-is-my-video.jpg";

function normalizeFalVideoModelId(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return FAL_VIDEO_MODELS[0];
  if (normalized === "fal-ai/creatify" || normalized === "fal-ai/creatify/") {
    return "fal-ai/creatify/aurora";
  }
  return normalized;
}

function getVideoResolutionOptions(model) {
  return VIDEO_RESOLUTION_OPTIONS_BY_MODEL[normalizeFalVideoModelId(model)] || ["720p"];
}
const COPY_LANGUAGE_CODES = new Set(COPY_LANGUAGE_OPTIONS.map((item) => item.code));
const ELEVENLABS_TTS_MODELS = [
  "eleven_v3",
  "eleven_flash_v2_5",
  "eleven_multilingual_v2",
  "eleven_turbo_v2_5"
];
const DEFAULT_ASPECT_RATIO = "9:16";
const DEFAULT_IMAGE_ASPECT_RATIOS = ["1:1", "3:2", "2:3", "3:4", "4:3", "4:5", "5:4", "9:16", "16:9", "21:9", "1:4", "4:1", "1:8", "8:1"];
const IMAGE_ASPECT_RATIOS_BY_MODEL = {
  "fal-ai/nano-banana-pro": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "fal-ai/imagen4/preview": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "fal-ai/bytedance/seedream/v4.5/text-to-image": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "fal-ai/nano-banana-pro/edit": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "fal-ai/nano-banana-2": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "fal-ai/nano-banana-2/edit": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "fal-ai/bytedance/seedream/v4.5/edit": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "wan/v2.6/image-to-image": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "google/nano-banana/text-to-image": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "google/nano-banana-pro/text-to-image": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "google/nano-banana-2/text-to-image": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "google/nano-banana-pro/edit": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "google/nano-banana-2/edit": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "bytedance/seedream-v4.5/edit": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "alibaba/wan-2.6/image-edit": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "wavespeed-ai/z-image/turbo": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "bytedance/seedream-v4.5": [...DEFAULT_IMAGE_ASPECT_RATIOS],
  "bytedance/seedream-v4": [...DEFAULT_IMAGE_ASPECT_RATIOS]
};
const FAL_IMAGE_RESOLUTION_OPTIONS_BY_MODEL = {
  "fal-ai/nano-banana-pro": ["1K", "2K", "4K"],
  "fal-ai/nano-banana-2": ["0.5K", "1K", "2K", "4K"],
  "fal-ai/nano-banana-pro/edit": ["1K", "2K", "4K"],
  "fal-ai/nano-banana-2/edit": ["0.5K", "1K", "2K", "4K"]
};
const WAVESPEED_IMAGE_PARAMS_BY_MODEL = {
  "google/nano-banana/text-to-image": {
    outputFormats: ["png", "jpeg"],
    resolutions: [],
    supportsImage: false,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: false
  },
  "google/nano-banana-pro/text-to-image": {
    outputFormats: ["png", "jpeg"],
    resolutions: ["1K", "2K", "4K"],
    supportsImage: false,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: false
  },
  "google/nano-banana-2/text-to-image": {
    outputFormats: ["png", "jpeg"],
    resolutions: ["1K", "2K", "4K"],
    supportsImage: false,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: false
  },
  "google/nano-banana-pro/edit": {
    outputFormats: ["png", "jpeg"],
    resolutions: ["1K", "2K", "4K"],
    supportsImage: true,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: false
  },
  "google/nano-banana-2/edit": {
    outputFormats: ["png", "jpeg"],
    resolutions: ["1K", "2K", "4K"],
    supportsImage: true,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: false
  },
  "bytedance/seedream-v4.5/edit": {
    outputFormats: [],
    resolutions: [],
    supportsImage: true,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: true
  },
  "alibaba/wan-2.6/image-edit": {
    outputFormats: ["jpeg", "png", "webp"],
    resolutions: [],
    supportsImage: true,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: true
  },
  "wavespeed-ai/z-image/turbo": {
    outputFormats: ["jpeg", "png", "webp"],
    resolutions: [],
    supportsImage: true,
    supportsStrength: true,
    supportsSeed: true,
    supportsEnableSyncMode: false,
    supportsEnableBase64Output: false,
    supportsSizeOverride: true
  },
  "bytedance/seedream-v4.5": {
    outputFormats: [],
    resolutions: [],
    supportsImage: false,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: true
  },
  "bytedance/seedream-v4": {
    outputFormats: [],
    resolutions: [],
    supportsImage: false,
    supportsStrength: false,
    supportsSeed: false,
    supportsEnableSyncMode: true,
    supportsEnableBase64Output: true,
    supportsSizeOverride: true
  }
};
const DEFAULT_ELEVENLABS_SETTINGS = {
  voiceScope: "my_generated"
};
const VOICE_CATEGORY_ORDER = ["professional", "cloned", "generated", "premade", "default", "community", "saved", "workspace", "uncategorized"];
const VOICE_CATEGORY_LABELS = {
  professional: "Professional",
  cloned: "Cloned",
  generated: "Generated",
  premade: "Premade",
  default: "Default",
  community: "Community",
  saved: "Saved",
  workspace: "Workspace",
  uncategorized: "Other"
};

const initialPipeline = {
  image: { approved: false, url: "" },
  imageEdit: { approved: false, skipped: false, url: "" },
  copy: { approved: false, text: "" },
  audio: { approved: false, url: "" },
  video: { url: "" }
};

const initialOperationCosts = {
  image: null,
  imageEdit: null,
  copy: null,
  copyEnhance: null,
  ttsEstimate: null,
  tts: null,
  video: null
};

const initialWizardUiState = {
  activeStep: 1,
  wizardImageMode: "",
  wizardEditIntent: "",
  wizardStarted: false,
  wizardExpanded: true
};

function hasWizardProgress(pipeline = initialPipeline) {
  return Boolean(
    pipeline?.image?.url ||
      pipeline?.imageEdit?.url ||
      pipeline?.imageEdit?.approved ||
      pipeline?.imageEdit?.skipped ||
      pipeline?.copy?.text ||
      pipeline?.copy?.approved ||
      pipeline?.audio?.url ||
      pipeline?.video?.url
  );
}

function getFallbackWizardImageMode(pipeline = initialPipeline) {
  return pipeline?.image?.url ? "generate" : "";
}

function getFallbackWizardEditIntent(pipeline = initialPipeline) {
  if (pipeline?.imageEdit?.approved || pipeline?.imageEdit?.url) return "yes";
  if (pipeline?.imageEdit?.skipped) return "no";
  return "";
}

function getFallbackActiveStep(pipeline = initialPipeline, wizardEditIntent = "") {
  const shouldRunImageEdit = wizardEditIntent !== "no";

  if (pipeline?.video?.url) return 5;
  if (pipeline?.audio?.approved) return 5;
  if (pipeline?.audio?.url) return 4;
  if (pipeline?.copy?.approved) return 4;
  if (String(pipeline?.copy?.text || "").trim()) return 3;

  if (!shouldRunImageEdit) {
    if (pipeline?.image?.approved) return 3;
    if (pipeline?.image?.url) return 1;
    return 1;
  }

  if (pipeline?.imageEdit?.approved || pipeline?.imageEdit?.skipped) return 3;
  if (pipeline?.imageEdit?.url) return 2;
  if (pipeline?.image?.approved) return 2;
  if (pipeline?.image?.url) return 1;
  return 1;
}

function buildWizardUiState(pipeline = initialPipeline, ui = initialWizardUiState) {
  const nextImageMode = ["generate", "upload"].includes(ui?.wizardImageMode)
    ? ui.wizardImageMode
    : getFallbackWizardImageMode(pipeline);
  const nextEditIntent = ["yes", "no"].includes(ui?.wizardEditIntent)
    ? ui.wizardEditIntent
    : getFallbackWizardEditIntent(pipeline);
  const fallbackActiveStep = getFallbackActiveStep(pipeline, nextEditIntent);
  const hasProgress = hasWizardProgress(pipeline);
  const nextStarted = typeof ui?.wizardStarted === "boolean"
    ? ui.wizardStarted
    : hasProgress || Boolean(nextImageMode && nextEditIntent);
  const nextExpanded = typeof ui?.wizardExpanded === "boolean" ? (nextStarted ? ui.wizardExpanded : true) : !nextStarted;
  const requestedActiveStep = Number(ui?.activeStep);
  const nextActiveStep = nextStarted
    ? Math.min(6, Math.max(1, Number.isInteger(requestedActiveStep) ? requestedActiveStep : fallbackActiveStep))
    : 1;

  return {
    activeStep: nextEditIntent === "no" && nextActiveStep === 2 ? 1 : nextActiveStep,
    wizardImageMode: nextImageMode,
    wizardEditIntent: nextEditIntent,
    wizardStarted: nextStarted,
    wizardExpanded: nextExpanded
  };
}

const initialLoadingState = {
  image: false,
  imageEdit: false,
  copy: false,
  tts: false,
  video: false,
  publishInstagram: false,
  publishTiktok: false,
  voices: false,
  enhance: false,
  videoPromptOptimize: false
};

const initialSettings = {
  apiKeys: {
    fal: "",
    wavespeed: "",
    openrouter: "",
    elevenlabs: ""
  },
  providerModels: {
    fal: [...FAL_IMAGE_MODELS, ...FAL_IMAGE_EDIT_MODELS, ...FAL_VIDEO_MODELS],
    wavespeed: [...WAVESPEED_IMAGE_MODELS, ...WAVESPEED_IMAGE_EDIT_MODELS],
    openrouter: [...OPENROUTER_COPY_MODELS],
    elevenlabs: [...ELEVENLABS_TTS_MODELS]
  },
  enabledModels: {
    fal: buildEnabledMap([...FAL_IMAGE_MODELS, ...FAL_IMAGE_EDIT_MODELS, ...FAL_VIDEO_MODELS]),
    wavespeed: buildEnabledMap([...WAVESPEED_IMAGE_MODELS, ...WAVESPEED_IMAGE_EDIT_MODELS]),
    openrouter: ensureAtLeastOneEnabled([...OPENROUTER_COPY_MODELS]),
    elevenlabs: buildEnabledMap([...ELEVENLABS_TTS_MODELS])
  },
  providerStatus: {
    fal: false,
    wavespeed: false,
    openrouter: false,
    elevenlabs: false
  },
  providerHealth: {
    fal: null,
    wavespeed: null,
    openrouter: null,
    elevenlabs: null
  },
  billingSettings: {
    image_cost: 1,
    copy_cost: 0.5,
    tts_cost: 1,
    tts_optimization_cost: 0.5,
    video_cost_per_second: 2,
    video_min_seconds: 5,
    video_max_seconds: 30
  },
  auroraPromptChainSettings: {
    enabled: false,
    vision_model: "google/gemini-2.0-flash-001",
    text_model: "openai/gpt-4.1-mini"
  }
};

const initialBillingOverview = {
  billing_mode: "credits",
  has_active_subscription: false,
  credits_balance: 0,
  monthly_credits: 0,
  renewal_date: null,
  credits_percentage: 0,
  plan: null
};

const initialAccount = {
  fullName: "",
  companyName: "",
  jobTitle: "",
  email: "",
  timezone: "Europe/Rome",
  locale: "en",
  accountRole: "client"
};

const initialAccountSubscription = {
  has_active_subscription: false,
  status: "",
  billing_mode: "credits",
  cancel_at_period_end: false,
  current_period_start: null,
  current_period_end: null,
  monthly_credits: 0,
  plan: null
};

const initialSocialFeatures = {
  meta_configured: false,
  tiktok_configured: false
};

const initialSocialConnections = [];

function buildAccountFromProps(auth, profile = {}) {
  return {
    fullName: profile?.full_name || auth?.name || "",
    companyName: profile?.company_name || "",
    jobTitle: profile?.job_title || "",
    email: profile?.email || auth?.email || "",
    timezone: profile?.timezone || auth?.timezone || initialAccount.timezone,
    locale: profile?.locale || auth?.locale || initialAccount.locale,
    accountRole: profile?.account_role || auth?.role || (auth?.superadmin ? "superadmin" : "client")
  };
}

function formatAccountDate(value) {
  if (!value) return "Not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not available";
  return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatPlanPrice(priceCents, currency = "EUR") {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: currency || "EUR",
    maximumFractionDigits: 0
  }).format((Number(priceCents) || 0) / 100);
}

function formatCreditDelta(value) {
  const numericValue = Number(value || 0);
  const prefix = numericValue > 0 ? "+" : "";
  return `${prefix}${numericValue.toLocaleString("en-US", {
    minimumFractionDigits: numericValue % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1
  })}`;
}

function getMetaConnection(connections = []) {
  return Array.isArray(connections) ? connections.find((connection) => connection.provider === "meta") || null : null;
}

function getTiktokConnection(connections = []) {
  return Array.isArray(connections) ? connections.find((connection) => connection.provider === "tiktok") || null : null;
}

function getDefaultInstagramAccount(connections = []) {
  const metaConnection = getMetaConnection(connections);
  if (!metaConnection) return null;

  const instagramAccounts = Array.isArray(metaConnection.accounts)
    ? metaConnection.accounts.filter((account) => account.platform === "instagram")
    : [];

  return instagramAccounts.find((account) => account.default_account) || instagramAccounts[0] || null;
}

function getDefaultTiktokAccount(connections = []) {
  const tiktokConnection = getTiktokConnection(connections);
  if (!tiktokConnection) return null;

  const tiktokAccounts = Array.isArray(tiktokConnection.accounts)
    ? tiktokConnection.accounts.filter((account) => account.platform === "tiktok")
    : [];

  return tiktokAccounts.find((account) => account.default_account) || tiktokAccounts[0] || null;
}

const initialForms = {
  imageProvider: "fal",
  imageModel: "fal-ai/nano-banana-pro",
  imageResolution: "2K",
  imagePrompt: "",
  imageAspectRatio: DEFAULT_ASPECT_RATIO,
  wavespeedOutputFormat: "png",
  wavespeedEnableSyncMode: "false",
  wavespeedEnableBase64Output: "false",
  wavespeedStrength: "0.8",
  wavespeedSeed: "",
  wavespeedReferenceImage: "",
  wavespeedSizeOverride: "",
  imageEditProvider: "fal",
  imageEditModel: DEFAULT_FAL_IMAGE_EDIT_MODEL,
  imageEditPrompt: "",
  imageEditAspectRatio: DEFAULT_ASPECT_RATIO,
  imageEditSizeOverride: "",
  imageEditOutputFormat: "png",
  imageEditResolution: "2K",
  imageEditEnableSyncMode: "false",
  imageEditEnableBase64Output: "false",
  copyModel: "",
  copyPlatform: "Instagram",
  copyContentType: "organic",
  copyLanguage: "en",
  copyLanguageManual: false,
  copyMaxSeconds: "15",
  sourceUrl: "",
  sourceText: "",
  generatedCopy: "",
  generatedPrimaryText: "",
  generatedHeadline: "",
  generatedDescription: "",
  generatedCaptionText: "",
  publishCaption: "",
  publishHashtags: "",
  publishTargetIds: ["instagram_reel"],
  voiceId: "",
  ttsModel: "",
  stability: "0.4",
  similarityBoost: "0.8",
  ttsText: "",
  videoModel: FAL_VIDEO_MODELS[0],
  videoAspectRatio: DEFAULT_ASPECT_RATIO,
  videoResolution: "720p",
  videoGuidanceScale: "1.0",
  videoPrompt: "",
  videoImageUrl: "",
  videoAudioUrl: "",
  socialPreviewEnabled: true,
  socialPreviewMode: "instagram_reels"
};

function buildSettingsFromAppConfig(appConfig = {}) {
  const providerHealth = appConfig.provider_health || initialSettings.providerHealth;
  return {
    ...initialSettings,
    providerModels: appConfig.provider_models || initialSettings.providerModels,
    enabledModels: appConfig.enabled_models || initialSettings.enabledModels,
    providerStatus: buildProviderAvailability(appConfig.provider_status || initialSettings.providerStatus, providerHealth),
    providerHealth,
    billingSettings: appConfig.billing_settings || initialSettings.billingSettings,
    auroraPromptChainSettings: appConfig.aurora_prompt_chain_settings || initialSettings.auroraPromptChainSettings
  };
}

const PUBLISH_TARGET_DEFINITIONS = [
  {
    id: "instagram_reel",
    label: "IG Reel",
    title: "Instagram Reel",
    network: "instagram",
    platform: "Meta",
    description: "Publish into a Reel.",
    availability: "available"
  },
  {
    id: "instagram_story",
    label: "IG Story",
    title: "Instagram Story",
    network: "instagram",
    platform: "Meta",
    description: "Story publishing flow for Meta.",
    availability: "coming_soon"
  },
  {
    id: "facebook_reel",
    label: "FB Reel",
    title: "Facebook Reel",
    network: "facebook",
    platform: "Meta",
    description: "Publish to Facebook Page Reel .",
    availability: "coming_soon"
  },
  {
    id: "facebook_story",
    label: "FB Story",
    title: "Facebook Story",
    network: "facebook",
    platform: "Meta",
    description: "Story publishing for Facebook.",
    availability: "coming_soon"
  },
  {
    id: "tiktok",
    label: "TikTok",
    title: "TikTok",
    network: "tiktok",
    platform: "TikTok",
    description: "Upload a draft to the connected TikTok account. The user finishes posting inside TikTok.",
    availability: "available"
  }
];

function normalizeHashtagString(value) {
  return String(value || "")
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => (item.startsWith("#") ? item : `#${item.replace(/^#+/, "")}`))
    .join(" ");
}

function buildProviderAvailability(configuredStatus = {}, providerHealth = {}) {
  return Object.keys(initialSettings.providerStatus).reduce((result, provider) => {
    const health = providerHealth?.[provider];
    result[provider] = typeof health?.valid === "boolean" ? Boolean(health.valid) : Boolean(configuredStatus?.[provider]);
    return result;
  }, {});
}

function buildElevenlabsSettingsFromAppConfig(appConfig = {}) {
  const raw = appConfig.elevenlabs_settings || {};
  const voiceScope = String(raw.voiceScope || raw.voice_scope || DEFAULT_ELEVENLABS_SETTINGS.voiceScope).trim();

  return {
    ...DEFAULT_ELEVENLABS_SETTINGS,
    ...raw,
    voiceScope: voiceScope || DEFAULT_ELEVENLABS_SETTINGS.voiceScope
  };
}

function managedFormDefaultsFromAppConfig(appConfig = {}) {
  const defaults = appConfig.workflow_defaults || {};

  return {
    imageProvider: defaults.image_provider || initialForms.imageProvider,
    imageModel: defaults.image_model || initialForms.imageModel,
    imageResolution: defaults.image_resolution || initialForms.imageResolution,
    imageEditProvider: defaults.image_edit_provider || initialForms.imageEditProvider,
    imageEditModel: defaults.image_edit_model || initialForms.imageEditModel,
    imageEditResolution: defaults.image_edit_resolution || initialForms.imageEditResolution,
    copyModel: defaults.copy_model || initialForms.copyModel,
    ttsModel: defaults.tts_model || initialForms.ttsModel,
    voiceId: defaults.voice_id || initialForms.voiceId,
    stability: defaults.stability || initialForms.stability,
    similarityBoost: defaults.similarity_boost || initialForms.similarityBoost,
    videoModel: defaults.video_model || initialForms.videoModel
  };
}

function lockedFormDefaultsFromAppConfig(appConfig = {}) {
  const defaults = managedFormDefaultsFromAppConfig(appConfig);

  return {
    imageProvider: defaults.imageProvider,
    imageModel: defaults.imageModel,
    imageResolution: defaults.imageResolution,
    imageEditProvider: defaults.imageEditProvider,
    imageEditModel: defaults.imageEditModel,
    imageEditResolution: defaults.imageEditResolution,
    copyModel: defaults.copyModel,
    ttsModel: defaults.ttsModel,
    stability: defaults.stability,
    similarityBoost: defaults.similarityBoost,
    videoModel: defaults.videoModel
  };
}

function buildFormsFromAppConfig(appConfig = {}) {
  return {
    ...initialForms,
    ...managedFormDefaultsFromAppConfig(appConfig)
  };
}

function buildPersistableForms(forms = {}) {
  const nextForms = { ...(forms || {}) };
  [
    "imageProvider",
    "imageModel",
    "imageResolution",
    "imageEditProvider",
    "imageEditModel",
    "imageEditResolution",
    "copyModel",
    "ttsModel",
    "stability",
    "similarityBoost",
    "videoModel",
    "wavespeedResolution"
  ].forEach((key) => {
    delete nextForms[key];
  });

  return nextForms;
}

const IMAGE_PROMPT_PRESETS = [
  {
    id: "preset-office-01",
    caption: "Office Dress in Corporate Open Space",
    imageUrl: "/media/influencer-grid/office-01.jpg",
    prompt:
      "Girl wearing a office dress, standing in a modern open-space office, bright and professional meeting room of a office, soft natural daylight coming from windows, clean and contemporary corporate setting, candid and soft expression."
  },
  {
    id: "preset-kitchen-01",
    caption: "Golden Hour Kitchen Selfie",
    imageUrl: "/media/influencer-grid/ugc_kitchen-01.png",
    prompt:
      "Realistic photo of a young woman taking a selfie indoors during golden hour. She has fair skin, light blonde hair tied back casually with a few loose strands, and clear blue eyes. She is smiling softly with a natural, relaxed expression. She wears a minimal cream/beige sweatshirt with a simple round neckline. Warm sunlight streams through a window, casting strong golden highlights on her face and creating long, soft-edged shadows across her forehead and clothing. The setting is a modern kitchen with clean white cabinets, a wooden backsplash, and a countertop with a metal kettle. The mood is warm, cozy, and natural. Soft depth of field, photographic realism, high detail, warm tones, natural lighting, lifestyle aesthetic."
  }
];

const DEFAULT_IMAGE_PROMPT = IMAGE_PROMPT_PRESETS[0]?.prompt || "";
const INSPIRATION_FALLBACK_IMAGES = ["/media/influencer-grid/office-01.jpg", "/media/influencer-grid/ugc_kitchen-01.png"];
const INSPIRATION_PROMPT_TEXTS = [
  "Realistic UGC-style portrait of a young woman speaking directly to a front-facing camera positioned at eye level. She has fair skin, light ash-blonde hair in a loose low bun, soft blue eyes, and minimal natural makeup. She wears an olive-green button-up shirt with a small black clip-on microphone attached to the collar. Background: neutral beige wall with soft daylight entering from a window on the left side, creating gentle shadows across her face. Modern apartment interior. Shallow depth of field, natural skin texture, authentic lifestyle realism.",
  "Realistic UGC-style portrait of a young woman speaking directly to a front-facing smartphone camera placed at eye level. She has light-medium skin, long straight dark brown hair parted in the center, natural makeup with soft pink lips, and warm brown eyes. She wears a light gray ribbed knit sweater. Background: cozy living room with a beige sofa and decorative patterned cushions, soft warm lamp light glowing from the left side. Neutral cream wall behind her. Soft indoor lighting, natural skin texture, subtle depth of field, authentic social media realism, iPhone front camera look, lifestyle aesthetic.",
  "Realistic photo of a young brunette woman filming herself in a bedroom. She has light skin, long straight brown hair, and wears a soft gray athletic tank top with a fabric headband. She is mid-sentence, expressive facial expression. Background: bed with white duvet, small nightstand with skincare products, pastel lavender wall. Balanced daylight from window in front of her. Clean influencer aesthetic, high realism, subtle depth blur.",
  "Realistic outdoor selfie of a woman speaking to her phone in a tropical garden. She has warm tan skin, long dark brown hair, and wears a navy spaghetti-strap top. Sunlight filters through palm leaves creating dappled light on her shoulders. Background: blurred green foliage and terracotta roof. Natural sunlight, vibrant colors, high detail, lifestyle influencer mood.",
  "Realistic selfie of a young Latina woman in medical scrubs speaking to camera. She has caramel skin tone, soft brown eyes, long straight dark hair. She wears navy scrubs with a visible hospital lanyard badge. Indoor clinic setting with white corridor and medical signage blurred behind her. Cool overhead fluorescent lighting. Authentic healthcare influencer aesthetic, sharp focus, real skin texture.",
  "Realistic UGC selfie photo of a young woman speaking to her smartphone camera indoors. She has fair skin, light ash-blonde hair tied in a low messy bun, light blue eyes, and minimal natural makeup. She wears a muted olive-green button-up shirt. She is holding a small black clip-on microphone near her collar. Background: neutral beige wall with soft indirect daylight coming from the left side through a window. Soft shadows on one side of her face. Modern apartment interior. Eye-level framing, shallow depth of field, natural skin texture, lifestyle realism.",
  "Realistic UGC portrait of a man speaking directly to a front-facing camera indoors. Medium skin tone, short dark hair, trimmed beard, neutral confident expression. Wearing a dark patterned button-up shirt slightly open at the collar. Background: minimalist modern interior with soft gray wall and blurred window light coming from the side. Balanced natural lighting on face, high facial detail, authentic conversational tone, realistic smartphone camera perspective.",
  "Realistic selfie of a young woman with short pastel pink hair speaking outdoors. She has fair skin and wears a simple white oversized t-shirt. Background: lush green park with trees and sunlight creating soft bokeh. Natural daylight, handheld perspective, casual social media aesthetic.",
  "Realistic UGC video frame of a confident brunette woman speaking enthusiastically to a stationary smartphone camera. Light olive skin tone, long straight light brown hair, gold hoop earrings, expressive eyes. She wears a bright pink long-sleeve top and holds a small black clip-on microphone near her chest. Background: modern apartment interior with a large black flat-screen TV mounted on a white wall. Bright diffused daylight illuminating her face evenly. Sharp facial detail, conversational expression mid-speech, authentic influencer ad-style realism.",
  "Realistic selfie of a young woman inside a parked car. She has warm beige skin tone, long dark hair, minimal makeup. Wearing a light blue fitted t-shirt. Background: visible car headrest and side window with daylight outside. Soft natural lighting entering through windshield. Authentic in-car influencer aesthetic.",
  "Realistic selfie of an East Asian woman speaking to camera indoors. She has long straight black hair, porcelain skin, and natural soft makeup. Wearing a light beige blouse. Background: modern kitchen with warm wooden cabinets and soft under-cabinet lighting. Cozy domestic atmosphere, shallow depth of field.",
  "Realistic selfie of a Black woman with sleek shoulder-length straight hair and deep brown skin tone. Wearing a burnt orange fitted top. She smiles while talking. Background: white hallway with minimal decor. Soft indoor lighting, clean composition, social media realism.",
];
const INSPIRATION_PROMPTS = INSPIRATION_PROMPT_TEXTS.map((prompt, index) => {
  const number = String(index + 1).padStart(2, "0");
  return {
    id: `inspiration-${number}`,
    title: `Prompt ${number}`,
    imageUrl: `/media/inspiration/prompt-${number}.png`,
    fileName: `prompt-${number}.png`,
    prompt
  };
});

function normalizeExternalImageUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    if (parsed.protocol === "http:") {
      parsed.protocol = "https:";
    }
    return parsed.toString();
  } catch {
    return raw;
  }
}

function normalizeInternalAssetUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw);
    const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    if (path.startsWith("/rails/active_storage/") || path.startsWith("/generated/")) {
      return path;
    }
    return parsed.toString();
  } catch {
    return raw;
  }
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function normalizeRoute(pathname) {
  return VALID_ROUTES.includes(pathname) ? pathname : "/generation";
}

function appPathFor(route) {
  const normalized = normalizeRoute(route);
  if (normalized === "/generation") return "/app";
  return `/app${normalized}`;
}

function openInspirationWindow() {
  if (typeof window === "undefined") return;
  window.open("/app/inspiration", "_blank", "noopener");
}

function uniq(values = []) {
  return [...new Set(values.filter((value) => typeof value === "string" && value.trim()).map((value) => value.trim()))];
}

function uniqVoiceEntries(voices = []) {
  const seen = new Set();

  return (voices || []).filter((voice) => {
    const voiceId = String(voice?.voice_id || "").trim();
    if (!voiceId || seen.has(voiceId)) return false;
    seen.add(voiceId);
    return true;
  });
}

function normalizeVoiceCategoryKey(category) {
  const normalized = String(category || "").trim().toLowerCase();
  return normalized || "uncategorized";
}

function formatVoiceCategoryLabel(category) {
  const normalized = normalizeVoiceCategoryKey(category);
  if (VOICE_CATEGORY_LABELS[normalized]) return VOICE_CATEGORY_LABELS[normalized];
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function compareVoiceCategoryKeys(left, right) {
  const leftIndex = VOICE_CATEGORY_ORDER.indexOf(left);
  const rightIndex = VOICE_CATEGORY_ORDER.indexOf(right);

  if (leftIndex !== -1 || rightIndex !== -1) {
    if (leftIndex === -1) return 1;
    if (rightIndex === -1) return -1;
    return leftIndex - rightIndex;
  }

  return left.localeCompare(right);
}

function buildVoiceGroups(voices = []) {
  const grouped = uniqVoiceEntries(voices).reduce((result, voice) => {
    const category = normalizeVoiceCategoryKey(voice?.category);
    if (!result[category]) result[category] = [];
    result[category].push({
      ...voice,
      category
    });
    return result;
  }, {});

  return Object.keys(grouped)
    .sort(compareVoiceCategoryKeys)
    .map((category) => ({
      key: category,
      label: formatVoiceCategoryLabel(category),
      voices: grouped[category]
    }));
}

function buildEnabledMap(models, currentMap = {}) {
  const next = {};
  models.forEach((model) => {
    next[model] = typeof currentMap[model] === "boolean" ? currentMap[model] : true;
  });
  return next;
}

function ensureAtLeastOneEnabled(models = [], enabledMap = {}) {
  const next = buildEnabledMap(models, enabledMap);
  if (models.length && !models.some((model) => next[model] !== false)) {
    next[models[0]] = true;
  }
  return next;
}

function getActiveModels(provider, settings) {
  const models = settings.providerModels[provider] || [];
  const enabledMap = settings.enabledModels[provider] || {};
  return models.filter((model) => enabledMap[model] !== false);
}

function getImageModelsByProvider(provider, activeModels) {
  if (provider === "fal") return activeModels.falImage || [];
  if (provider === "wavespeed") return activeModels.wavespeedImage || [];
  return [];
}

function getImageCatalogByProvider(provider, providerModels) {
  if (provider === "fal") {
    const configured = (providerModels?.fal || []).filter((model) => FAL_IMAGE_MODELS.includes(model));
    return configured.length ? configured : [...FAL_IMAGE_MODELS];
  }
  if (provider === "wavespeed") {
    const configured = (providerModels?.wavespeed || []).filter((model) => WAVESPEED_IMAGE_MODELS.includes(model));
    return configured.length ? configured : [...WAVESPEED_IMAGE_MODELS];
  }
  return [];
}

function getImageEditModelsByProvider(provider, activeModels) {
  if (provider === "fal") return activeModels.falEdit || [];
  if (provider === "wavespeed") return activeModels.wavespeedEdit || [];
  return [];
}

function getImageEditCatalogByProvider(provider, providerModels) {
  if (provider === "fal") {
    const configured = (providerModels?.fal || []).filter((model) => FAL_IMAGE_EDIT_MODELS.includes(model));
    return configured.length ? configured : [...FAL_IMAGE_EDIT_MODELS];
  }
  if (provider === "wavespeed") {
    const configured = (providerModels?.wavespeed || []).filter((model) => WAVESPEED_IMAGE_EDIT_MODELS.includes(model));
    return configured.length ? configured : [...WAVESPEED_IMAGE_EDIT_MODELS];
  }
  return [];
}

function getImageAspectRatioOptions(provider, model) {
  if (model && IMAGE_ASPECT_RATIOS_BY_MODEL[model]) {
    return IMAGE_ASPECT_RATIOS_BY_MODEL[model];
  }
  if (provider === "wavespeed") {
    return DEFAULT_IMAGE_ASPECT_RATIOS;
  }
  return DEFAULT_IMAGE_ASPECT_RATIOS;
}

function formatAspectRatioOptionLabel(ratio) {
  const normalized = String(ratio || "").trim();
  if (normalized === "9:16") return "9:16 - perfect for Reels and Stories";
  return normalized;
}

function getReadableModelName(modelId) {
  const parts = String(modelId || "")
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return "N/A";
  let index = parts.length - 1;
  const trailingKinds = new Set(["text-to-image", "image-to-image", "edit", "preview"]);
  if (trailingKinds.has(parts[index]) && index > 0) index -= 1;
  return parts[index]
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatUsdAmount(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "";
  if (value === 0) return "$0.00";
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function formatBillingAmount(amount, currency = "USD") {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "";
  const normalizedCurrency = String(currency || "USD").toUpperCase();
  if (normalizedCurrency === "USD") return formatUsdAmount(value);
  if (normalizedCurrency === "CREDITS") return `${Math.round(value)} credits`;
  if (normalizedCurrency === "CHARS") return `${Math.round(value)} chars`;
  return `${value}`;
}

function formatCreditsAmount(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "";
  if (Number.isInteger(value)) return `${value} credit${value === 1 ? "" : "s"}`;
  return `${value.toFixed(1)} credits`;
}

function sumCreditsUsed(...billings) {
  return billings.reduce((sum, billing) => {
    const value = Number(billing?.creditsUsed);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);
}

function combineBillings(...billings) {
  const valid = billings.filter((billing) => Number.isFinite(Number(billing?.amount)));
  if (!valid.length) return null;
  if (valid.length === 1) return valid[0];

  const firstCurrency = String(valid[0].currency || "USD").toUpperCase();
  const sameCurrency = valid.every((billing) => String(billing.currency || "USD").toUpperCase() === firstCurrency);
  if (!sameCurrency) {
    return {
      provider: "mixed",
      amount: Number.NaN,
      currency: "MIXED",
      parts: valid
    };
  }
  return {
    provider: valid[0].provider || "mixed",
    operation: "combined",
    amount: valid.reduce((sum, billing) => sum + Number(billing.amount), 0),
    currency: firstCurrency,
    source: "combined"
  };
}

function formatBillingSuffix(billing) {
  const amountText = formatBillingAmount(billing?.amount, billing?.currency || "USD");
  if (!amountText) return "";
  return ` API cost: ${amountText}.`;
}

function formatCreditsSuffix(creditsUsed) {
  const amountText = formatCreditsAmount(creditsUsed);
  if (!amountText) return "";
  return ` Credits spent: ${amountText}.`;
}

function formatBillingDisplayText(billing) {
  if (billing?.currency === "MIXED" && Array.isArray(billing.parts) && billing.parts.length) {
    return billing.parts
      .map((item) => formatBillingAmount(item?.amount, item?.currency || "USD"))
      .filter(Boolean)
      .join(" + ");
  }
  return formatBillingAmount(billing?.amount, billing?.currency || "USD") || "-";
}

function StepCostAlert({ billing, showCreditsSpent = false }) {
  if (showCreditsSpent && Number.isFinite(Number(billing?.creditsUsed))) {
    return (
      <div className="flex rounded-xl border border-emerald-500/70 bg-emerald-500/15 px-3 py-2 text-sm text-emerald-100">
        <span className="font-semibold">Credits spent:</span>
        <span className="ml-1">{formatCreditsAmount(billing.creditsUsed)}</span>
      </div>
    );
  }

  if (billing?.currency === "MIXED" && Array.isArray(billing.parts) && billing.parts.length) {
    const mixedText = billing.parts
      .map((item) => formatBillingAmount(item?.amount, item?.currency || "USD"))
      .filter(Boolean)
      .join(" + ");
    return (
      <div className="flex rounded-xl border border-emerald-500/70 bg-emerald-500/15 px-3 py-2 text-sm text-emerald-100">
        <span className="font-semibold">Cost:</span>
        <span className="ml-1">{mixedText || "....."}</span>
      </div>
    );
  }
  const amountText = formatBillingAmount(billing?.amount, billing?.currency || "USD") || ".....";
  return (
    <div className="flex rounded-xl border border-emerald-500/70 bg-emerald-500/15 px-3 py-2 text-sm text-emerald-100">
      <span className="font-semibold">Cost:</span>
      <span className="ml-1">{amountText}</span>
    </div>
  );
}

function getVideoAspectRatioOptions(model) {
  return VIDEO_ASPECT_RATIOS_BY_MODEL[model] || ["9:16", "16:9"];
}

function isAspectRatioValid(width, height, expectedRatio = "9:16", tolerance = 0.01) {
  const [ratioWidthRaw, ratioHeightRaw] = String(expectedRatio || "").split(":");
  const ratioWidth = Number(ratioWidthRaw);
  const ratioHeight = Number(ratioHeightRaw);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return false;
  if (!Number.isFinite(ratioWidth) || !Number.isFinite(ratioHeight) || ratioWidth <= 0 || ratioHeight <= 0) return false;

  const actual = width / height;
  const expected = ratioWidth / ratioHeight;
  return Math.abs(actual - expected) <= tolerance;
}

function loadImageDimensions(imageUrl) {
  return new Promise((resolve, reject) => {
    if (!imageUrl) {
      reject(new Error("Unable to read image dimensions."));
      return;
    }
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height
      });
    };
    image.onerror = () => {
      reject(new Error("Unable to read uploaded image dimensions."));
    };
    image.src = imageUrl;
  });
}

function getAspectRatioPreviewData(aspectRatio) {
  const raw = String(aspectRatio || "").trim();
  if (!raw || raw === "auto" || !raw.includes(":")) {
    return { label: "auto (9:16)", widthPct: 56.25, heightPct: 100 };
  }

  const [wRaw, hRaw] = raw.split(":");
  const width = Number(wRaw);
  const height = Number(hRaw);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return { label: raw, widthPct: 56.25, heightPct: 100 };
  }

  if (width >= height) {
    return {
      label: raw,
      widthPct: 100,
      heightPct: (height / width) * 100
    };
  }

  return {
    label: raw,
    widthPct: (width / height) * 100,
    heightPct: 100
  };
}

function normalizeCopyLanguageCode(code) {
  const normalized = String(code || "").trim().toLowerCase();
  return COPY_LANGUAGE_CODES.has(normalized) ? normalized : "en";
}

function detectLikelyLanguageClient(text) {
  const sample = String(text || "").trim();
  if (!sample) return { code: "en", name: "English" };

  if (/[\u4e00-\u9fff]/.test(sample)) return { code: "zh", name: "Chinese (Mandarin)" };
  if (/[\u0600-\u06FF]/.test(sample)) return { code: "ar", name: "Arabic" };
  if (/[\u0900-\u097F]/.test(sample)) return { code: "hi", name: "Hindi" };
  if (/[\u0400-\u04FF]/.test(sample)) return { code: "ru", name: "Russian" };

  const lowered = sample.toLowerCase();
  const profiles = [
    { code: "it", words: ["questo", "questa", "prodotto", "servizio", "oggi", "della", "delle"] },
    { code: "es", words: ["este", "esta", "producto", "servicio", "hoy", "marca"] },
    { code: "fr", words: ["avec", "pour", "votre", "produit", "service", "bonjour"] },
    { code: "de", words: ["dies", "diese", "produkt", "service", "heute", "mit"] },
    { code: "pt", words: ["este", "esta", "produto", "servico", "hoje", "voce"] },
    { code: "en", words: ["this", "that", "your", "product", "service", "today"] }
  ];

  let winner = "en";
  let winnerScore = -1;
  for (const profile of profiles) {
    const score = profile.words.reduce((sum, word) => {
      const regex = new RegExp(`\\b${word}\\b`, "gi");
      const matches = lowered.match(regex);
      return sum + (matches?.length || 0);
    }, 0);
    if (score > winnerScore) {
      winner = profile.code;
      winnerScore = score;
    }
  }

  const selected = COPY_LANGUAGE_OPTIONS.find((item) => item.code === winner) || COPY_LANGUAGE_OPTIONS[0];
  return { code: selected.code, name: selected.name };
}

function normalizeScriptForTiming(text) {
  return String(text || "")
    .replace(/\[[^\]]+\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function analyzeScriptTiming(text, maxSeconds) {
  const targetSeconds = Math.min(30, Math.max(6, Number(maxSeconds) || 15));
  const normalizedText = normalizeScriptForTiming(text);
  const characterCount = normalizedText.length;
  const wordCount = normalizedText ? normalizedText.split(/\s+/).length : 0;
  const estimatedByWords = wordCount / TTS_ESTIMATED_WORDS_PER_SECOND;
  const estimatedByChars = characterCount / TTS_ESTIMATED_CHARS_PER_SECOND;
  const estimatedSeconds = Math.max(estimatedByWords, estimatedByChars, 0);
  const minRecommendedSeconds = Number((targetSeconds * TTS_MIN_DURATION_RATIO).toFixed(1));
  const maxRecommendedSeconds = Number((targetSeconds * TTS_MAX_DURATION_RATIO).toFixed(1));

  let status = "empty";
  if (normalizedText) {
    if (estimatedSeconds < minRecommendedSeconds) {
      status = "too_short";
    } else if (estimatedSeconds > maxRecommendedSeconds) {
      status = "too_long";
    } else {
      status = "ok";
    }
  }

  return {
    normalizedText,
    characterCount,
    wordCount,
    estimatedSeconds,
    targetSeconds,
    minRecommendedSeconds,
    maxRecommendedSeconds,
    status,
    withinRange: status === "ok"
  };
}

function formatEstimatedSeconds(seconds) {
  return `${Number(seconds || 0).toFixed(1)}s`;
}

function buildScriptTimingValidationMessage(analysis, label = "Video script") {
  const targetRange = `${formatEstimatedSeconds(analysis.minRecommendedSeconds)} to ${formatEstimatedSeconds(analysis.maxRecommendedSeconds)}`;
  const estimated = formatEstimatedSeconds(analysis.estimatedSeconds);

  if (analysis.status === "too_short") {
    return `${label} is too short for ${analysis.targetSeconds}s. Estimated duration: ${estimated}. Target range: ${targetRange}. Add more spoken content or reduce Max script seconds.`;
  }

  if (analysis.status === "too_long") {
    return `${label} is too long for ${analysis.targetSeconds}s. Estimated duration: ${estimated}. Target range: ${targetRange}. Shorten the script or increase Max script seconds.`;
  }

  return "";
}

function normalizeFalGuidanceScaleClient(value) {
  const numeric = Number.parseFloat(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(numeric)) return "1.0";

  const clamped = Math.min(FAL_GUIDANCE_SCALE_MAX, Math.max(FAL_GUIDANCE_SCALE_MIN, numeric));
  return clamped.toFixed(1);
}

function createEmptyReferenceImageSlot() {
  return { file: null, name: "", dataUrl: "" };
}

function StepResultLoader({ label }) {
  return (
    <div className="relative min-h-72 overflow-hidden rounded-xl border border-slate-700 bg-slate-950/60">
      <FlickeringGrid className="absolute inset-0 opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/10 via-slate-950/25 to-slate-950/45" />
      <div className="relative z-10 flex min-h-72 items-end p-4">
        <p className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100">{label}</p>
      </div>
    </div>
  );
}

function StepResultPlaceholder({ text }) {
  return (
    <div className="flex min-h-72 items-center justify-center rounded-xl border border-slate-700 bg-slate-950/45 p-4 text-center text-sm text-slate-400">
      {text}
    </div>
  );
}

function formatCompactAudioTime(value) {
  const totalSeconds = Math.max(0, Math.floor(Number(value) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function Step5ImageUrlPreview({ src }) {
  const normalizedSrc = String(src || "").trim();
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [normalizedSrc]);

  const hasPreview = normalizedSrc && !imageError;

  return (
    <div className="step5-url-preview-card" aria-label={hasPreview ? "Image preview" : "Image preview unavailable"}>
      {hasPreview ? (
        <img
          src={normalizedSrc}
          alt="Selected image preview"
          className="h-full w-full rounded-[0.95rem] object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="step5-url-preview-fallback">
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[1.7]" aria-hidden>
            <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11z" />
            <path d="M8 14l2.4-2.8a1 1 0 0 1 1.5 0l2.4 2.8 1.3-1.4a1 1 0 0 1 1.5 0L20 16" />
            <circle cx="9" cy="9" r="1.25" />
          </svg>
          <span>Preview</span>
        </div>
      )}
    </div>
  );
}

function Step5AudioUrlPreview({ src }) {
  const normalizedSrc = String(src || "").trim();
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioError, setAudioError] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setAudioError(false);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [normalizedSrc]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleLoadedMetadata = () => {
      const nextDuration = Number.isFinite(audio.duration) ? audio.duration : 0;
      setDuration(nextDuration);
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime || 0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(audio.duration || 0);
    };
    const handleError = () => {
      setAudioError(true);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [normalizedSrc]);

  const canPreview = Boolean(normalizedSrc) && !audioError;
  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const handleTogglePlayback = async () => {
    if (!canPreview || !audioRef.current) return;

    if (audioRef.current.paused) {
      try {
        await audioRef.current.play();
      } catch {
        setIsPlaying(false);
      }
      return;
    }

    audioRef.current.pause();
  };

  return (
    <div className="step5-audio-preview-card" aria-label={canPreview ? "Audio preview" : "Audio preview unavailable"}>
      <audio ref={audioRef} src={normalizedSrc} preload="metadata" />
      <button
        type="button"
        className="step5-audio-preview-button"
        onClick={handleTogglePlayback}
        disabled={!canPreview}
        aria-label={isPlaying ? "Pause audio preview" : "Play audio preview"}
        title={isPlaying ? "Pause audio preview" : "Play audio preview"}
      >
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
          {isPlaying ? (
            <>
              <path d="M8.5 6v12" />
              <path d="M15.5 6v12" />
            </>
          ) : (
            <path d="M8 6.5v11l8-5.5-8-5.5z" fill="currentColor" stroke="none" />
          )}
        </svg>
      </button>
      <div className="min-w-0 flex-1">
        <div className="step5-audio-preview-meta">
          <span>{canPreview ? (isPlaying ? "Playing" : "Ready") : "Preview"}</span>
          <span>{formatCompactAudioTime(duration || currentTime)}</span>
        </div>
        <div className="step5-audio-preview-track">
          <span className="step5-audio-preview-progress" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
}

function OverlayIcon({ className, children }) {
  return (
    <span className={cn("inline-flex h-6 w-6 items-center justify-center text-white", className)} aria-hidden>
      {children}
    </span>
  );
}

function IconHeart() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M12 21s-7.2-4.6-9.3-8.7C.8 8.7 3 4.8 7 4.8c2 0 3.4 1 5 2.9 1.6-1.9 3-2.9 5-2.9 4 0 6.2 3.9 4.3 7.5C19.2 16.4 12 21 12 21z" />
      </svg>
    </OverlayIcon>
  );
}

function IconComment() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M20 14.5c0 3.6-3.6 6.5-8 6.5-1.2 0-2.4-.2-3.4-.6L4 21.5l1.2-3C3.8 17.4 3 16 3 14.5 3 10.9 6.6 8 11 8s9 2.9 9 6.5z" />
      </svg>
    </OverlayIcon>
  );
}

function IconSend() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M21 3L10 14" />
        <path d="M21 3l-7 18-4-7-7-4 18-7z" />
      </svg>
    </OverlayIcon>
  );
}

function IconCamera() {
  return (
    <OverlayIcon className="h-7 w-7">
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-none stroke-current stroke-[1.8]">
        <path d="M4 8.5C4 7.1 5.1 6 6.5 6h2.4l1.3-1.5h3.6L15.1 6h2.4C18.9 6 20 7.1 20 8.5v8C20 17.9 18.9 19 17.5 19h-11C5.1 19 4 17.9 4 16.5v-8z" />
        <circle cx="12" cy="12.5" r="3.1" />
      </svg>
    </OverlayIcon>
  );
}

function IconHome() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M3 11.5L12 4l9 7.5" />
        <path d="M6 10.8V20h12v-9.2" />
      </svg>
    </OverlayIcon>
  );
}

function IconSearch() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <circle cx="11" cy="11" r="6.5" />
        <path d="M16 16l5 5" />
      </svg>
    </OverlayIcon>
  );
}

function IconReel() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.8]">
        <rect x="3.5" y="4.5" width="17" height="15" rx="4" />
        <path d="M8 4.5L11 9M13 4.5l3 4.5M3.8 9h16.4" />
        <path d="M10 11.5v4l4-2-4-2z" />
      </svg>
    </OverlayIcon>
  );
}

function IconBag() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.8]">
        <rect x="5" y="7" width="14" height="13" rx="3" />
        <path d="M9 9V7a3 3 0 016 0v2" />
      </svg>
    </OverlayIcon>
  );
}

function IconUser() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.8]">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c1.5-3 4-4.8 7-4.8s5.5 1.8 7 4.8" />
      </svg>
    </OverlayIcon>
  );
}

function IconClose() {
  return (
    <OverlayIcon className="h-7 w-7">
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-none stroke-current stroke-[1.9]">
        <path d="M5 5l14 14M19 5L5 19" />
      </svg>
    </OverlayIcon>
  );
}

function IconChevronUp() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current stroke-[1.9]">
        <path d="M6 15l6-6 6 6" />
      </svg>
    </OverlayIcon>
  );
}

function IconDots() {
  return (
    <OverlayIcon>
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
        <circle cx="6" cy="12" r="1.7" />
        <circle cx="12" cy="12" r="1.7" />
        <circle cx="18" cy="12" r="1.7" />
      </svg>
    </OverlayIcon>
  );
}

function ReelsPreviewOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 text-white">
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/25" />

      <div className="absolute inset-x-4 top-10 flex items-center justify-between">
        <span className="text-4xl font-semibold tracking-tight">Reels</span>
        <IconCamera />
      </div>

      <div className="absolute bottom-28 right-3 flex flex-col items-center gap-3 text-center">
        <div className="space-y-0.5">
          <IconHeart />
          <p className="text-xs font-medium">823k</p>
        </div>
        <div className="space-y-0.5">
          <IconComment />
          <p className="text-xs font-medium">51</p>
        </div>
        <IconSend />
      </div>

      <div className="absolute bottom-16 left-3 right-14 space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200/40 bg-emerald-500/25 text-[9px] font-semibold">
            LOGO
          </span>
          <span className="font-semibold">account_name</span>
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-black text-black">v</span>
        </div>
        <div className="flex items-center justify-between rounded-lg bg-white/30 px-3 py-2 text-base backdrop-blur-sm">
          <span>Learn more</span>
          <span className="text-lg">&gt;</span>
        </div>
        <p className="truncate text-base">Brighten up your home with a green space ...</p>
        <div className="flex items-center justify-between text-sm text-white/85">
          <span>Sponsored</span>
          <IconDots />
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-black/90 px-4 pb-2 pt-3">
        <div className="flex items-center justify-between">
          <IconHome />
          <IconSearch />
          <IconReel />
          <IconBag />
          <IconUser />
        </div>
      </div>
    </div>
  );
}

function StoriesPreviewOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 text-white">
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/25" />

      <div className="absolute inset-x-3 top-11 flex gap-1">
        <div className="h-1 rounded-full bg-white/95" style={{ width: "48%" }} />
        <div className="h-1 flex-1 rounded-full bg-white/45" />
      </div>

      <div className="absolute inset-x-3 top-14 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[9px] font-semibold text-slate-900">
            LOGO
          </span>
          <div className="leading-tight">
            <p className="text-[15px] font-semibold">account_name</p>
            <p className="text-[13px] text-white/85">Sponsored</p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-white/90">
          <IconDots />
          <IconClose />
        </div>
      </div>

      <div className="absolute left-1/2 top-[61%] -translate-x-1/2 rounded-xl border border-white/70 bg-white/90 px-5 py-2 text-[16px] font-semibold tracking-wide text-[#7d5547] shadow-lg">
        SHOP NOW
      </div>

      <div className="absolute inset-x-0 bottom-0">
        <div className="h-16 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-3 flex items-center justify-between px-10">
          <IconChevronUp />
          <IconSend />
        </div>
      </div>
    </div>
  );
}

function SocialPreviewOverlay({ mode }) {
  if (mode === "instagram_stories") return <StoriesPreviewOverlay />;
  return <ReelsPreviewOverlay />;
}

function getWavespeedImageModelParams(model) {
  return (
    WAVESPEED_IMAGE_PARAMS_BY_MODEL[model] || {
      outputFormats: [],
      resolutions: [],
      supportsImage: false,
      supportsStrength: false,
      supportsSeed: false,
      supportsEnableSyncMode: false,
      supportsEnableBase64Output: false,
      supportsSizeOverride: true
    }
  );
}

const resolveImageResolutionOptions = (provider, model) => {
  if (provider === "wavespeed") {
    return getWavespeedImageModelParams(model).resolutions || [];
  }
  return FAL_IMAGE_RESOLUTION_OPTIONS_BY_MODEL[model] || [];
};

function resolveManagedResolution(currentValue, options, fallbackValue = "2K") {
  const availableOptions = Array(options || [])
    .map((option) => String(option || "").trim())
    .filter(Boolean);
  if (availableOptions.length === 0) return "";

  const current = String(currentValue || "").trim();
  const fallback = String(fallbackValue || "").trim();
  const matchedCurrent = availableOptions.find((option) => option.toLowerCase() === current.toLowerCase());
  if (matchedCurrent) return matchedCurrent;

  const matchedFallback = availableOptions.find((option) => option.toLowerCase() === fallback.toLowerCase());
  if (matchedFallback) return matchedFallback;

  return availableOptions.find((option) => option.toLowerCase() === "2k") || availableOptions[0];
}

function mapAspectRatioToWavespeedSize(aspectRatio) {
  const normalized = String(aspectRatio || DEFAULT_ASPECT_RATIO).trim().replace("/", ":");
  const fixedMap = {
    "1:1": "1024*1024",
    "3:4": "1024*1365",
    "4:3": "1365*1024",
    "9:16": "1024*1792",
    "16:9": "1792*1024"
  };
  if (fixedMap[normalized]) return fixedMap[normalized];

  const [widthRaw, heightRaw] = normalized.split(":");
  const width = Number(widthRaw);
  const height = Number(heightRaw);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return "1024*1792";

  const longSide = 1792;
  const minSide = 256;
  const roundTo64 = (value) => {
    const rounded = Math.round(value / 64) * 64;
    return Math.min(longSide, Math.max(minSide, rounded));
  };

  if (width >= height) {
    const computedHeight = roundTo64((longSide * height) / width);
    return `${longSide}*${computedHeight}`;
  }

  const computedWidth = roundTo64((longSide * width) / height);
  return `${computedWidth}*${longSide}`;
}

function StatusPills({ statusList, activeStep, onStepChange }) {
  const lastIndex = statusList.length - 1;
  const connectorSourceByIndex = statusList.map((status, index) => {
    if (!status.disabled) return status;
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      if (!statusList[cursor]?.disabled) {
        return statusList[cursor];
      }
    }
    return status;
  });

  return (
    <nav aria-label="Progress" className="rounded-2xl border border-slate-700 bg-slate-900/55 p-2.5">
      <ol role="list" className="flex overflow-x-auto pb-1 md:hidden">
        {statusList.map((status, index) => {
          const isActive = activeStep === status.step;
          const isDone = status.ok;
          const isDisabled = Boolean(status.disabled);
          const isPending = !isDone && !isDisabled;
          const isLast = index === lastIndex;
          const connectorSource = connectorSourceByIndex[index];
          const isBridgedDisabled = isDisabled && Boolean(connectorSource) && !connectorSource?.disabled;
          const isBridgeComplete = isBridgedDisabled && Boolean(connectorSource?.ok);
          const isBridgeActive = isBridgedDisabled && activeStep === connectorSource?.step && !connectorSource?.ok;
          const connectorTone = connectorSource?.ok
            ? "bg-emerald-400/55"
            : activeStep === connectorSource?.step
              ? "bg-cyan-300/55"
              : "bg-slate-700";

          return (
            <li key={status.label} className={cn("relative w-40 shrink-0 snap-start pr-3 md:w-auto md:shrink md:pr-0", !isLast && "md:pb-2.5")}>
              {!isLast && (
                <div
                  aria-hidden="true"
                  className={cn("absolute left-8 right-0 top-4 h-0.5", connectorTone, "md:left-4 md:right-auto md:top-4 md:mt-0.5 md:h-full md:w-0.5")}
                />
              )}

              <button
                type="button"
                onClick={() => onStepChange(status.step)}
                disabled={isDisabled}
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "group relative flex h-full w-full flex-col items-start rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70 md:flex-row",
                  isDisabled && "cursor-not-allowed opacity-80"
                )}
              >
                <span aria-hidden="true" className="flex h-8 w-full items-center md:h-9 md:w-auto">
                  <span
                    className={cn(
                      "relative z-10 flex size-8 items-center justify-center rounded-full border-2 transition-colors",
                      isDisabled && !isBridgedDisabled && "border-slate-700 bg-slate-950 text-slate-600",
                      isBridgeComplete && "border-emerald-300 bg-transparent text-emerald-300",
                      isBridgeActive && "border-cyan-300 bg-transparent text-cyan-100",
                      isDone &&
                        !isActive &&
                        "border-emerald-200 bg-gradient-to-br from-emerald-300 via-teal-300 to-cyan-300 text-slate-950 shadow-[0_0_18px_rgba(52,211,153,.28)]",
                      isActive && "border-cyan-300 bg-slate-950 text-cyan-100",
                      isPending && "border-slate-600 bg-slate-950 text-slate-500"
                    )}
                  >
                    {isDone ? (
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="size-5">
                        <path d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" fillRule="evenodd" />
                      </svg>
                    ) : isBridgeComplete ? (
                      <span className="size-2.5 rounded-full bg-emerald-300" />
                    ) : isBridgeActive ? (
                      <span className="size-2.5 rounded-full bg-cyan-300" />
                    ) : isActive ? (
                      <span className="size-2.5 rounded-full bg-cyan-300" />
                    ) : (
                      <span className="size-2.5 rounded-full bg-transparent group-hover:bg-slate-400" />
                    )}
                  </span>
                </span>

                <div
                  className={cn(
                    "mt-2 flex min-h-[68px] w-full min-w-0 flex-col rounded-xl border px-3 py-2.5 transition-colors md:mt-0 md:ml-3 md:min-h-[96px] md:flex-1 md:py-3",
                    isDisabled && "border-slate-800/90 bg-slate-950/85",
                    isActive &&
                      "border-cyan-200/90 bg-cyan-400/14 shadow-[0_0_0_1px_rgba(103,232,249,.28),0_0_28px_rgba(34,211,238,.14)] shine-border",
                    !isActive && isDone && "border-slate-700/90 bg-slate-900/65",
                    !isActive && isPending && "border-slate-700 bg-slate-900/80 hover:bg-slate-800/85"
                  )}
                >
                  <span
                    className={cn(
                      "text-[12px] font-semibold leading-tight",
                      isDisabled && "text-slate-500",
                      isActive ? "text-white" : isDone ? "text-slate-100" : "text-slate-200"
                    )}
                  >
                    {status.label}
                  </span>
                  <span
                    className={cn(
                      "mt-1 text-[10px] leading-snug",
                      isDisabled ? "text-slate-500" : isActive ? "text-cyan-100" : isDone ? "text-slate-300" : "text-slate-400"
                    )}
                  >
                    {status.text}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ol>

      <ol role="list" className="hidden overflow-hidden md:block">
        {statusList.map((status, index) => {
          const isActive = activeStep === status.step;
          const isDone = status.ok;
          const isDisabled = Boolean(status.disabled);
          const isPending = !isDone && !isDisabled;
          const isLast = index === lastIndex;
          const connectorSource = connectorSourceByIndex[index];
          const isBridgedDisabled = isDisabled && Boolean(connectorSource) && !connectorSource?.disabled;
          const isBridgeComplete = isBridgedDisabled && Boolean(connectorSource?.ok);
          const isBridgeActive = isBridgedDisabled && activeStep === connectorSource?.step && !connectorSource?.ok;
          const connectorTone = connectorSource?.ok
            ? "bg-emerald-400/55"
            : activeStep === connectorSource?.step
              ? "bg-cyan-300/55"
              : "bg-slate-700";

          return (
            <li key={`${status.label}-desktop`} className={cn("relative", !isLast && "pb-2.5")}>
              {!isLast && (
                <div
                  aria-hidden="true"
                  className={cn("absolute left-4 top-4 mt-0.5 h-full w-0.5", connectorTone)}
                />
              )}

              <button
                type="button"
                onClick={() => onStepChange(status.step)}
                disabled={isDisabled}
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "group relative flex w-full items-start rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70",
                  isDisabled && "cursor-not-allowed opacity-80"
                )}
              >
                <span aria-hidden="true" className="flex h-9 items-center">
                  <span
                    className={cn(
                      "relative z-10 flex size-8 items-center justify-center rounded-full border-2 transition-colors",
                      isDisabled && !isBridgedDisabled && "border-slate-700 bg-slate-950 text-slate-600",
                      isBridgeComplete && "border-emerald-300 bg-transparent text-emerald-300",
                      isBridgeActive && "border-cyan-300 bg-transparent text-cyan-100",
                      isDone &&
                        !isActive &&
                        "border-emerald-200 bg-gradient-to-br from-emerald-300 via-teal-300 to-cyan-300 text-slate-950 shadow-[0_0_18px_rgba(52,211,153,.28)]",
                      isActive && "border-cyan-300 bg-slate-950 text-cyan-100",
                      isPending && "border-slate-600 bg-slate-950 text-slate-500"
                    )}
                  >
                    {isDone ? (
                      <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="size-5">
                        <path d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" fillRule="evenodd" />
                      </svg>
                    ) : isBridgeComplete ? (
                      <span className="size-2.5 rounded-full bg-emerald-300" />
                    ) : isBridgeActive ? (
                      <span className="size-2.5 rounded-full bg-cyan-300" />
                    ) : isActive ? (
                      <span className="size-2.5 rounded-full bg-cyan-300" />
                    ) : (
                      <span className="size-2.5 rounded-full bg-transparent group-hover:bg-slate-400" />
                    )}
                  </span>
                </span>

                <div
                  className={cn(
                    "ml-3 flex min-w-0 flex-1 flex-col rounded-xl border px-3 py-3 transition-colors md:min-h-[96px]",
                    isDisabled && "border-slate-800/90 bg-slate-950/85",
                    isActive &&
                      "border-cyan-200/90 bg-cyan-400/14 shadow-[0_0_0_1px_rgba(103,232,249,.28),0_0_28px_rgba(34,211,238,.14)] shine-border",
                    !isActive && isDone && "border-slate-700/90 bg-slate-900/65",
                    !isActive && isPending && "border-slate-700 bg-slate-900/80 hover:bg-slate-800/85"
                  )}
                >
                  <span
                    className={cn(
                      "text-[12px] font-semibold leading-tight",
                      isDisabled && "text-slate-500",
                      isActive ? "text-white" : isDone ? "text-slate-100" : "text-slate-200"
                    )}
                  >
                    {status.label}
                  </span>
                  <span
                    className={cn(
                      "mt-1 text-[10px] leading-snug",
                      isDisabled ? "text-slate-500" : isActive ? "text-cyan-100" : isDone ? "text-slate-300" : "text-slate-400"
                    )}
                  >
                    {status.text}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function BrandLogo({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="viralforge-logo-gradient" x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#22d3ee" />
          <stop offset="0.5" stopColor="#38bdf8" />
          <stop offset="1" stopColor="#34d399" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#020617" stroke="url(#viralforge-logo-gradient)" strokeWidth="3" />
      <path
        d="M17 45L30.5 17.5a2 2 0 013 0L47 45a2 2 0 01-1.8 2.9H18.8A2 2 0 0117 45z"
        fill="none"
        stroke="url(#viralforge-logo-gradient)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="33" r="5.5" fill="url(#viralforge-logo-gradient)" />
    </svg>
  );
}

function CreditsInfoTooltip({ billingSettings }) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  function updatePosition() {
    if (!triggerRef.current || typeof window === "undefined") return;
    const rect = triggerRef.current.getBoundingClientRect();
    const maxWidth = Math.min(352, window.innerWidth - 24);
    const tooltipHeight = panelRef.current ? panelRef.current.getBoundingClientRect().height : 300;

    let left = rect.right - maxWidth;
    if (left < 12) left = 12;
    if (left + maxWidth > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - maxWidth - 12);
    }

    let top = rect.bottom + 10;
    if (top + tooltipHeight > window.innerHeight - 12) {
      top = Math.max(12, rect.top - tooltipHeight - 10);
    }

    setPosition({ left, top, width: maxWidth });
  }

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = window.requestAnimationFrame(() => updatePosition());
    const handleReposition = () => updatePosition();
    const handleOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <span className="relative z-[120] inline-flex shrink-0 align-middle">
      <button
        ref={triggerRef}
        type="button"
        aria-label="How credits work"
        aria-expanded={open}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-cyan-400/50 bg-cyan-500/10 text-[11px] font-bold text-cyan-200 transition hover:bg-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
        onClick={() => setOpen((prev) => !prev)}
      >
        i
      </button>
      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              className="pointer-events-auto fixed z-[9999] rounded-xl border border-slate-700 bg-slate-950/95 p-3 text-left text-xs leading-relaxed text-slate-200 shadow-[0_12px_30px_rgba(2,6,23,.55)] backdrop-blur whitespace-normal break-words"
              style={{
                left: `${position.left}px`,
                top: `${position.top}px`,
                width: `${position.width}px`,
                maxHeight: "70vh",
                overflowY: "auto"
              }}
            >
              <p className="font-semibold text-slate-100">How credits work</p>
              <p className="mt-1">Credits are used whenever you generate content inside the platform.</p>
              <p className="mt-2 font-semibold text-slate-100">Typical usage:</p>
              <p className="mt-1">Image generation -&gt; {billingSettings?.image_cost ?? 1} credit</p>
              <p>Script generation -&gt; {billingSettings?.copy_cost ?? 0.5} credits</p>
              <p>Voice generation -&gt; {billingSettings?.tts_cost ?? 1} credit</p>
              <p>Voice optimization -&gt; {billingSettings?.tts_optimization_cost ?? 0.5} credits</p>
              <p>Video generation -&gt; {billingSettings?.video_cost_per_second ?? 2} credits per billed second *</p>
              <p className="mt-2">
                *Video duration is measured based on the final generated output and rounded up to the next full second.
              </p>
              <p className="mt-2">
                A typical 15-second video usually consumes around 35-40 credits, depending on retries and the number of assets generated.
              </p>
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

function InlineInfoTooltip({ message, label = "More info" }) {
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  function updatePosition() {
    if (!triggerRef.current || typeof window === "undefined") return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = Math.min(280, window.innerWidth - 24);
    const tooltipHeight = panelRef.current ? panelRef.current.getBoundingClientRect().height : 80;

    let left = rect.left + rect.width / 2 - width / 2;
    if (left < 12) left = 12;
    if (left + width > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - width - 12);
    }

    let top = rect.bottom + 10;
    if (top + tooltipHeight > window.innerHeight - 12) {
      top = Math.max(12, rect.top - tooltipHeight - 10);
    }

    setPosition({ left, top, width });
  }

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = window.requestAnimationFrame(() => updatePosition());
    const handleReposition = () => updatePosition();
    const handleOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEsc);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  return (
    <span className="relative inline-flex shrink-0 align-middle">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-cyan-400/45 bg-cyan-500/10 text-[11px] font-bold text-cyan-200 transition hover:bg-cyan-500/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60"
        onClick={() => setOpen((prev) => !prev)}
      >
        i
      </button>
      {open && position
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed z-[9999] rounded-xl border border-slate-700 bg-slate-950/95 px-3 py-2 text-xs leading-relaxed text-slate-200 shadow-[0_12px_30px_rgba(2,6,23,.55)] backdrop-blur"
              style={{
                left: `${position.left}px`,
                top: `${position.top}px`,
                width: `${position.width}px`
              }}
            >
              {message}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

function HeaderNav({ route, navigate, account, settings, auth, billingOverview }) {
  const hasImageProviderKey = Boolean(settings?.providerStatus?.fal || settings?.providerStatus?.wavespeed);
  const hasOpenRouterKey = Boolean(settings?.providerStatus?.openrouter);
  const hasElevenLabsKey = Boolean(settings?.providerStatus?.elevenlabs);
  const hasRequiredApiSetup = hasImageProviderKey && hasOpenRouterKey && hasElevenLabsKey;
  const canManageSettings = Boolean(auth?.superadmin);
  const normalizedBillingOverview = billingOverview || initialBillingOverview;
  const maxCredits = Number(normalizedBillingOverview.monthly_credits || 0);
  const availableCredits = Number(normalizedBillingOverview.credits_balance || 0);
  const creditsPercentage = Number(normalizedBillingOverview.credits_percentage || 0);
  const isByoApi = normalizedBillingOverview.billing_mode === "byo_api";
  const planName = normalizedBillingOverview.plan?.name || "No active plan";
  const renewalText = normalizedBillingOverview.renewal_date
    ? `Renews ${new Date(normalizedBillingOverview.renewal_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`
    : null;

  const items = [
    { route: "/generation", label: "Generation" },
    { route: "/account", label: "Account" },
    { route: "/connections", label: "Connections" },
    { route: "/billing", label: "Billing" },
    ...(canManageSettings ? [{ route: "/settings", label: "Settings" }] : [])
  ];
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const displayName = auth?.name?.trim() || account?.fullName?.trim() || "Account";
  const dropdownItemBaseClass =
    "flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition-all duration-150 focus-visible:outline-none";
  const dropdownItemActiveClass =
    "bg-[linear-gradient(135deg,rgba(34,211,238,.2),rgba(59,130,246,.14))] text-cyan-100 shadow-[inset_0_0_0_1px_rgba(34,211,238,.08),0_10px_18px_rgba(2,6,23,.16)]";
  const dropdownItemIdleClass =
    "text-slate-200 hover:translate-x-0.5 hover:bg-slate-800 hover:text-white hover:shadow-[0_10px_20px_rgba(2,6,23,.26)] focus-visible:bg-slate-800 focus-visible:text-white";
  const dropdownSuperadminClass =
    "text-violet-100 hover:translate-x-0.5 hover:bg-violet-500/18 hover:text-white hover:shadow-[0_10px_20px_rgba(76,29,149,.18)] focus-visible:bg-violet-500/18 focus-visible:text-white";
  const dropdownSignOutClass =
    "text-rose-100 hover:translate-x-0.5 hover:bg-rose-500/18 hover:text-white hover:shadow-[0_10px_20px_rgba(190,24,93,.16)] focus-visible:bg-rose-500/18 focus-visible:text-white";

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutside = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      if (menuButtonRef.current?.contains(target)) return;
      setMenuOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  return (
    <header className="mb-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <Link href="/" className="flex items-center gap-3 transition hover:opacity-90">
          <BrandLogo className="h-10 w-10 shrink-0 md:h-12 md:w-12" />
          <div>
            <h1 className="text-base font-bold tracking-tight md:text-base">
              <SparklesText textClassName="font-bold tracking-tight">UGC Persona</SparklesText>
            </h1>
            <p className="mt-0.5 text-[11px] font-medium text-slate-300 md:text-xs">Your AI influencer. Your brand. Infinite content.</p>
          </div>
        </Link>

        {auth ? (
          <div className="relative shrink-0">
            <button
              ref={menuButtonRef}
              type="button"
              aria-expanded={menuOpen}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-400/35 bg-slate-950/70 px-1.5 py-1.5 shadow-[0_0_0_1px_rgba(8,145,178,.12),0_10px_24px_rgba(2,6,23,.28)] transition hover:border-cyan-300/55 hover:bg-slate-900/80"
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              <img src={auth.avatar_url} alt={displayName} className="h-8 w-8 rounded-full object-cover" />
              <svg
                viewBox="0 0 20 20"
                className={cn("h-4 w-4 text-slate-300 transition-transform", menuOpen && "rotate-180")}
                aria-hidden
              >
                <path d="m5 7.5 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {menuOpen ? (
              <div
                ref={menuRef}
                className="absolute right-0 top-full z-50 mt-2 w-[250px] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950/95 p-2 shadow-[0_18px_40px_rgba(2,6,23,.55)] backdrop-blur"
              >
                <div className="rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2.5">
                  <p className="truncate text-sm font-semibold text-slate-100">{displayName}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-400">{auth.email}</p>
                </div>

                <div className="mt-2 space-y-1">
                  {items.map((item) => (
                    <button
                      key={`menu-${item.route}`}
                      type="button"
                      className={cn(
                        dropdownItemBaseClass,
                        route === item.route
                          ? dropdownItemActiveClass
                          : dropdownItemIdleClass
                      )}
                      onClick={() => {
                        setMenuOpen(false);
                        navigate(item.route);
                      }}
                      >
                      <span>{item.label}</span>
                    </button>
                  ))}

                  <button
                    type="button"
                    className={cn(dropdownItemBaseClass, dropdownItemIdleClass)}
                    onClick={() => {
                      setMenuOpen(false);
                      router.visit("/docs", { preserveScroll: true });
                    }}
                  >
                    <span>Docs</span>
                  </button>

                  {canManageSettings ? (
                    <button
                      type="button"
                      className={cn(dropdownItemBaseClass, dropdownSuperadminClass)}
                      onClick={() => {
                        setMenuOpen(false);
                        router.visit("/admin/settings");
                      }}
                    >
                      <span>Superadmin</span>
                    </button>
                  ) : null}

                  <div className="my-1 h-px bg-slate-800" />

                  <button
                    type="button"
                    className={cn(dropdownItemBaseClass, "font-normal", dropdownSignOutClass)}
                    onClick={() => {
                      setMenuOpen(false);
                      router.delete("/users/sign_out");
                    }}
                  >
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900/75 p-2">
        {items.map((item) => (
          <button
            key={item.route}
            type="button"
            className={cn(
              "rounded-xl px-3 py-2 text-sm font-medium transition",
              route === item.route ? "bg-cyan-500 text-slate-950" : "bg-slate-800 text-slate-200 hover:bg-slate-700"
            )}
            onClick={() => navigate(item.route)}
          >
            {item.label}
          </button>
        ))}

        {canManageSettings ? (
          <button
            type="button"
            className="rounded-xl border border-violet-500/60 bg-violet-500/10 px-3 py-2 text-sm font-medium text-violet-100 transition hover:bg-violet-500/20"
            onClick={() => router.visit("/admin/settings")}
          >
            Superadmin
          </button>
        ) : null}

        <div className="ml-auto min-w-[230px] rounded-xl border border-slate-700 bg-slate-950/60 px-3 py-2 text-xs text-slate-300">
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold text-slate-200">
              {planName}
              {normalizedBillingOverview.has_active_subscription ? (
                <span className="ml-1.5 inline-flex items-center rounded-full border border-emerald-400/35 bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-emerald-300">
                  Active
                </span>
              ) : null}
            </span>
            <span>
              {isByoApi
                ? "BYO API"
                : normalizedBillingOverview.has_active_subscription
                  ? `${availableCredits.toLocaleString("en-US")} / ${maxCredits.toLocaleString("en-US")}`
                  : "No credits"}
            </span>
          </div>
          {!isByoApi ? (
            <>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-emerald-400 transition-all"
                  style={{ width: `${creditsPercentage}%` }}
                />
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                {renewalText || "Attach a subscription to show the monthly credit balance here."}
              </p>
            </>
          ) : (
            <p className="mt-2 text-[11px] text-slate-500">Provider costs are charged directly on your own API accounts.</p>
          )}
        </div>

        {hasRequiredApiSetup ? null : canManageSettings ? (
          <button
            type="button"
            className="rounded-xl border border-red-500/70 bg-red-500/10 px-3 py-2 text-xs text-red-200 transition hover:bg-red-500/15"
            onClick={() => navigate("/settings")}
          >
            API not set
          </button>
        ) : (
          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
            Waiting for superadmin setup
          </div>
        )}
      </div>
    </header>
  );
}

function MarketingLandingPage({ onLogin }) {
  const videoSources = [
    "/media/home/ugc-1.mp4",
    "/media/home/ugc-2.mp4",
    "/media/home/ugc-3.mp4"
  ];
  const heroTiles = Array.from({ length: 9 }, (_, index) => ({
    id: index + 1,
    src: videoSources[index % videoSources.length]
  }));
  const socialPlatforms = ["Facebook", "Instagram", "TikTok", "LinkedIn"];
  const influencerGridImages = [
    "/media/influencer-grid/office-01.jpg",
    "/media/influencer-grid/office-02.jpg",
    "/media/influencer-grid/office-03.jpg",
    "/media/influencer-grid/office-04.jpg",
    "/media/influencer-grid/office-05.jpg",
    "/media/influencer-grid/office-06.jpg",
    "/media/influencer-grid/office-07.jpg",
    "/media/influencer-grid/office-08.jpg"
  ];
  const influencerTopRow = influencerGridImages.slice(0, 4);
  const influencerBottomRow = influencerGridImages.slice(4, 8);
  const ugcScriptTags = ["[HOOK]", "[PROBLEM]", "[SOLUTION]", "[CTA]"];
  const ugcScriptPreview = useMemo(() => {
    const source =
      "[HOOK] Stop scrolling, this one change can boost your content results fast [PROBLEM] creators waste hours on scripts [SOLUTION] we generate ready-to-shoot UGC in seconds [CTA] try it now";
    const words = source.trim().split(/\s+/);
    return words.length > 10 ? `${words.slice(0, 10).join(" ")}...` : source;
  }, []);
  const voiceWaveHeights = [6, 10, 14, 20, 12, 18, 9, 16, 22, 11, 17, 8, 14, 6];
  const stepThreeBeamContainerRef = useRef(null);
  const stepThreeSourceRef = useRef(null);
  const stepThreeInstagramRef = useRef(null);
  const stepThreeFacebookRef = useRef(null);
  const stepThreeTiktokRef = useRef(null);
  const stepThreeLinkedinRef = useRef(null);
  const toolkitCards = [
    "Creative Studio",
    "Avatar Generator",
    "Influencer Feed",
    "Product Ads",
    "MMS to UGC",
    "Social Snippets"
  ];
  const resultStats = [
    {
      value: "+87%",
      label: "ROI",
      description: "Brands see higher returns by creating and testing ads faster with our AI ad generator."
    },
    {
      value: "10x",
      label: "Ad Variations",
      description: "AI enables rapid testing of multiple creatives at scale."
    },
    {
      value: "+40%",
      label: "Conversion Rate",
      description: "Personalized AI ads turn viewers into buyers more effectively."
    },
    {
      value: "+30%",
      label: "CTR",
      description: "AI-optimized ads capture more attention than manually created ads."
    }
  ];
  function socialLogo(platform) {
    if (platform === "Facebook") {
      return (
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#1877F2] text-base font-black text-white">
          f
        </span>
      );
    }
    if (platform === "Instagram") {
      return (
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-[8px] bg-gradient-to-br from-[#F9CE34] via-[#EE2A7B] to-[#6228D7]">
          <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-white stroke-[1.9]" aria-hidden>
            <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="4.6" />
            <circle cx="12" cy="12" r="3.8" />
            <circle cx="17.2" cy="6.8" r="1" className="fill-white stroke-none" />
          </svg>
        </span>
      );
    }
    if (platform === "TikTok") {
      return (
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black text-sm font-black text-white ring-1 ring-slate-600">
          ♪
        </span>
      );
    }
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] bg-[#0A66C2] text-[11px] font-black text-white">
        in
      </span>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-40 border-b border-slate-800/90 bg-black/80 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-[1140px] items-center gap-4 px-4 py-3 md:px-6">
          <Link href="/" className="flex items-center gap-2">
            <BrandLogo className="h-8 w-8 shrink-0" />
            <span className="text-sm font-semibold uppercase tracking-[0.18em] text-white">UGC Persona</span>
          </Link>

          <nav className="ml-auto flex items-center gap-2">
            <a href="#pricing" className="rounded-lg px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-900">
              Pricing
            </a>
            <button type="button" onClick={onLogin} className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:border-slate-500 hover:bg-slate-800">
              Login
            </button>
          </nav>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-slate-800 bg-black">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_5%,rgba(79,70,229,.55),transparent_46%),radial-gradient(circle_at_20%_18%,rgba(251,146,60,.35),transparent_38%),radial-gradient(circle_at_80%_14%,rgba(56,189,248,.4),transparent_38%)]" />
          <div className="relative mx-auto w-full max-w-[1140px] px-4 pb-10 pt-14 md:px-6 md:pb-14 md:pt-20">
            <div className="mx-auto max-w-2xl text-center">
              <h1 className="text-4xl font-semibold tracking-tight text-white md:text-6xl">Design your own VERY photorealistic AI influencer.</h1>
              <p className="mx-auto mt-4 max-w-xl text-sm text-slate-200 md:text-base">
                Generate authentic UGC videos effortlessly — from concept to ready-to-share content in minutes.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3">
                <button type="button" onClick={onLogin} className="rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition hover:bg-slate-200">
                  Login to app
                </button>
                <a href="#pricing" className="rounded-xl border border-slate-500 bg-slate-900/70 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800">
                  View pricing
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-6 md:px-6">
          <p className="mb-3 text-center text-xs font-semibold uppercase tracking-[0.16em] text-violet-400">Creator Showcase</p>
          <div className="overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max snap-x snap-mandatory gap-3">
              {heroTiles.map((tile) => (
                <div
                  key={tile.id}
                  className="group relative h-[260px] w-[148px] shrink-0 snap-start overflow-hidden rounded-xl border border-slate-700 bg-slate-900 md:h-[320px] md:w-[180px]"
                >
                  <video
                    src={tile.src}
                    className="h-full w-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/65 to-transparent" />
                  <span className="pointer-events-none absolute bottom-2 right-2 rounded-full border border-white/30 bg-black/55 px-2 py-1 text-[10px] font-semibold text-white/90">
                    MUTE
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="mb-3 text-sm font-medium text-slate-300 md:text-base">
              Engage your audience across the world&apos;s most popular social platforms.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center md:grid-cols-4">
            {socialPlatforms.map((platform) => (
              <div
                key={platform}
                className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-xs font-semibold tracking-wide text-slate-200"
              >
                {socialLogo(platform)}
                <span>{platform}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {resultStats.map((stat) => (
              <div key={stat.label} className="metric-card rounded-2xl border border-slate-800/90 bg-black px-6 py-8 text-center shadow-[0_10px_30px_rgba(2,6,23,.45)]">
                <div className="relative z-10">
                  <p className="text-5xl font-semibold tracking-tight text-indigo-400 md:text-6xl">{stat.value}</p>
                  <p className="mt-5 text-2xl font-semibold tracking-tight text-white md:text-3xl">{stat.label}</p>
                  <p className="mx-auto mt-4 max-w-[32ch] leading-snug text-slate-300">{stat.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-10 md:px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight md:text-4xl">Build your lifelike influencer for your product or service.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-400 md:text-base">
            Use generative AI image and video to amaze followers and generate leads.
          </p>
          <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-950 p-4 shadow-[0_16px_40px_rgba(2,6,23,.5)] md:p-6">
            <div className="grid gap-4 md:grid-cols-[1.45fr_0.95fr] md:items-center">
              <div className="overflow-hidden rounded-xl border border-slate-800/80 bg-black/40 p-2">
                <Marquee pauseOnHover className="[--duration:34s] [--gap:0.75rem]">
                  {influencerTopRow.map((src, idx) => (
                    <img
                      key={`top-${src}`}
                      src={src}
                      alt={`Influencer sample ${idx + 1}`}
                      className="aspect-[9/16] w-20 rounded-lg border border-slate-700 object-cover md:w-24 lg:w-28"
                      loading="lazy"
                    />
                  ))}
                </Marquee>
                <Marquee reverse pauseOnHover className="mt-3 [--duration:34s] [--gap:0.75rem]">
                  {influencerBottomRow.map((src, idx) => (
                    <img
                      key={`bottom-${src}`}
                      src={src}
                      alt={`Influencer sample ${idx + 5}`}
                      className="aspect-[9/16] w-20 rounded-lg border border-slate-700 object-cover md:w-24 lg:w-28"
                      loading="lazy"
                    />
                  ))}
                </Marquee>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">UGC Persona</p>
                <h3 className="mt-2 text-2xl font-semibold tracking-tight">Effortless Influencer Creation</h3>
                <p className="mt-3 text-sm text-slate-300">
                  Easily create your influencer and place them in any setting with the outfit you prefer. You&apos;ll always achieve spectacular
                  results that boost success on your social channels.
                </p>
                <button type="button" onClick={onLogin} className="mt-5 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-slate-200">
                  Login
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-10 md:px-6">
          <div className="space-y-8 rounded-2xl border border-slate-800 bg-slate-950 p-5 shadow-[0_16px_40px_rgba(2,6,23,.5)]">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-violet-400 md:text-base">Social Preview Lab</p>
              <h3 className="mt-2 text-4xl font-semibold tracking-tight md:text-5xl">See your content inside real social previews.</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-300 md:text-lg">
                Validate how your UGC looks before publishing. Preview the same video in Instagram Reels and Stories layouts to fine-tune framing, CTA placement, and overall impact.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 md:items-start">
              <div className="flex flex-col items-center gap-2">
                <Iphone className="h-[600px] w-auto max-w-full md:h-[700px]">
                  <div className="relative h-full w-full">
                    <video
                      src={videoSources[0]}
                      className="h-full w-full bg-black object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                    <SocialPreviewOverlay mode="instagram_reels" />
                  </div>
                </Iphone>
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Instagram Reels</p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <Iphone className="h-[600px] w-auto max-w-full md:h-[700px]">
                  <div className="relative h-full w-full">
                    <video
                      src={videoSources[1]}
                      className="h-full w-full bg-black object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                    <SocialPreviewOverlay mode="instagram_stories" />
                  </div>
                </Iphone>
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Instagram Stories</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1140px] gap-6 px-4 py-8 md:px-6">
          <div className="grid gap-6 md:min-h-[26rem] md:grid-cols-2 md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">Step 1</p>
              <h3 className="mt-2 text-2xl font-semibold">Generate Your Influencer With Top AI Models</h3>
              <p className="mt-3 text-sm text-slate-400">
                Unlike other tools, you only answer a few simple questions about your business. UGC Persona builds a consistent influencer identity
                and places it in the right setting automatically.
              </p>
            </div>
            <div className="relative h-[24rem] overflow-hidden rounded-2xl border border-slate-800 bg-black/80 shadow-[0_16px_30px_rgba(2,6,23,.45)]">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-16 bg-gradient-to-r from-black/90 to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-16 bg-gradient-to-l from-black/90 to-transparent" />
              <Marquee pauseOnHover className="h-full items-center px-4 [--duration:24s] [--gap:0.85rem]">
                {influencerGridImages.map((src, index) => (
                  <article
                    key={`step-one-${src}`}
                    className={cn(
                      "group relative h-[20.5rem] w-[11.2rem] overflow-hidden rounded-2xl border border-slate-700/90 bg-slate-900/70 shadow-[0_12px_24px_rgba(2,6,23,.48)] transition-transform duration-500",
                      index % 2 === 0 ? "rotate-[0.8deg]" : "-rotate-[0.8deg]"
                    )}
                  >
                    <img
                      src={src}
                      alt={`Influencer gallery ${index + 1}`}
                      className="h-full w-full object-cover object-top transition duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/45 to-transparent" />
                  </article>
                ))}
              </Marquee>
            </div>
          </div>

          <div className="grid gap-6 md:min-h-[26rem] md:grid-cols-2 md:items-center">
            <div className="order-2 min-h-[24rem] rounded-2xl border border-slate-800 bg-black/85 p-4 shadow-[0_16px_30px_rgba(2,6,23,.45)] md:order-1 md:min-h-[24rem]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-300">AI Text + Voice Pipeline</p>

              <div className="mt-3 grid items-center gap-2 rounded-xl border border-slate-700/90 bg-slate-950/70 p-2 text-[11px] md:grid-cols-[1fr_auto_1fr_auto_1fr]">
                <div className="rounded-lg border border-slate-700 bg-slate-900/80 px-2.5 py-2">
                  <p className="flex items-center gap-1.5 font-semibold text-slate-100">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <rect x="4" y="4" width="16" height="16" rx="2.2" />
                      <path d="M8 9h8M8 13h5" />
                    </svg>
                    URL / Content
                  </p>
                  <p className="mt-1 text-slate-400">Page link, text, or product brief.</p>
                </div>

                <span className="hidden text-slate-500 md:block">→</span>

                <div className="rounded-lg border border-cyan-700/70 bg-cyan-500/10 px-2.5 py-2">
                  <p className="flex items-center gap-1.5 font-semibold text-cyan-200">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M12 3l1.8 3.8L18 8.6l-3 2.9.7 4.2L12 13.8l-3.7 1.9.7-4.2-3-2.9 4.2-1.8L12 3z" />
                    </svg>
                    AI Generation
                  </p>
                  <p className="mt-1 text-cyan-100/80">Builds UGC script and speech pacing.</p>
                </div>

                <span className="hidden text-slate-500 md:block">→</span>

                <div className="rounded-lg border border-slate-700 bg-slate-900/80 px-2.5 py-2">
                  <p className="flex items-center gap-1.5 font-semibold text-slate-100">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M4 6h16v12H4z" />
                      <path d="M8 10h8M8 14h6" />
                    </svg>
                    Script + Audio
                  </p>
                  <p className="mt-1 text-slate-400">Voice-ready output for your influencer.</p>
                </div>
              </div>

              <p className="mt-3 rounded-xl border border-slate-700/90 bg-slate-900/80 px-3 py-3 font-mono text-sm leading-relaxed text-slate-200">
                {ugcScriptPreview}
              </p>

              <div className="mt-2 rounded-xl border border-slate-700/90 bg-slate-900/80 px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-200">
                    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M5 10v4h3l4 3V7l-4 3H5z" />
                      <path d="M16 9a4 4 0 010 6" />
                      <path d="M18.7 6.8a7.5 7.5 0 010 10.4" />
                    </svg>
                    AI Voice Preview
                  </p>
                  <div className="flex items-end gap-[3px]">
                    {voiceWaveHeights.map((height, index) => (
                      <span
                        key={`wave-${index}`}
                        className="waveform-bar"
                        style={{
                          height: `${height}px`,
                          animationDelay: `${index * 0.08}s`
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {ugcScriptTags.map((tag) => (
                  <span key={tag} className="rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-slate-300">
                    {tag}
                  </span>
                ))}
                <span className="rounded-lg border border-emerald-700/70 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
                  TTS Optimized
                </span>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">Step 2</p>
              <h3 className="mt-2 text-2xl font-semibold">Create Script And Social Copy With AI</h3>
              <p className="mt-3 text-sm text-slate-400">
                Share your web page URL or paste your text. Our AI analyzes it to generate a script with exact timing for social platforms and also
                creates the right supporting copy for sponsored ads or organic reels.
              </p>
            </div>
          </div>

          <div className="grid gap-6 md:min-h-[26rem] md:grid-cols-2 md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-400">Step 3</p>
              <h3 className="mt-2 text-2xl font-semibold">Preview, Validate, And Download Your Final Video</h3>
              <p className="mt-3 text-sm text-slate-400">
                Watch the final result, check how it appears in Instagram Reels or Stories, and download it instantly so you can publish on your
                channels right away.
              </p>
            </div>
            <div ref={stepThreeBeamContainerRef} className="relative h-64 overflow-hidden rounded-2xl border border-slate-800 bg-black/90 shadow-[0_16px_30px_rgba(2,6,23,.45)] md:h-[24rem]">
              <div className="pointer-events-none absolute inset-x-0 top-1/2 z-0 h-px bg-slate-800/90" />

              <div
                ref={stepThreeSourceRef}
                className="absolute left-6 top-1/2 z-20 inline-flex h-20 w-20 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white text-slate-900 shadow-[0_10px_22px_rgba(0,0,0,.5)] md:h-24 md:w-24"
              >
                <svg viewBox="0 0 24 24" className="h-10 w-10 fill-none stroke-current stroke-[1.8]" aria-hidden>
                  <rect x="3.5" y="6.5" width="12.5" height="11" rx="2.2" />
                  <path d="M16 10l4-2.8v9.6L16 14z" />
                  <path d="M8.5 10.2v3.6l3.1-1.8-3.1-1.8z" className="fill-current stroke-none" />
                </svg>
              </div>

              <div className="absolute right-6 top-[18%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeInstagramRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#F9CE34] via-[#EE2A7B] to-[#6228D7] text-white md:h-11 md:w-11">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-white stroke-[1.9]" aria-hidden>
                    <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="4.6" />
                    <circle cx="12" cy="12" r="3.8" />
                    <circle cx="17.2" cy="6.8" r="1" className="fill-white stroke-none" />
                  </svg>
                </div>
              </div>

              <div className="absolute right-6 top-[41%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeFacebookRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1877F2] text-lg font-black text-white md:h-11 md:w-11">
                  f
                </div>
              </div>

              <div className="absolute right-6 top-[64%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeTiktokRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-black text-base font-black text-white md:h-11 md:w-11">
                  ♪
                </div>
              </div>

              <div className="absolute right-6 top-[86%] z-20 inline-flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-slate-300/70 bg-white shadow-[0_8px_18px_rgba(0,0,0,.45)] md:h-16 md:w-16">
                <div ref={stepThreeLinkedinRef} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0A66C2] text-sm font-black text-white md:h-11 md:w-11">
                  in
                </div>
              </div>

              <AnimatedBeam containerRef={stepThreeBeamContainerRef} fromRef={stepThreeSourceRef} toRef={stepThreeInstagramRef} curvature={120} duration={3.2} />
              <AnimatedBeam containerRef={stepThreeBeamContainerRef} fromRef={stepThreeSourceRef} toRef={stepThreeFacebookRef} curvature={44} duration={3.4} delay={0.2} />
              <AnimatedBeam containerRef={stepThreeBeamContainerRef} fromRef={stepThreeSourceRef} toRef={stepThreeTiktokRef} curvature={-34} duration={3.7} delay={0.45} />
              <AnimatedBeam
                containerRef={stepThreeBeamContainerRef}
                fromRef={stepThreeSourceRef}
                toRef={stepThreeLinkedinRef}
                curvature={-106}
                duration={4}
                delay={0.7}
                reverse
              />
            </div>
          </div>
        </section>


        <section className="relative overflow-hidden border-y border-slate-800 bg-slate-950 py-12 text-white">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,.45),transparent_45%),radial-gradient(circle_at_80%_80%,rgba(236,72,153,.35),transparent_40%)]" />
          <div className="relative mx-auto w-full max-w-[1140px] px-4 md:px-6">
            <h2 className="text-center text-3xl font-semibold tracking-tight">Lead & Revenue Impact</h2>
            <p className="mx-auto mt-2 max-w-3xl text-center text-sm text-slate-200/90">
              Publishing UGC content consistently across organic channels and paid ads helps brands generate more qualified leads, increase sales,
              and boost brand awareness.
            </p>
            <div className="mt-8 grid gap-3 md:grid-cols-4">
              {[
                {
                  label: "Qualified leads",
                  value: "+320%",
                  description: "More inbound leads from always-on organic reels plus retargeting ads."
                },
                {
                  label: "Ad-to-lead conversion",
                  value: "2.4x",
                  description: "UGC-native creatives convert better than static ads in paid campaigns."
                },
                {
                  label: "Organic reach",
                  value: "+68%",
                  description: "Short-form content cadence improves visibility and audience discovery."
                },
                {
                  label: "Brand awareness",
                  value: "+41%",
                  description: "Higher branded searches and stronger recall after multichannel distribution."
                }
              ].map((item) => (
                <div key={item.label} className="rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.14em] text-cyan-200">{item.label}</p>
                  <p className="mt-3 text-3xl font-semibold">{item.value}</p>
                  <p className="mt-1 text-sm text-slate-200">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="mx-auto w-full max-w-[1140px] px-4 py-12 md:px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight md:text-4xl">Pricing</h2>
          <p className="mx-auto mt-3 max-w-3xl text-center text-base text-slate-300 md:text-lg">
            Choose the plan that matches how many final UGC videos you want to produce each month.
          </p>

          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            <MagicCard allowOverflow className="pricing-card flex h-full flex-col p-7 md:p-8">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-100">Starter</p>
              </div>
              <p className="mt-5 text-3xl font-semibold tracking-tight md:text-4xl">
                €39 <span className="text-[0.68em] font-medium text-slate-300">/ month</span>
              </p>
              <ul className="mt-8 space-y-3.5 text-sm text-slate-300 md:text-[1.02rem]">
                {[
                  "220 credits per month",
                  "Ideal for testing short UGC videos",
                  "Supports videos from 5 to 30 seconds",
                  "Images, script, voice, and video generation included"
                ].map((item, idx) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-violet-300 via-fuchsia-400 to-cyan-300 shadow-[0_0_10px_rgba(168,85,247,.45)]" />
                    <div className="inline-flex items-center gap-2">
                      <span>{item}</span>
                      {idx === 0 ? <CreditsInfoTooltip billingSettings={settings.billingSettings} /> : null}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                <button type="button" onClick={onLogin} className="w-full rounded-2xl bg-violet-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-violet-500">
                  Start now
                </button>
              </div>
            </MagicCard>

            <MagicCard allowOverflow className="pricing-card flex h-full flex-col p-7 md:p-8">
              <p className="text-sm font-semibold text-slate-100">Basic</p>
              <p className="mt-5 text-3xl font-semibold tracking-tight md:text-4xl">
                €79 <span className="text-[0.68em] font-medium text-slate-300">/ month</span>
              </p>
              <ul className="mt-8 space-y-3.5 text-sm text-slate-300 md:text-[1.02rem]">
                {[
                  "500 credits per month",
                  "Ideal for ongoing monthly content production",
                  "Supports videos from 5 to 30 seconds",
                  "Images, script, voice, and video generation included"
                ].map((item, idx) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-sky-300 via-cyan-400 to-indigo-300 shadow-[0_0_10px_rgba(34,211,238,.4)]" />
                    <div className="inline-flex items-center gap-2">
                      <span>{item}</span>
                      {idx === 0 ? <CreditsInfoTooltip billingSettings={settings.billingSettings} /> : null}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                <button type="button" onClick={onLogin} className="w-full rounded-2xl bg-violet-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-violet-500">
                  Start now
                </button>
              </div>
            </MagicCard>

            <MagicCard allowOverflow className="pricing-card flex h-full flex-col p-7 md:p-8">
              <p className="text-sm font-semibold text-slate-100">Pro</p>
              <p className="mt-5 text-3xl font-semibold tracking-tight md:text-4xl">
                €149 <span className="text-[0.68em] font-medium text-slate-300">/ month</span>
              </p>
              <ul className="mt-8 space-y-3.5 text-sm text-slate-300 md:text-[1.02rem]">
                {[
                  "1100 credits per month",
                  "Ideal for higher-volume video production",
                  "Supports videos from 5 to 30 seconds",
                  "Images, script, voice, and video generation included"
                ].map((item, idx) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-emerald-300 via-teal-400 to-cyan-300 shadow-[0_0_10px_rgba(45,212,191,.4)]" />
                    <div className="inline-flex items-center gap-2">
                      <span>{item}</span>
                      {idx === 0 ? <CreditsInfoTooltip billingSettings={settings.billingSettings} /> : null}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                <button type="button" onClick={onLogin} className="w-full rounded-2xl bg-violet-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-violet-500">
                  Start now
                </button>
              </div>
            </MagicCard>
          </div>

          <div className="mx-auto mt-6 max-w-[34rem]">
            <MagicCard allowOverflow className="pricing-card flex h-full flex-col p-7 text-center md:p-8">
              <p className="text-sm font-semibold text-cyan-200">B2B / Private Label</p>
              <p className="mt-5 text-[1.75rem] font-semibold leading-snug text-slate-100">Want to use your own API keys?</p>
              <p className="mt-3 text-base leading-relaxed text-slate-300">Use the platform with your own providers and unlimited usage.</p>
              <ul className="mt-8 space-y-3.5 text-sm text-slate-300 md:text-[1.02rem]">
                {[
                  "Bring your own API keys",
                  "White label ready",
                  "Agency usage",
                  "Custom limits",
                  "No credit system"
                ].map((item) => (
                  <li key={item} className="flex items-start justify-center gap-3 text-left">
                    <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gradient-to-br from-cyan-300 via-sky-400 to-teal-300 shadow-[0_0_10px_rgba(34,211,238,.42)]" />
                    <span className="min-w-[16rem]">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-10">
                <a
                  href="mailto:hello@ugcpersona.com?subject=UGC%20Persona%20-%20B2B%20Inquiry"
                  className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-600 bg-slate-900 px-4 py-3 text-base font-semibold text-slate-100 transition hover:border-slate-400 hover:bg-slate-800"
                >
                  Contact sales
                </a>
              </div>
            </MagicCard>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 py-12 md:px-6">
          <h2 className="text-center text-3xl font-semibold tracking-tight md:text-4xl">FAQ</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-slate-400 md:text-base">
            The most common questions about credits, billing logic, retries, and agency usage.
          </p>
          <div className="mx-auto mt-8 grid max-w-5xl gap-4 md:grid-cols-2">
            {[
              {
                q: "How do credits work?",
                a: [
                  "Credits are used each time you generate content inside the platform.",
                  "Video generation uses credits based on the number of seconds generated.",
                  "Images, script, voice, and voice optimization also use credits."
                ]
              },
              {
                q: "How is video generation charged?",
                a: [
                  "Video generation is charged based on the actual duration of the generated video.",
                  "The system rounds the duration up to the next full second."
                ]
              },
              {
                q: "What does “rounded up” mean?",
                a: [
                  "If your generated video lasts 7.2 seconds, it will be billed as 8 seconds.",
                  "If it lasts 15.1 seconds, it will be billed as 16 seconds."
                ]
              },
              {
                q: "What video lengths are supported?",
                a: [
                  "You can generate videos from about 5 seconds up to 30 seconds."
                ]
              },
              {
                q: "How many credits does a video use?",
                a: [
                  "The total depends on the final duration and the number of generation steps used.",
                  "As a general rule, video generation uses:",
                  "2 credits × billed seconds",
                  "Other steps like images, script, voice, and voice optimization also consume credits."
                ]
              },
              {
                q: "Why do retries sometimes use credits?",
                a: [
                  "Credits are deducted only if the provider actually processes and bills the request."
                ]
              },
              {
                q: "Do unused credits roll over?",
                a: [
                  "No. Credits reset at each monthly renewal."
                ]
              },
              {
                q: "Can I buy extra credits?",
                a: [
                  "Not at the moment.",
                  "Each plan includes a fixed monthly amount of credits."
                ]
              },
              {
                q: "What happens if I run out of credits?",
                a: [
                  "You’ll need to wait for the next renewal or upgrade to a higher plan."
                ]
              },
              {
                q: "Can I use my own API keys?",
                a: [
                  "Yes. Agency customers can use a Bring Your Own API setup."
                ]
              },
              {
                q: "Do you offer refunds?",
                a: [
                  "Used credits are not refundable.",
                  "Refunds are only considered in case of billing or technical errors."
                ]
              }
            ].map((item) => (
              <details key={item.q} className="group h-fit rounded-xl border border-slate-800 bg-slate-950 p-4 shadow-[0_8px_24px_rgba(2,6,23,.35)]">
                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-slate-100 marker:content-['']">
                  <span className="flex-1">{item.q}</span>
                  <span className="shrink-0 pt-0.5 text-slate-400 transition group-open:rotate-45">+</span>
                </summary>
                <div className="mt-3 space-y-2 text-sm leading-relaxed text-slate-300">
                  {item.a.map((line, idx) => (
                    <p key={`${item.q}-${idx}`}>{line}</p>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1140px] px-4 pb-14 md:px-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6 text-center shadow-[0_12px_30px_rgba(2,6,23,.5)] md:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-violet-300">Support</p>
            <h2 className="mt-2 text-3xl font-semibold tracking-tight">Have questions? We are here to help.</h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-slate-300">
              We help you configure workflows, APIs, and models to get the best out of your UGC content, from the initial concept to the final
              publish-ready video.
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={onLogin}
                className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-500"
              >
                Enter the web app
              </button>
              <a
                href="mailto:support@ugcpersona.com"
                className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
              >
                Contact support
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-800 bg-slate-950 py-10 text-slate-300">
        <div className="mx-auto flex w-full max-w-[1140px] flex-col items-center px-4 text-center md:px-6">
          <div className="flex items-center gap-2">
            <BrandLogo className="h-8 w-8" />
            <p className="text-base font-semibold tracking-tight text-white">UGC Persona</p>
          </div>
          <p className="mt-1 text-xs text-slate-400">Your AI influencer. Your brand. Infinite content.</p>
          <p className="mt-3 text-xs text-slate-500">© {new Date().getFullYear()} UGC Persona. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function InspirationPage({ settings }) {
  const [copiedPromptId, setCopiedPromptId] = useState("");
  const [showCopyNotice, setShowCopyNotice] = useState(false);
  const [actionNotice, setActionNotice] = useState({ text: "", kind: "ok" });
  const [lightboxImage, setLightboxImage] = useState(null);
  const [generatingById, setGeneratingById] = useState({});
  const [savingById, setSavingById] = useState({});
  const [generatedById, setGeneratedById] = useState({});
  const [savedById, setSavedById] = useState({});
  const copyNoticeTimeoutRef = useRef(null);
  const actionNoticeTimeoutRef = useRef(null);
  const canGenerateFromQuery = useMemo(() => {
    const params = new URLSearchParams(window.location.search || "");
    return params.has("generate");
  }, []);

  useEffect(() => {
    if (!lightboxImage) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setLightboxImage(null);
      }
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightboxImage]);

  useEffect(() => {
    return () => {
      if (copyNoticeTimeoutRef.current) {
        window.clearTimeout(copyNoticeTimeoutRef.current);
      }
      if (actionNoticeTimeoutRef.current) {
        window.clearTimeout(actionNoticeTimeoutRef.current);
      }
    };
  }, []);

  function showTimedActionNotice(text, kind = "ok") {
    setActionNotice({ text, kind });
    if (actionNoticeTimeoutRef.current) {
      window.clearTimeout(actionNoticeTimeoutRef.current);
    }
    actionNoticeTimeoutRef.current = window.setTimeout(() => {
      setActionNotice({ text: "", kind: "ok" });
    }, 1600);
  }

  async function handleCopyPrompt(prompt, id) {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(prompt);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = prompt;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopiedPromptId(id);
      setShowCopyNotice(true);

      if (copyNoticeTimeoutRef.current) {
        window.clearTimeout(copyNoticeTimeoutRef.current);
      }
      copyNoticeTimeoutRef.current = window.setTimeout(() => {
        setShowCopyNotice(false);
      }, 1300);

      window.setTimeout(() => {
        setCopiedPromptId((prev) => (prev === id ? "" : prev));
      }, 1400);
    } catch {
      setCopiedPromptId("");
      setShowCopyNotice(false);
    }
  }

  async function handleGeneratePromptImage(item) {
    const wavespeedKey = settings?.apiKeys?.wavespeed || "";
    if (!wavespeedKey) {
      showTimedActionNotice("Wavespeed API key is missing.", "error");
      return;
    }

    try {
      setGeneratingById((prev) => ({ ...prev, [item.id]: true }));
      const data = await apiCall("/api/generate-image", {
        provider: "wavespeed",
        apiKey: wavespeedKey,
        model: "google/nano-banana-pro/text-to-image",
        prompt: item.prompt,
        aspectRatio: "9:16",
        wavespeedOptions: {
          outputFormat: "png",
          resolution: "1k",
          enableSyncMode: false,
          enableBase64Output: false
        }
      });

      const normalizedImageUrl = normalizeExternalImageUrl(data.imageUrl);
      setGeneratedById((prev) => ({ ...prev, [item.id]: normalizedImageUrl }));
      showTimedActionNotice("Image generated.");
    } catch (error) {
      showTimedActionNotice(error.message || "Image generation failed.", "error");
    } finally {
      setGeneratingById((prev) => ({ ...prev, [item.id]: false }));
    }
  }

  async function handleSavePromptImage(item) {
    const generatedUrl = normalizeExternalImageUrl(generatedById[item.id]);
    if (!generatedUrl) return;

    try {
      setSavingById((prev) => ({ ...prev, [item.id]: true }));
      let data;
      try {
        data = await apiCall("/api/inspiration/save-image", {
          imageUrl: generatedUrl,
          fileName: item.fileName
        });
      } catch (firstError) {
        // Fallback: let the browser fetch the image and upload as data URL.
        const imageResponse = await fetch(generatedUrl);
        if (!imageResponse.ok) throw firstError;
        const imageBlob = await imageResponse.blob();
        const imageDataUrl = await blobToDataUrl(imageBlob);
        if (!imageDataUrl) throw firstError;
        data = await apiCall("/api/inspiration/save-image", {
          imageDataUrl,
          fileName: item.fileName
        });
      }

      const cacheBustUrl = `${data.imageUrl}?v=${Date.now()}`;
      setSavedById((prev) => ({ ...prev, [item.id]: cacheBustUrl }));
      showTimedActionNotice("Saved to gallery.");
    } catch (error) {
      showTimedActionNotice(error.message || "Save failed.", "error");
    } finally {
      setSavingById((prev) => ({ ...prev, [item.id]: false }));
    }
  }

  function handleCloseTab() {
    if (typeof window === "undefined") return;
    window.close();
  }

  return (
    <div className="space-y-4">
      <MagicCard>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">Prompt Library</p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-white md:text-3xl">UGC Inspiration Gallery</h2>
            <p className="mt-2 text-sm text-slate-300">
              24 ready-to-use prompts. Click <span className="font-semibold text-cyan-200">Copy prompt</span> and paste into Image Generation.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
              onClick={handleCloseTab}
            >
              Close tab
            </button>
          </div>
        </div>
      </MagicCard>

      <MagicCard>
        <div className="rounded-xl border border-slate-700/80 bg-slate-950/60 p-3 text-xs text-slate-300">
          Image folder: <span className="font-semibold text-slate-100">/web/public/media/inspiration/</span>
          <span className="mx-1 text-slate-500">|</span>
          Naming format: <span className="font-semibold text-cyan-200">prompt-01.png</span> to{" "}
          <span className="font-semibold text-cyan-200">prompt-24.png</span>
        </div>
      </MagicCard>

      <MagicCard>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4">
          {INSPIRATION_PROMPTS.map((item, index) => (
            <div key={item.id} className="rounded-xl border border-slate-700 bg-slate-900/70 p-2 text-left transition hover:border-slate-500">
              {(() => {
                const cardImageSrc = savedById[item.id] || generatedById[item.id] || item.imageUrl;
                return (
              <button
                type="button"
                className="block w-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                onClick={() => setLightboxImage({ src: cardImageSrc, alt: item.title })}
                aria-label={`Open ${item.title} preview`}
              >
                <img
                  src={cardImageSrc}
                  alt={item.title}
                  className="aspect-[4/5] w-full cursor-zoom-in rounded-lg border border-slate-700 object-cover"
                  onError={(event) => {
                    const node = event.currentTarget;
                    if (node.dataset.fallbackApplied === "true") return;
                    node.dataset.fallbackApplied = "true";
                    node.src = INSPIRATION_FALLBACK_IMAGES[index % INSPIRATION_FALLBACK_IMAGES.length];
                  }}
                />
              </button>
                );
              })()}
              <p className="mt-2 text-xs font-medium text-slate-200">{item.title}</p>
              <p className="mt-2 max-h-28 overflow-auto rounded-lg border border-slate-700/80 bg-slate-950/70 p-2 text-[11px] leading-relaxed text-slate-300">
                {item.prompt}
              </p>
              <button
                type="button"
                className="mt-2 w-full rounded-xl border border-cyan-700/70 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition hover:bg-cyan-500/20"
                onClick={() => handleCopyPrompt(item.prompt, item.id)}
              >
                {copiedPromptId === item.id ? "Copied" : "Copy prompt"}
              </button>
              {canGenerateFromQuery && (
                <button
                  type="button"
                  className="mt-2 w-full rounded-xl border border-violet-700/70 bg-violet-500/10 px-3 py-2 text-xs font-semibold text-violet-100 transition hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleGeneratePromptImage(item)}
                  disabled={Boolean(generatingById[item.id])}
                >
                  {generatingById[item.id] ? "Generating..." : "Generate image"}
                </button>
              )}
              {canGenerateFromQuery && generatedById[item.id] && (
                <button
                  type="button"
                  className="mt-2 w-full rounded-xl border border-emerald-700/70 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => handleSavePromptImage(item)}
                  disabled={Boolean(savingById[item.id])}
                >
                  {savingById[item.id] ? "Saving..." : "Save to gallery"}
                </button>
              )}
            </div>
          ))}
        </div>
      </MagicCard>

      {showCopyNotice && (
        <div className="fixed right-4 top-4 z-[60] rounded-lg border border-emerald-500/60 bg-emerald-500/15 px-3 py-2 pr-10 text-xs font-semibold text-emerald-100 shadow-lg backdrop-blur">
          Prompt copied
          <button
            type="button"
            onClick={() => setShowCopyNotice(false)}
            className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md border border-emerald-200/20 bg-emerald-50/10 text-emerald-100 transition hover:bg-emerald-50/20"
            aria-label="Dismiss"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-4 w-4">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>
      )}
      {actionNotice.text && (
        <div
          className={cn(
            "fixed right-4 top-16 z-[60] rounded-lg border px-3 py-2 pr-10 text-xs font-semibold shadow-lg backdrop-blur",
            actionNotice.kind === "error"
              ? "border-red-500/60 bg-red-500/15 text-red-100"
              : "border-cyan-500/60 bg-cyan-500/15 text-cyan-100"
          )}
        >
          {actionNotice.text}
          <button
            type="button"
            onClick={() => setActionNotice({ text: "", kind: "ok" })}
            className={cn(
              "absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md border bg-black/15 transition hover:bg-black/25",
              actionNotice.kind === "error"
                ? "border-red-200/20 text-red-100"
                : "border-cyan-200/20 text-cyan-100"
            )}
            aria-label="Dismiss"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-4 w-4">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>
      )}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative w-full max-w-3xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="absolute right-2 top-2 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/60 text-white transition hover:bg-black/80"
              onClick={() => setLightboxImage(null)}
              aria-label="Close image preview"
            >
              ✕
            </button>
            <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-950/95 shadow-2xl">
              <img src={lightboxImage.src} alt={lightboxImage.alt} className="max-h-[88vh] w-full object-contain" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GenerationPage({
  pipeline,
  operationCosts,
  forms,
  setForms,
  activeStep,
  setActiveStep,
  wizardImageMode,
  setWizardImageMode,
  wizardEditIntent,
  setWizardEditIntent,
  wizardStarted,
  setWizardStarted,
  wizardExpanded,
  setWizardExpanded,
  approvedVoices,
  settings,
  auth,
  activeModels,
  loading,
  socialConnections,
  socialFeatures,
  publishResults,
  onGenerateImage,
  onUseUploadedInfluencerImage,
  onApproveImage,
  onGenerateImageEdit,
  onApproveImageEdit,
  onSkipImageEdit,
  onGenerateCopy,
  onEnhanceCopy,
  onApproveCopy,
  onGenerateTts,
  onApproveAudio,
  onOptimizeVideoPrompt,
  onGenerateVideo,
  onPublishInstagramReel,
  onUploadTiktokVideo,
  onOpenConnections,
  onClearFeedback,
  onSetFeedback
}) {
  const selectedImageModel = forms.imageModel;
  const imageAspectRatioOptions = getImageAspectRatioOptions(
    forms.imageProvider,
    selectedImageModel
  );
  const selectedImageEditModel = forms.imageEditModel;
  const imageEditAspectRatioOptions = getImageAspectRatioOptions(
    forms.imageEditProvider,
    selectedImageEditModel
  );
  const wavespeedParams = getWavespeedImageModelParams(selectedImageModel);
  const wavespeedEditParams = getWavespeedImageModelParams(selectedImageEditModel);
  const isWavespeed = forms.imageProvider === "wavespeed";
  const isWavespeedEdit = forms.imageEditProvider === "wavespeed";
  const autoWavespeedSize = mapAspectRatioToWavespeedSize(forms.imageAspectRatio);
  const autoWavespeedEditSize = mapAspectRatioToWavespeedSize(forms.imageEditAspectRatio);
  const isAdCopy = forms.copyContentType === "ad";
  const canUseStep4Controls = pipeline.audio.approved || Boolean(pipeline.video.url);
  const videoPreviewRef = useRef(null);
  const shouldFillPhoneFrame = forms.socialPreviewEnabled;
  const [uploadedInfluencerImageDataUrl, setUploadedInfluencerImageDataUrl] = useState("");
  const [uploadedInfluencerImageName, setUploadedInfluencerImageName] = useState("");
  const [imageEditReferenceSlots, setImageEditReferenceSlots] = useState(() => [createEmptyReferenceImageSlot()]);
  const shouldRunImageEdit = wizardEditIntent !== "no";
  const isUploadImageMode = wizardImageMode === "upload";
  const isWizardReady = Boolean(wizardImageMode && wizardEditIntent);
  const isWizardCollapsed = wizardStarted && !wizardExpanded;
  const stepOneAspectRatioOptions = imageAspectRatioOptions;
  const stepOneAspectRatioPreview = getAspectRatioPreviewData(forms.imageAspectRatio);
  const stepOneModelLabel = getReadableModelName(forms.imageModel);
  const stepOneProviderLabel = forms.imageProvider === "wavespeed" ? "WAVESPEED" : "FAL";
  const stepOneResolutionOptions = resolveImageResolutionOptions(forms.imageProvider, forms.imageModel);
  const stepTwoModelLabel = getReadableModelName(forms.imageEditModel);
  const stepTwoProviderLabel = forms.imageEditProvider === "wavespeed" ? "WAVESPEED" : "FAL";
  const stepTwoResolutionOptions = resolveImageResolutionOptions(forms.imageEditProvider, forms.imageEditModel);
  const imageStepBilling = combineBillings(operationCosts?.image);
  const imageEditStepBilling = combineBillings(operationCosts?.imageEdit);
  const copyStepBilling = combineBillings(operationCosts?.copy, operationCosts?.copyEnhance, operationCosts?.ttsEstimate);
  const copyStepCreditsSpent = sumCreditsUsed(operationCosts?.copy, operationCosts?.copyEnhance);
  const ttsStepBilling = combineBillings(operationCosts?.tts);
  const videoStepBilling = combineBillings(operationCosts?.video);
  const showAuroraPromptOptimizer = Boolean(auth?.superadmin && settings?.auroraPromptChainSettings?.enabled);
  const availableApprovedVoices = useMemo(() => uniqVoiceEntries(approvedVoices), [approvedVoices]);
  const availableApprovedVoiceGroups = useMemo(() => buildVoiceGroups(availableApprovedVoices), [availableApprovedVoices]);
  const hasSavedApprovedVoiceOutsideCurrentList = Boolean(
    forms.voiceId && !availableApprovedVoices.some((voice) => voice.voice_id === forms.voiceId)
  );
  const selectedApprovedVoice = useMemo(
    () => availableApprovedVoices.find((voice) => voice.voice_id === forms.voiceId) || null,
    [availableApprovedVoices, forms.voiceId]
  );
  const copyScriptTiming = analyzeScriptTiming(forms.generatedCopy, forms.copyMaxSeconds);
  const stepTwoAspectRatioPreview = getAspectRatioPreviewData(forms.imageEditAspectRatio);
  const videoAspectRatioOptions = getVideoAspectRatioOptions(forms.videoModel);
  const videoAspectRatioPreview = getAspectRatioPreviewData(forms.videoAspectRatio);
  const videoResolutionOptions = getVideoResolutionOptions(forms.videoModel);
  const showAdminModelMeta = Boolean(auth?.superadmin);
  const metaConnection = getMetaConnection(socialConnections);
  const tiktokConnection = getTiktokConnection(socialConnections);
  const defaultInstagramAccount = getDefaultInstagramAccount(socialConnections);
  const defaultTiktokAccount = getDefaultTiktokAccount(socialConnections);
  const tiktokUploadScopeGranted = Boolean(
    Array.isArray(tiktokConnection?.granted_scopes) && tiktokConnection.granted_scopes.includes("video.upload")
  );
  const canPublishInstagramReel = Boolean(socialFeatures?.meta_configured && defaultInstagramAccount && pipeline.video.url);
  const canUploadTiktok = Boolean(socialFeatures?.tiktok_configured && defaultTiktokAccount && pipeline.video.url && tiktokUploadScopeGranted);
  const normalizedPublishHashtags = normalizeHashtagString(forms.publishHashtags);
  const finalPublishCaption = [forms.publishCaption.trim(), normalizedPublishHashtags].filter(Boolean).join("\n\n");
  const selectedPublishTargetIds = Array.isArray(forms.publishTargetIds) ? forms.publishTargetIds : [];
  const selectedPublishTargets = PUBLISH_TARGET_DEFINITIONS.filter((target) => selectedPublishTargetIds.includes(target.id));
  const hasPublishedInstagramReel = Boolean(publishResults?.instagramReel?.permalink || publishResults?.instagramReel?.mediaId);
  const hasUploadedTiktok = Boolean(publishResults?.tiktok?.draftUploaded || publishResults?.tiktok?.publishId || publishResults?.tiktok?.postId);
  const publishTargetCards = PUBLISH_TARGET_DEFINITIONS.map((target) => {
    if (target.id === "instagram_reel") {
      const isReady = Boolean(socialFeatures?.meta_configured && defaultInstagramAccount);
      return {
        ...target,
        disabled: false,
        statusTone: isReady ? "available" : "requires_connection",
        helperText: isReady
          ? `Ready with ${defaultInstagramAccount?.name || defaultInstagramAccount?.username || "Instagram account"}`
          : socialFeatures?.meta_configured
            ? "Connect Meta and select a default Instagram account in Connections."
            : "Meta publishing is not configured by the superadmin yet."
      };
    }

    if (target.id === "tiktok") {
      const isReady = Boolean(socialFeatures?.tiktok_configured && defaultTiktokAccount && tiktokUploadScopeGranted);
      return {
        ...target,
        disabled: false,
        statusTone: isReady ? "available" : "requires_connection",
        helperText: isReady
          ? `Ready to upload draft with ${defaultTiktokAccount?.name || defaultTiktokAccount?.username || "TikTok account"}`
          : !socialFeatures?.tiktok_configured
            ? "TikTok uploading is not configured by the superadmin yet."
            : !defaultTiktokAccount
              ? "Connect TikTok and pick the default TikTok destination in Connections."
              : "Reconnect TikTok to grant the video.upload scope."
      };
    }

    return {
      ...target,
      disabled: true,
      statusTone: "coming_soon",
      helperText: "Coming soon"
    };
  });

  const hasCopyOutput = Boolean(
    forms.generatedCopy.trim() ||
      forms.generatedCaptionText.trim() ||
      forms.generatedPrimaryText.trim() ||
      forms.generatedHeadline.trim() ||
      forms.generatedDescription.trim() ||
      pipeline.copy.text
  );
  const hasImageEditOutput = Boolean(pipeline.imageEdit?.url);
  const statusList = [
    {
      step: 1,
      label: "1. Image",
      text: pipeline.image.url ? (pipeline.image.approved ? "approved" : "generated") : "pending",
      ok: Boolean(pipeline.image.url)
    },
    {
      step: 2,
      label: "2. Image Edit",
      text: !shouldRunImageEdit
        ? "disabled"
        : pipeline.imageEdit?.approved
          ? "approved"
          : pipeline.imageEdit?.skipped
            ? "skipped"
            : hasImageEditOutput
              ? "generated"
              : "pending",
      ok: Boolean(pipeline.imageEdit?.approved || hasImageEditOutput),
      disabled: !shouldRunImageEdit
    },
    {
      step: 3,
      label: "3. Copy",
      text: hasCopyOutput ? (pipeline.copy.approved ? "approved" : "generated") : "pending",
      ok: hasCopyOutput
    },
    {
      step: 4,
      label: "4. TTS",
      text: pipeline.audio.url ? (pipeline.audio.approved ? "approved" : "generated") : "pending",
      ok: Boolean(pipeline.audio.url)
    },
    {
      step: 5,
      label: "5. Video",
      text: pipeline.video.url ? "generated" : "pending",
      ok: Boolean(pipeline.video.url)
    },
    {
      step: 6,
      label: "6. Delivery",
      text: !pipeline.video.url ? "locked" : (hasPublishedInstagramReel || hasUploadedTiktok) ? "completed" : "ready",
      ok: hasPublishedInstagramReel || hasUploadedTiktok,
      disabled: !pipeline.video.url
    }
  ];
  const canApproveCopy = Boolean(forms.generatedCopy.trim());

  function handleStep3ScriptChange(nextValue) {
    setForms((prev) => ({
      ...prev,
      generatedCopy: nextValue,
      ttsText: nextValue
    }));
  }

  useEffect(() => {
    if (wizardImageMode !== "generate") return;
    setForms((prev) => {
      const nextAspectRatio = stepOneAspectRatioOptions.includes(prev.imageAspectRatio)
        ? prev.imageAspectRatio
        : stepOneAspectRatioOptions[0] || DEFAULT_ASPECT_RATIO;
      const nextPrompt = prev.imagePrompt.trim() ? prev.imagePrompt : DEFAULT_IMAGE_PROMPT;

      if (
        prev.imageAspectRatio === nextAspectRatio &&
        prev.imagePrompt === nextPrompt
      ) {
        return prev;
      }

      return {
        ...prev,
        imageAspectRatio: nextAspectRatio,
        imagePrompt: nextPrompt
      };
    });
  }, [wizardImageMode, setForms, stepOneAspectRatioOptions]);

  useEffect(() => {
    const detected = detectLikelyLanguageClient(forms.sourceText);
    setForms((prev) => {
      if (prev.copyLanguageManual) return prev;
      const nextCode = normalizeCopyLanguageCode(detected.code);
      if (prev.copyLanguage === nextCode) return prev;
      return {
        ...prev,
        copyLanguage: nextCode
      };
    });
  }, [forms.sourceText, setForms]);

  useEffect(() => {
    if (shouldRunImageEdit || activeStep !== 2) return;
    setActiveStep(1);
  }, [shouldRunImageEdit, activeStep]);

  useEffect(() => {
    if (activeStep !== 4) return;
    const nextTtsText = String(pipeline.copy.text || forms.generatedCopy || "").trim();
    if (!nextTtsText) return;
    setForms((prev) => (prev.ttsText === nextTtsText ? prev : { ...prev, ttsText: nextTtsText }));
  }, [activeStep, pipeline.copy.text, forms.generatedCopy, setForms]);

  function handleVideoPreviewPlay() {
    const node = videoPreviewRef.current;
    if (!node) return;
    const maybePromise = node.play();
    if (maybePromise && typeof maybePromise.catch === "function") {
      maybePromise.catch(() => {});
    }
  }

  function handleVideoPreviewStop() {
    const node = videoPreviewRef.current;
    if (!node) return;
    node.pause();
    node.currentTime = 0;
  }

  function handleVideoPreviewRestart() {
    const node = videoPreviewRef.current;
    if (!node) return;
    node.currentTime = 0;
    const maybePromise = node.play();
    if (maybePromise && typeof maybePromise.catch === "function") {
      maybePromise.catch(() => {});
    }
  }

  function handleStartWizardFlow() {
    if (!isWizardReady) return;
    setWizardStarted(true);
    setWizardExpanded(false);
    const nextEditProvider = forms.imageEditProvider || "fal";
    const availableModels = getImageEditModelsByProvider(nextEditProvider, activeModels);
    const preferredModel = nextEditProvider === "wavespeed" ? DEFAULT_WAVESPEED_IMAGE_EDIT_MODEL : DEFAULT_FAL_IMAGE_EDIT_MODEL;
    const nextEditModel = availableModels.includes(forms.imageEditModel)
      ? forms.imageEditModel
      : availableModels.includes(preferredModel)
        ? preferredModel
        : availableModels[0] || preferredModel;
    const nextAspectOptions = getImageAspectRatioOptions(nextEditProvider, nextEditModel);
    setForms((prev) => ({
      ...prev,
      imageEditProvider: nextEditProvider,
      imageEditModel: nextEditModel,
      imageEditAspectRatio: nextAspectOptions.includes(prev.imageEditAspectRatio)
        ? prev.imageEditAspectRatio
        : nextAspectOptions[0] || prev.imageAspectRatio || DEFAULT_ASPECT_RATIO
    }));
    setActiveStep(1);
  }

  function handleInfluencerImageUpload(event) {
    const file = event.target.files?.[0];
    if (!file) {
      setUploadedInfluencerImageDataUrl("");
      setUploadedInfluencerImageName("");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setUploadedInfluencerImageDataUrl(typeof reader.result === "string" ? reader.result : "");
      setUploadedInfluencerImageName(file.name || "influencer-image");
    };
    reader.readAsDataURL(file);
  }

  async function handleUseUploadedInfluencerImage(event) {
    event.preventDefault();
    if (!uploadedInfluencerImageDataUrl) return;
    await onUseUploadedInfluencerImage(uploadedInfluencerImageDataUrl, {
      enforceVideoAspectRatio: !shouldRunImageEdit,
      requiredAspectRatio: "9:16"
    });
  }

  function handleApproveImageAndNext() {
    onClearFeedback();
    onApproveImage();
    if (!shouldRunImageEdit) {
      onSkipImageEdit();
      setActiveStep(3);
      return;
    }
    setActiveStep(2);
  }

  function handleImageEditReferenceUpload(index, event) {
    const file = event.target.files?.[0] || null;
    setImageEditReferenceSlots((prev) =>
      prev.map((slot, slotIndex) =>
        slotIndex === index
          ? {
              file,
              name: file?.name || "",
              dataUrl: ""
            }
          : slot
      )
    );
  }

  function handleConfirmImageEditReferenceUpload(index, event) {
    event.preventDefault();
    const slot = imageEditReferenceSlots[index];
    if (!slot?.file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const nextDataUrl = typeof reader.result === "string" ? reader.result : "";
      setImageEditReferenceSlots((prev) =>
        prev.map((entry, slotIndex) =>
          slotIndex === index
            ? {
                ...entry,
                dataUrl: nextDataUrl,
                name: slot.file?.name || entry.name || "uploaded-image"
              }
            : entry
        )
      );
    };
    reader.readAsDataURL(slot.file);
  }

  function handleAddImageEditReferenceSlot() {
    setImageEditReferenceSlots((prev) => [...prev, createEmptyReferenceImageSlot()]);
  }

  async function handleGenerateImageEditAndStore(event) {
    event.preventDefault();
    await onGenerateImageEdit({
      provider: forms.imageEditProvider,
      model: forms.imageEditModel,
      prompt: forms.imageEditPrompt,
      aspectRatio: forms.imageEditAspectRatio,
      imageSize: forms.imageEditSizeOverride,
      outputFormat: forms.imageEditOutputFormat,
      resolution: forms.imageEditResolution,
      enableSyncMode: forms.imageEditEnableSyncMode,
      enableBase64Output: forms.imageEditEnableBase64Output,
      extraImageDataUrls: imageEditReferenceSlots.map((slot) => slot.dataUrl).filter(Boolean)
    });
  }

  function handleApproveImageEditAndNext() {
    onClearFeedback();
    onApproveImageEdit();
    setActiveStep(3);
  }

  function handleSkipImageEditAndNext() {
    onClearFeedback();
    onSkipImageEdit();
    setActiveStep(3);
  }

  async function handleApproveCopyAndNext() {
    onClearFeedback();
    const approvedScript = await onApproveCopy();
    if (approvedScript) {
      setForms((prev) => ({
        ...prev,
        generatedCopy: approvedScript,
        ttsText: approvedScript
      }));
      setActiveStep(4);
    }
  }

  function handleApproveAudioAndNext() {
    onClearFeedback();
    onApproveAudio();
    setActiveStep(5);
  }

  function togglePublishTarget(targetId) {
    const target = publishTargetCards.find((item) => item.id === targetId);
    if (!target || target.disabled) return;

    setForms((prev) => {
      const currentIds = Array.isArray(prev.publishTargetIds) ? prev.publishTargetIds : [];
      const nextIds = currentIds.includes(targetId)
        ? currentIds.filter((item) => item !== targetId)
        : [...currentIds, targetId];

      return {
        ...prev,
        publishTargetIds: nextIds
      };
    });
  }

  async function handlePublishSelectedTargets() {
    if (!selectedPublishTargetIds.length) {
      onSetFeedback({ message: "Select at least one destination in Step 6 before sending.", kind: "error" });
      return;
    }

    if (!pipeline.video.url) {
      onSetFeedback({ message: "Generate the final video in Step 5 before sending it to Instagram or TikTok.", kind: "error" });
      return;
    }

    if (selectedPublishTargetIds.includes("tiktok") && !defaultTiktokAccount?.id) {
      onSetFeedback({ message: "Connect TikTok and choose a default TikTok account first.", kind: "error" });
      return;
    }

    if (selectedPublishTargetIds.includes("tiktok") && !tiktokUploadScopeGranted) {
      onSetFeedback({ message: "Reconnect TikTok to grant the video.upload scope before uploading a draft.", kind: "error" });
      return;
    }

    let submitted = false;

    if (selectedPublishTargetIds.includes("instagram_reel")) {
      const instagramSubmitted = await onPublishInstagramReel(finalPublishCaption);
      submitted = submitted || instagramSubmitted;
    }

    if (selectedPublishTargetIds.includes("tiktok")) {
      const tiktokSubmitted = await onUploadTiktokVideo();
      submitted = submitted || tiktokSubmitted;
    }

    if (!submitted) {
      onSetFeedback({ message: "The selected destinations are not available yet.", kind: "error" });
    }
  }

  async function handleDownloadAsset(assetUrl, fileName) {
    if (!assetUrl) return;
    try {
      const response = await fetch("/api/download-file", {
        method: "POST",
        credentials: "same-origin",
        headers: buildJsonHeaders(),
        body: JSON.stringify({
          url: assetUrl,
          fileName
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Unable to download file.");
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      onSetFeedback?.({
        message: error.message || "Unable to download file.",
        kind: "error"
      });
    }
  }

  return (
    <div className="space-y-4">
      <MagicCard className="border border-slate-700/70 bg-[radial-gradient(circle_at_top,rgba(34,211,238,.16),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(99,102,241,.16),transparent_42%),rgba(2,6,23,.75)]">
        <div className={cn("space-y-4", isWizardCollapsed && "space-y-3")}>
          <div className={cn("flex flex-col gap-3 md:flex-row md:justify-between", isWizardCollapsed ? "md:items-center" : "md:items-start")}>
            <div className={cn("space-y-1", isWizardCollapsed && "space-y-0")}>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">Generation Wizard</p>
              {!isWizardCollapsed && (
                <>
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-100">Start your influencer workflow</h2>
                  <p className="text-sm text-slate-300">Answer two quick questions and we&apos;ll guide the flow for image creation, image edit, and copy generation.</p>
                </>
              )}
            </div>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/45 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-500/15"
              onClick={() => setWizardExpanded((prev) => !prev)}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                <path d="M12 2.5l2.1 5.3 5.4 2.2-5.4 2.2L12 17.5l-2.1-5.3-5.4-2.2 5.4-2.2L12 2.5zm6.5 13.2l.9 2.4 2.4.9-2.4.9-.9 2.4-.9-2.4-2.4-.9 2.4-.9.9-2.4zm-13-1.8l1.1 2.8 2.8 1.1-2.8 1.1-1.1 2.8-1.1-2.8-2.8-1.1 2.8-1.1 1.1-2.8z" />
              </svg>
              {wizardStarted && !wizardExpanded ? "Start again" : "Start Wizard"}
            </button>
          </div>

          {isWizardCollapsed ? (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-700/70 bg-slate-950/55 px-3 py-2.5 text-sm text-slate-300">
              <span className="font-semibold text-slate-100">Current flow:</span>
              <span className="rounded-full border border-slate-700 bg-slate-900/80 px-2.5 py-1 text-xs">
                {isUploadImageMode ? "Upload influencer image" : "Generate influencer image"}
              </span>
              <span className="rounded-full border border-slate-700 bg-slate-900/80 px-2.5 py-1 text-xs">
                {shouldRunImageEdit ? "Image edit enabled" : "Image edit skipped"}
              </span>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div className="rounded-xl border border-slate-700/70 bg-slate-950/60 p-5">
                  <p className="mb-8 text-2xl font-semibold text-slate-100">1) Do you want to generate the influencer image or upload one?</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      className={cn(
                        "wizard-choice-btn",
                        wizardImageMode === "generate" && "wizard-choice-btn-active"
                      )}
                      onClick={() => {
                        setWizardImageMode("generate");
                        setWizardEditIntent("");
                      }}
                    >
                    Start from Scratch
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "wizard-choice-btn",
                        wizardImageMode === "upload" && "wizard-choice-btn-active"
                      )}
                      onClick={() => {
                        setWizardImageMode("upload");
                        setWizardEditIntent("");
                      }}
                    >
                      I&apos;ll Upload my own UGC Image
                    </button>
                  </div>
                </div>

                {wizardImageMode && (
                  <div className="rounded-xl border border-slate-700/70 bg-slate-950/60 p-3">
                    <p className="mb-8 text-2xl font-semibold text-slate-100">2) Do you want to upload a product/setting image and/or edit the influencer image?</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        className={cn(
                          "wizard-choice-btn",
                          wizardEditIntent === "yes" && "wizard-choice-btn-active"
                        )}
                        onClick={() => setWizardEditIntent("yes")}
                      >
                        I want to upload a product/setting image
                      </button>
                      <button
                        type="button"
                        className={cn(
                          "wizard-choice-btn",
                          wizardEditIntent === "no" && "wizard-choice-btn-active"
                        )}
                        onClick={() => setWizardEditIntent("no")}
                      >
                        No, skip edit
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {(isWizardReady || wizardStarted) && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {isWizardReady ? (
                    <ShimmerButton
                      type="button"
                      className="wizard-choice-btn-compact"
                      background={null}
                      onClick={handleStartWizardFlow}
                    >
                      <span className="inline-flex items-center gap-2">
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
                          <path d="M12 2.5l2.1 5.3 5.4 2.2-5.4 2.2L12 17.5l-2.1-5.3-5.4-2.2 5.4-2.2L12 2.5zm6.5 13.2l.9 2.4 2.4.9-2.4.9-.9 2.4-.9-2.4-2.4-.9 2.4-.9.9-2.4zm-13-1.8l1.1 2.8 2.8 1.1-2.8 1.1-1.1 2.8-1.1-2.8-2.8-1.1 2.8-1.1 1.1-2.8z" />
                        </svg>
                        Start wizard
                      </span>
                    </ShimmerButton>
                  ) : null}
                  {wizardStarted && (
                    <button
                      type="button"
                      className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-slate-800"
                      onClick={() => {
                        setWizardStarted(false);
                        setWizardExpanded(true);
                      }}
                    >
                      Change answers
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </MagicCard>

      {wizardStarted && (
        <div className="workflow-shell">
      <div className="space-y-4 md:sticky md:top-24 md:h-fit md:self-start">
        <StatusPills
          statusList={statusList}
          activeStep={activeStep}
          onStepChange={(step) => {
            if (step === 2 && !shouldRunImageEdit) return;
            onClearFeedback();
            setActiveStep(step);
          }}
        />
      </div>

      <div className="flex flex-col gap-4 md:self-start">
      <MagicCard className={cn(activeStep !== 1 && "hidden")}>
        <h2 className="section-title mb-3">{isUploadImageMode ? "Influencer Image Upload" : "Image Generation"}</h2>
        {isUploadImageMode ? (
          <div className="grid gap-4 md:grid-cols-2 md:items-start">
            <form className="grid gap-3" onSubmit={handleUseUploadedInfluencerImage}>
              <label className="label-ui">
                Upload influencer image
                <div className="mt-1 grid gap-2 sm:grid-cols-[1fr_auto]">
                  <input className="input-ui" type="file" accept="image/*" onChange={handleInfluencerImageUpload} />
                  <button
                    type="submit"
                    className="wizard-choice-btn sm:min-w-[120px]"
                    disabled={!uploadedInfluencerImageDataUrl}
                  >
                    Upload
                  </button>
                </div>
                {uploadedInfluencerImageName && <span className="text-xs text-slate-400">Loaded: {uploadedInfluencerImageName}</span>}
                {!shouldRunImageEdit && (
                  <span className="text-xs text-amber-200">
                    This flow requires a 9:16 image. Non-compliant uploads will be rejected.
                  </span>
                )}
              </label>

              <div className="rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-xs text-slate-300">
                Upload a ready image of your influencer. After confirmation, the wizard will proceed to image edit (or directly to copy if edit is
                skipped).
              </div>

              {pipeline.image.url && (
                <ShimmerButton type="button" className="wizard-choice-btn-compact w-fit" background={null} onClick={handleApproveImageAndNext}>
                  <span className="inline-flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                    {shouldRunImageEdit ? "Confirm and continue to image edit" : "Confirm and continue to copy"}
                  </span>
                </ShimmerButton>
              )}

            </form>

            <div className="space-y-3">
              {pipeline.image.url ? (
                <div className="space-y-3">
                  <img
                    src={pipeline.image.url}
                    alt="Uploaded influencer"
                    className="mx-auto aspect-[9/16] w-full max-w-[42vh] max-h-[75vh] rounded-xl border border-slate-700 bg-slate-950/60 object-contain"
                  />
                </div>
              ) : (
                <div className="mx-auto flex aspect-[9/16] w-full max-w-[42vh] max-h-[75vh] flex-col items-center justify-center rounded-xl border border-dashed border-slate-700 bg-slate-950/45 p-4 text-center">
                  <svg viewBox="0 0 24 24" className="mb-3 h-14 w-14 text-slate-500" aria-hidden>
                    <path
                      d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5z"
                      className="fill-none stroke-current"
                      strokeWidth="1.5"
                    />
                    <circle cx="9" cy="9" r="1.6" className="fill-current" />
                    <path d="M6.5 16.5l3.3-3.3a1.2 1.2 0 0 1 1.7 0l1.9 1.9 2-2a1.2 1.2 0 0 1 1.7 0l1.4 1.4" className="fill-none stroke-current" strokeWidth="1.5" />
                  </svg>
                  <p className="text-sm font-semibold text-slate-300">9:16 Preview Placeholder</p>
                  <p className="mt-1 text-xs text-slate-500">Upload an image to preview it here.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <form className="grid gap-3 md:grid-cols-2" onSubmit={onGenerateImage}>
            <label className="label-ui md:col-span-2">
              Aspect ratio
              <div className="aspect-ratio-control">
                <select
                  className="input-ui input-ui-select aspect-ratio-select"
                  value={forms.imageAspectRatio}
                  onChange={(event) => setForms((prev) => ({ ...prev, imageAspectRatio: event.target.value }))}
                >
                  {stepOneAspectRatioOptions.map((ratio) => (
                    <option key={ratio} value={ratio}>
                      {formatAspectRatioOptionLabel(ratio)}
                    </option>
                  ))}
                </select>

                <div className="aspect-ratio-preview">
                  <div className="flex h-20 items-center justify-center rounded-lg border border-slate-700/80 bg-slate-950/80">
                    <div
                      className="rounded-md border border-cyan-400/80 bg-cyan-500/15 shadow-[0_0_0_1px_rgba(34,211,238,.15)]"
                      style={{
                        width: `${stepOneAspectRatioPreview.widthPct}%`,
                        height: `${stepOneAspectRatioPreview.heightPct}%`
                      }}
                    />
                  </div>
                  <p className="mt-1 text-center text-[10px] uppercase tracking-wide text-slate-400">
                    {stepOneAspectRatioPreview.label}
                  </p>
                </div>
              </div>
            </label>

            {showAdminModelMeta && (
              <div className="rounded-xl border border-cyan-500/35 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100 md:col-span-2">
                Model: <span className="font-semibold">{stepOneModelLabel}</span> | Provider:{" "}
                <span className="font-semibold">{stepOneProviderLabel}</span>
              </div>
            )}

            {showAdminModelMeta && stepOneResolutionOptions.length > 0 && (
              <div className="rounded-xl border border-cyan-500/35 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100">
                Resolution: <span className="font-semibold">{resolveManagedResolution(forms.imageResolution, stepOneResolutionOptions)}</span>
              </div>
            )}

            {isWavespeed && wavespeedParams.outputFormats.length > 0 && (
              <label className="label-ui">
                Output format
                <select
                  className="input-ui"
                  value={forms.wavespeedOutputFormat}
                  onChange={(event) => setForms((prev) => ({ ...prev, wavespeedOutputFormat: event.target.value }))}
                >
                  {wavespeedParams.outputFormats.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {isWavespeed && wavespeedParams.supportsEnableSyncMode && (
              <label className="label-ui">
                Enable sync mode
                <select
                  className="input-ui"
                  value={forms.wavespeedEnableSyncMode}
                  onChange={(event) => setForms((prev) => ({ ...prev, wavespeedEnableSyncMode: event.target.value }))}
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              </label>
            )}

            {isWavespeed && wavespeedParams.supportsEnableBase64Output && (
              <label className="label-ui">
                Enable base64 output
                <select
                  className="input-ui"
                  value={forms.wavespeedEnableBase64Output}
                  onChange={(event) => setForms((prev) => ({ ...prev, wavespeedEnableBase64Output: event.target.value }))}
                >
                  <option value="false">false</option>
                  <option value="true">true</option>
                </select>
              </label>
            )}

            {isWavespeed && wavespeedParams.supportsSizeOverride && (
              <label className="label-ui">
                Size override (optional)
                <input
                  className="input-ui"
                  value={forms.wavespeedSizeOverride}
                  onChange={(event) => setForms((prev) => ({ ...prev, wavespeedSizeOverride: event.target.value }))}
                  placeholder={autoWavespeedSize}
                />
              </label>
            )}

            {isWavespeed && wavespeedParams.supportsImage && (
              <label className="label-ui md:col-span-2">
                Reference image URL (optional)
                <input
                  className="input-ui"
                  value={forms.wavespeedReferenceImage}
                  onChange={(event) => setForms((prev) => ({ ...prev, wavespeedReferenceImage: event.target.value }))}
                  placeholder="https://..."
                />
              </label>
            )}

            {isWavespeed && wavespeedParams.supportsStrength && (
              <label className="label-ui">
                Strength (0-1)
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={forms.wavespeedStrength}
                  onChange={(event) => setForms((prev) => ({ ...prev, wavespeedStrength: event.target.value }))}
                />
              </label>
            )}

            {isWavespeed && wavespeedParams.supportsSeed && (
              <label className="label-ui">
                Seed (optional)
                <input
                  className="input-ui"
                  type="number"
                  min="0"
                  step="1"
                  value={forms.wavespeedSeed}
                  onChange={(event) => setForms((prev) => ({ ...prev, wavespeedSeed: event.target.value }))}
                  placeholder="12345"
                />
              </label>
            )}

            <label className="label-ui md:col-span-2">
              Prompt
              <textarea
                className="input-ui min-h-28"
                value={forms.imagePrompt}
                onChange={(event) => setForms((prev) => ({ ...prev, imagePrompt: event.target.value }))}
                placeholder="Describe the visual scene..."
              />
            </label>

            <button type="submit" className="wizard-choice-btn md:col-span-2" disabled={loading.image}>
              {loading.image ? "Generating..." : "Generate Image"}
            </button>

            <div className="md:col-span-2">
              <p className="text-sm font-semibold text-cyan-200">Prompt Examples</p>
              <p className="mb-2 text-xs text-slate-400">You can start from these prompts to build your influencer.</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-4">
                {IMAGE_PROMPT_PRESETS.map((preset) => {
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className="flex h-full w-full flex-col rounded-xl border border-slate-700 bg-slate-900/70 p-2 text-left transition hover:border-slate-500"
                      onClick={() => setForms((prev) => ({ ...prev, imagePrompt: preset.prompt }))}
                    >
                      <img src={preset.imageUrl} alt={preset.caption} className="aspect-[4/5] w-full shrink-0 rounded-lg border border-slate-700 object-cover" />
                      <p className="mt-2 min-h-[3.75rem] text-xs font-medium text-slate-200">{preset.caption}</p>
                    </button>
                  );
                })}
                <div className="flex h-full flex-col justify-between rounded-xl border border-dashed border-slate-600 bg-slate-900/40 p-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-cyan-200">Need more ideas?</p>
                    <p className="mt-1 text-xs text-slate-400">Open the full inspiration gallery with copy-paste prompts.</p>
                  </div>
                  <button
                    type="button"
                    onClick={openInspirationWindow}
                    className="mt-3 inline-flex items-center justify-center rounded-xl border border-slate-600 bg-slate-900 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-400 hover:bg-slate-800"
                  >
                    Get inspired
                  </button>
                </div>
              </div>
            </div>
          </form>

          <div className="space-y-3">
            {loading.image ? (
              <StepResultLoader label="Image generation in progress..." />
            ) : pipeline.image.url ? (
              <div className="space-y-3">
                <img
                  src={pipeline.image.url}
                  alt="Generated image"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950/60 object-contain md:mx-auto md:w-auto md:max-h-[68vh] md:max-w-full"
                />
                <div className="wizard-step-action-row">
                  <ShimmerButton type="button" className="wizard-choice-btn-compact wizard-step-action-confirm" background={null} onClick={handleApproveImageAndNext}>
                    <span className="inline-flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                        <path d="M5 12h14" />
                        <path d="M13 6l6 6-6 6" />
                      </svg>
                    {shouldRunImageEdit ? "Confirm and continue to image edit" : "Confirm and continue to copy"}
                    </span>
                  </ShimmerButton>
                  <ShimmerButton
                    type="button"
                    className="wizard-choice-btn-compact wizard-choice-btn-download wizard-step-action-download"
                    background={null}
                    onClick={() => handleDownloadAsset(pipeline.image.url, "ugc-image.png")}
                    aria-label="Download image"
                    title="Download image"
                  >
                    <span className="inline-flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                        <path d="M12 3v11" />
                        <path d="M7.5 10.5L12 15l4.5-4.5" />
                        <path d="M4 18.5h16" />
                      </svg>
                      <span className="md:hidden">Download image</span>
                    </span>
                  </ShimmerButton>
                </div>
              </div>
            ) : (
              <StepResultPlaceholder text="Generated image will appear here." />
            )}
          </div>
        </div>
        )}
      </MagicCard>

      <MagicCard className={cn(activeStep !== 2 && "hidden", !pipeline.image.approved && "opacity-60", !shouldRunImageEdit && "hidden")}>
        <h2 className="section-title mb-3">Step 2: Image Edit</h2>
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <fieldset disabled={!pipeline.image.approved}>
            <form className="grid gap-3 md:grid-cols-2" onSubmit={handleGenerateImageEditAndStore}>
              {showAdminModelMeta && (
                <div className="rounded-xl border border-cyan-500/35 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100 md:col-span-2">
                  Model: <span className="font-semibold">{stepTwoModelLabel}</span> | Provider:{" "}
                  <span className="font-semibold">{stepTwoProviderLabel}</span>
                </div>
              )}

              <label className="label-ui md:col-span-2">
                Aspect ratio
                <div className="aspect-ratio-control">
                  <select
                    className="input-ui input-ui-select aspect-ratio-select"
                    value={forms.imageEditAspectRatio}
                    onChange={(event) => setForms((prev) => ({ ...prev, imageEditAspectRatio: event.target.value }))}
                  >
                    {imageEditAspectRatioOptions.map((ratio) => (
                      <option key={ratio} value={ratio}>
                        {formatAspectRatioOptionLabel(ratio)}
                      </option>
                    ))}
                  </select>

                  <div className="aspect-ratio-preview">
                    <div className="flex h-20 items-center justify-center rounded-lg border border-slate-700/80 bg-slate-950/80">
                      <div
                        className="rounded-md border border-cyan-400/80 bg-cyan-500/15 shadow-[0_0_0_1px_rgba(34,211,238,.15)]"
                        style={{
                          width: `${stepTwoAspectRatioPreview.widthPct}%`,
                          height: `${stepTwoAspectRatioPreview.heightPct}%`
                        }}
                      />
                    </div>
                    <p className="mt-1 text-center text-[10px] uppercase tracking-wide text-slate-400">
                      {stepTwoAspectRatioPreview.label}
                    </p>
                  </div>
                </div>
              </label>

              {isWavespeedEdit && wavespeedEditParams.outputFormats.length > 0 && (
                <label className="label-ui">
                  Output format
                  <select
                    className="input-ui"
                    value={forms.imageEditOutputFormat}
                    onChange={(event) => setForms((prev) => ({ ...prev, imageEditOutputFormat: event.target.value }))}
                  >
                    {wavespeedEditParams.outputFormats.map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {showAdminModelMeta && stepTwoResolutionOptions.length > 0 && (
                <div className="rounded-xl border border-cyan-500/35 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100">
                  Resolution: <span className="font-semibold">{resolveManagedResolution(forms.imageEditResolution, stepTwoResolutionOptions)}</span>
                </div>
              )}

              {isWavespeedEdit && wavespeedEditParams.supportsEnableSyncMode && (
                <label className="label-ui">
                  Enable sync mode
                  <select
                    className="input-ui"
                    value={forms.imageEditEnableSyncMode}
                    onChange={(event) => setForms((prev) => ({ ...prev, imageEditEnableSyncMode: event.target.value }))}
                  >
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </label>
              )}

              {isWavespeedEdit && wavespeedEditParams.supportsEnableBase64Output && (
                <label className="label-ui">
                  Enable base64 output
                  <select
                    className="input-ui"
                    value={forms.imageEditEnableBase64Output}
                    onChange={(event) => setForms((prev) => ({ ...prev, imageEditEnableBase64Output: event.target.value }))}
                  >
                    <option value="false">false</option>
                    <option value="true">true</option>
                  </select>
                </label>
              )}

              {isWavespeedEdit && wavespeedEditParams.supportsSizeOverride && (
                <label className="label-ui">
                  Image size (optional)
                  <input
                    className="input-ui"
                    value={forms.imageEditSizeOverride}
                    onChange={(event) => setForms((prev) => ({ ...prev, imageEditSizeOverride: event.target.value }))}
                    placeholder={autoWavespeedEditSize}
                  />
                </label>
              )}

              <label className="label-ui md:col-span-2">
                Base image (from Step 1)
                <div className="mt-1 flex items-center gap-2">
                  <input className="input-ui flex-1" value={pipeline.image.url || ""} readOnly />
                  {pipeline.image.url ? (
                    <a
                      href={pipeline.image.url}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 overflow-hidden rounded-lg border border-slate-700 bg-slate-950/60"
                      aria-label="Open base image"
                    >
                      <img
                        src={pipeline.image.url}
                        alt="Base image preview"
                        className="h-12 w-12 object-cover md:h-14 md:w-14"
                      />
                    </a>
                  ) : (
                    <div className="h-12 w-12 shrink-0 rounded-lg border border-slate-700 bg-slate-950/60 md:h-14 md:w-14" />
                  )}
                </div>
              </label>

              <label className="label-ui md:col-span-2">
                Additional image for prompt (upload)
                <div className="mt-1 space-y-2">
                  {imageEditReferenceSlots.map((slot, index) => (
                    <div key={`image-edit-reference-slot-${index}`} className="grid gap-2 sm:grid-cols-[1fr_auto]">
                      <input
                        className="input-ui"
                        type="file"
                        accept="image/*"
                        onChange={(event) => handleImageEditReferenceUpload(index, event)}
                      />
                      <button
                        type="button"
                        className="wizard-choice-btn sm:min-w-[120px]"
                        disabled={!slot.file}
                        onClick={(event) => handleConfirmImageEditReferenceUpload(index, event)}
                      >
                        Upload
                      </button>
                      {slot.name && (
                        <span className="sm:col-span-2 text-xs text-slate-400">
                          {slot.dataUrl ? `Loaded: ${slot.name}` : `Selected: ${slot.name}`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-2 rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
                  onClick={handleAddImageEditReferenceSlot}
                >
                  Add image
                </button>
              </label>

              {imageEditReferenceSlots.some((slot) => slot.dataUrl) && (
                <div className="md:col-span-2 grid grid-cols-2 gap-2 rounded-xl border border-slate-700 bg-slate-950/40 p-2 md:grid-cols-4">
                  {imageEditReferenceSlots
                    .filter((slot) => slot.dataUrl)
                    .map((slot, index) => (
                      <img
                        key={`image-edit-reference-preview-${index}`}
                        src={slot.dataUrl}
                        alt={slot.name || "Additional reference"}
                        className="max-h-40 w-full rounded-lg object-cover"
                      />
                    ))}
                </div>
              )}

              <label className="label-ui md:col-span-2">
                Edit prompt
                <textarea
                  className="input-ui min-h-24"
                  value={forms.imageEditPrompt}
                  onChange={(event) => setForms((prev) => ({ ...prev, imageEditPrompt: event.target.value }))}
                  placeholder="Describe how to transform the image..."
                />
              </label>

              <div className="flex flex-wrap gap-2 md:col-span-2">
                <button type="submit" className="wizard-choice-btn" disabled={loading.imageEdit || !pipeline.image.approved}>
                  {loading.imageEdit ? "Generating..." : "Generate Edited Image"}
                </button>
                <button type="button" className="wizard-choice-btn" onClick={handleSkipImageEditAndNext}>
                  <span className="inline-flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                    Skip and continue to copy
                  </span>
                </button>
              </div>
            </form>
          </fieldset>

          <div className="space-y-3">
            {loading.imageEdit ? (
              <StepResultLoader label="Image edit in progress..." />
            ) : pipeline.imageEdit?.url ? (
              <div className="space-y-3">
                <img
                  src={pipeline.imageEdit.url}
                  alt="Edited image"
                  className="w-full rounded-xl border border-slate-700 bg-slate-950/60 object-contain md:mx-auto md:w-auto md:max-h-[68vh] md:max-w-full"
                />
                <div className="wizard-step-action-row">
                  <ShimmerButton type="button" className="wizard-choice-btn-compact wizard-step-action-confirm" background={null} onClick={handleApproveImageEditAndNext}>
                    <span className="inline-flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                        <path d="M5 12h14" />
                        <path d="M13 6l6 6-6 6" />
                      </svg>
                      Confirm edited image and continue to copy
                    </span>
                  </ShimmerButton>
                  <ShimmerButton
                    type="button"
                    className="wizard-choice-btn-compact wizard-choice-btn-download wizard-step-action-download"
                    background={null}
                    onClick={() => handleDownloadAsset(pipeline.imageEdit.url, "ugc-image-edited.png")}
                    aria-label="Download edited image"
                    title="Download edited image"
                  >
                    <span className="inline-flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                        <path d="M12 3v11" />
                        <path d="M7.5 10.5L12 15l4.5-4.5" />
                        <path d="M4 18.5h16" />
                      </svg>
                      <span className="md:hidden">Download edited image</span>
                    </span>
                  </ShimmerButton>
                </div>
                <StepCostAlert billing={imageEditStepBilling} showCreditsSpent={!auth?.superadmin} />
              </div>
            ) : (
              <StepResultPlaceholder text="Edited image will appear here." />
            )}
          </div>
        </div>
      </MagicCard>

      <MagicCard className={cn(activeStep !== 3 && "hidden", !pipeline.image.approved && "opacity-60")}>
        <h2 className="section-title mb-3">Step 3: UGC Copy Generation</h2>
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <fieldset disabled={!pipeline.image.approved}>
            <form className="grid gap-3 md:grid-cols-2" onSubmit={onGenerateCopy}>
              <label className="label-ui">
                Platform
                <select className="input-ui" value={forms.copyPlatform} onChange={(event) => setForms((prev) => ({ ...prev, copyPlatform: event.target.value }))}>
                  <option value="Instagram">Instagram</option>
                  <option value="Facebook">Facebook</option>
                  <option value="TikTok">TikTok</option>
                </select>
              </label>

              <label className="label-ui">
                Content type
                <select className="input-ui" value={forms.copyContentType} onChange={(event) => setForms((prev) => ({ ...prev, copyContentType: event.target.value }))}>
                  <option value="organic">Organic reel</option>
                  <option value="ad">Advertisement (Ads)</option>
                </select>
              </label>

              <label className="label-ui">
                Language
                <select
                  className="input-ui"
                  value={normalizeCopyLanguageCode(forms.copyLanguage)}
                  onChange={(event) =>
                    setForms((prev) => ({
                      ...prev,
                      copyLanguage: normalizeCopyLanguageCode(event.target.value),
                      copyLanguageManual: true
                    }))
                  }
                >
                  {COPY_LANGUAGE_OPTIONS.map((language) => (
                    <option key={language.code} value={language.code}>
                      {language.name}
                    </option>
                  ))}
                </select>
                {!forms.copyLanguageManual && <span className="text-xs text-slate-400">Default: auto-detected from your content.</span>}
              </label>

              <label className="label-ui">
                Max script seconds (6-30)
                <input
                  className="input-ui"
                  type="number"
                  min="6"
                  max="30"
                  step="1"
                  value={forms.copyMaxSeconds}
                  onChange={(event) => {
                    const raw = event.target.value;
                    if (raw === "") {
                      setForms((prev) => ({ ...prev, copyMaxSeconds: "" }));
                      return;
                    }
                    const numeric = Number.parseInt(raw, 10);
                    if (!Number.isFinite(numeric)) return;
                    const bounded = Math.min(30, Math.max(6, numeric));
                    setForms((prev) => ({ ...prev, copyMaxSeconds: String(bounded) }));
                  }}
                />
              </label>

              <label className="label-ui md:col-span-2">
                Context URL
                <input className="input-ui" value={forms.sourceUrl} onChange={(event) => setForms((prev) => ({ ...prev, sourceUrl: event.target.value }))} placeholder="https://..." />
                <span className="text-xs text-slate-400">We read the page to identify the brand/site and understand the offer or service.</span>
              </label>

              <label className="label-ui md:col-span-2">
                Content
                <textarea
                  className="input-ui min-h-24"
                  value={forms.sourceText}
                  onChange={(event) => setForms((prev) => ({ ...prev, sourceText: event.target.value }))}
                  placeholder="Optional. Add hooks, offers, claims, product details, UGC notes or any text that must be included."
                />
                <span className="text-xs text-slate-400">If you add text here, the copy generator will explicitly include it in the final output.</span>
              </label>

              <div className="rounded-xl border border-slate-700 bg-slate-950/40 px-3 py-2 text-xs text-slate-300 md:col-span-2">
                Recommended duration: 7-15s (sweet spot), 15-30s (storytelling). Ads: hook in the first 2-3 seconds and final CTA.
              </div>

              <button type="submit" className="wizard-choice-btn md:col-span-2" disabled={loading.copy}>
                {loading.copy ? "Generating..." : "Generate Copy"}
              </button>
            </form>
          </fieldset>

          <fieldset disabled={!pipeline.image.approved}>
            {loading.copy ? (
              <StepResultLoader label="Copy generation in progress..." />
            ) : (
              <div className="space-y-2">
                <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-3">
                  <p className="mb-2 text-sm font-semibold text-slate-200">Output 1: Video supporting text</p>
                  {isAdCopy ? (
                    <div className="space-y-2">
                      <label className="label-ui">
                        Primary text (max 125 characters)
                        <textarea
                          className="input-ui min-h-20"
                          maxLength={125}
                          value={forms.generatedPrimaryText}
                          onChange={(event) => setForms((prev) => ({ ...prev, generatedPrimaryText: event.target.value }))}
                        />
                      </label>
                      <label className="label-ui">
                        Headline
                        <input
                          className="input-ui"
                          value={forms.generatedHeadline}
                          onChange={(event) => setForms((prev) => ({ ...prev, generatedHeadline: event.target.value }))}
                        />
                      </label>
                      <label className="label-ui">
                        Description
                        <input
                          className="input-ui"
                          value={forms.generatedDescription}
                          onChange={(event) => setForms((prev) => ({ ...prev, generatedDescription: event.target.value }))}
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="label-ui">
                      Organic reel caption
                      <textarea
                        className="input-ui min-h-24"
                        value={forms.generatedCaptionText}
                        onChange={(event) => setForms((prev) => ({ ...prev, generatedCaptionText: event.target.value }))}
                      />
                    </label>
                  )}
                </div>

                <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-200">Video script (for TTS)</p>
                      <p className="mt-1 text-xs text-slate-400">
                        Editable. Before ElevenLabs enhancement we validate that the script duration is coherent with Max script seconds.
                      </p>
                    </div>
                    <span
                      className={cn(
                        "inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold",
                        copyScriptTiming.status === "ok"
                          ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-100"
                          : copyScriptTiming.status === "too_long"
                            ? "border-red-500/50 bg-red-500/10 text-red-100"
                            : copyScriptTiming.status === "too_short"
                              ? "border-amber-500/50 bg-amber-500/10 text-amber-100"
                              : "border-slate-600 bg-slate-900/70 text-slate-300"
                      )}
                    >
                      {copyScriptTiming.status === "ok"
                        ? "Duration OK"
                        : copyScriptTiming.status === "too_long"
                          ? "Too long"
                          : copyScriptTiming.status === "too_short"
                            ? "Too short"
                            : "Waiting for script"}
                    </span>
                  </div>

                  <label className="label-ui mt-3">
                    <textarea
                      className="input-ui min-h-52"
                      value={forms.generatedCopy}
                      onChange={(event) => handleStep3ScriptChange(event.target.value)}
                      placeholder="Script will appear here..."
                    />
                  </label>

                  <div
                    className={cn(
                      "mt-3 rounded-xl border px-3 py-2 text-xs",
                      copyScriptTiming.status === "ok"
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-100"
                        : copyScriptTiming.status === "too_long"
                          ? "border-red-500/30 bg-red-500/10 text-red-100"
                          : copyScriptTiming.status === "too_short"
                            ? "border-amber-500/30 bg-amber-500/10 text-amber-100"
                            : "border-slate-700 bg-slate-950/60 text-slate-300"
                    )}
                  >
                    <p>
                      {copyScriptTiming.characterCount} chars · {copyScriptTiming.wordCount} words · estimated duration {formatEstimatedSeconds(copyScriptTiming.estimatedSeconds)}
                    </p>
                    <p className="mt-1">
                      Target window for {copyScriptTiming.targetSeconds}s: {formatEstimatedSeconds(copyScriptTiming.minRecommendedSeconds)} to {formatEstimatedSeconds(copyScriptTiming.maxRecommendedSeconds)}
                    </p>
                  </div>
                </div>
                <div className="step3-enhance-card">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-[192px] max-w-2xl flex-1">
                      <p className="text-sm font-semibold text-slate-100">Audio Enhancement v3</p>
                      <p className="mt-1 text-xs leading-6 text-slate-300">
                        Optional. Adds ElevenLabs-style audio tags and light spoken emphasis without rewriting the script itself before you move to TTS.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="wizard-choice-btn wizard-choice-btn-enhance w-full min-[700px]:w-auto min-[700px]:min-w-[220px] min-[700px]:flex-none"
                      disabled={!forms.generatedCopy.trim() || loading.enhance}
                      onClick={onEnhanceCopy}
                    >
                      <span className="inline-flex items-center gap-2">
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                          <path d="M12 4l1.7 3.8L18 9.2l-3 2.9.8 4.4-3.8-2.1-3.8 2.1.8-4.4-3-2.9 4.3-1.4L12 4z" />
                        </svg>
                        {loading.enhance ? "Enhancing text..." : "Enhance text"}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="wizard-step-action-row">
                  <button
                    type="button"
                    className="wizard-choice-btn wizard-step-action-confirm"
                    disabled={!canApproveCopy || loading.enhance}
                    onClick={handleApproveCopyAndNext}
                  >
                    <span className="inline-flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                        <path d="M5 12h14" />
                        <path d="M13 6l6 6-6 6" />
                      </svg>
                      Confirm text and continue to TTS
                    </span>
                  </button>
                </div>
                {auth?.superadmin ? (
                  <StepCostAlert billing={copyStepBilling} />
                ) : copyStepCreditsSpent > 0 ? (
                  <p className="text-sm text-emerald-100">Credits spent for this step: {formatCreditsAmount(copyStepCreditsSpent)}.</p>
                ) : null}
              </div>
            )}
          </fieldset>
        </div>
      </MagicCard>

      <MagicCard className={cn(activeStep !== 4 && "hidden", !pipeline.copy.approved && "opacity-60")}>
        <h2 className="section-title mb-3">Step 4: Voice Generation</h2>
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <fieldset disabled={!pipeline.copy.approved}>
            <form className="grid gap-3 md:grid-cols-2" onSubmit={onGenerateTts}>
              <label className="label-ui md:col-span-2">
                Voice
                <select
                  className="input-ui"
                  value={forms.voiceId}
                  onChange={(event) => setForms((prev) => ({ ...prev, voiceId: event.target.value }))}
                >
                  <option value="">
                    {loading.voices ? "Syncing approved voices..." : "Select a voice"}
                  </option>
                  {hasSavedApprovedVoiceOutsideCurrentList && (
                    <option value={forms.voiceId}>Saved voice ({forms.voiceId})</option>
                  )}
                  {availableApprovedVoiceGroups.map((group) => (
                    <optgroup key={group.key} label={group.label}>
                      {group.voices.map((voice) => (
                        <option key={voice.voice_id} value={voice.voice_id}>
                          {voice.name || voice.voice_id}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-slate-400">
                  {availableApprovedVoices.length
                    ? `Available approved voices: ${availableApprovedVoices.length}`
                    : "No approved ElevenLabs voices are available right now. Ask the superadmin to sync ElevenLabs and enable the voices you want to use."}
                </span>
              </label>

              <div className="flex flex-wrap gap-2 md:col-span-2">
                <button
                  type="submit"
                  className="wizard-choice-btn"
                  disabled={loading.tts || !forms.voiceId || !availableApprovedVoices.length}
                >
                  <span className="inline-flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M12 4a3 3 0 013 3v5a3 3 0 11-6 0V7a3 3 0 013-3z" />
                      <path d="M7 11.5a5 5 0 0010 0" />
                      <path d="M12 16.5V20" />
                      <path d="M8.5 20h7" />
                    </svg>
                    {loading.tts ? "Generating..." : "Generate Voice"}
                  </span>
                </button>
              </div>

              <label className="label-ui md:col-span-2">
                Text for voice generation
                <textarea className="input-ui min-h-36" value={forms.ttsText} onChange={(event) => setForms((prev) => ({ ...prev, ttsText: event.target.value }))} />
              </label>
            </form>
          </fieldset>

          <div className="space-y-3">
            {loading.tts ? (
              <StepResultLoader label="Audio generation in progress..." />
            ) : pipeline.audio.url ? (
              <div className="space-y-3">
                <WaveformAudioPlayer
                  src={pipeline.audio.url}
                  title="Generated voiceover"
                  subtitle={selectedApprovedVoice?.name || forms.voiceId || "ElevenLabs"}
                />
                <div className="wizard-step-action-row">
                  <ShimmerButton type="button" className="wizard-choice-btn-compact wizard-step-action-confirm" background={null} onClick={handleApproveAudioAndNext}>
                    <span className="inline-flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                        <path d="M5 12h14" />
                        <path d="M13 6l6 6-6 6" />
                      </svg>
                      Confirm audio and continue to video
                    </span>
                  </ShimmerButton>
                  <ShimmerButton
                    type="button"
                    className="wizard-choice-btn-compact wizard-choice-btn-download wizard-step-action-download"
                    background={null}
                    onClick={() => handleDownloadAsset(pipeline.audio.url, "ugc-voiceover.mp3")}
                    aria-label="Download audio"
                    title="Download audio"
                  >
                    <span className="inline-flex items-center gap-2">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                        <path d="M12 3v11" />
                        <path d="M7.5 10.5L12 15l4.5-4.5" />
                        <path d="M4 18.5h16" />
                      </svg>
                      <span className="md:hidden">Download audio</span>
                    </span>
                  </ShimmerButton>
                </div>
                <StepCostAlert billing={ttsStepBilling} showCreditsSpent={!auth?.superadmin} />
              </div>
            ) : (
              <StepResultPlaceholder text="Generated audio will appear here." />
            )}
          </div>
        </div>
      </MagicCard>

      <MagicCard className={cn(activeStep !== 5 && "hidden", !pipeline.audio.approved && "opacity-60")}>
        <h2 className="section-title mb-3">Step 5: Video Generation</h2>
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <fieldset disabled={!canUseStep4Controls}>
            <form className="grid gap-3 md:grid-cols-2" onSubmit={onGenerateVideo}>
              <label className="label-ui md:col-span-2">
                Aspect ratio
                <div className="mt-1 flex items-center gap-3">
                  <select
                    className="input-ui flex-1"
                    value={forms.videoAspectRatio}
                    onChange={(event) => setForms((prev) => ({ ...prev, videoAspectRatio: event.target.value }))}
                  >
                    {videoAspectRatioOptions.map((ratio) => (
                      <option key={ratio} value={ratio}>
                        {ratio}
                      </option>
                    ))}
                  </select>

                  <div className="w-24 shrink-0 rounded-xl border border-slate-700 bg-slate-900/60 p-2">
                    <div className="flex h-20 items-center justify-center rounded-lg border border-slate-700/80 bg-slate-950/80">
                      <div
                        className="rounded-md border border-cyan-400/80 bg-cyan-500/15 shadow-[0_0_0_1px_rgba(34,211,238,.15)]"
                        style={{
                          width: `${videoAspectRatioPreview.widthPct}%`,
                          height: `${videoAspectRatioPreview.heightPct}%`
                        }}
                      />
                    </div>
                    <p className="mt-1 text-center text-[10px] uppercase tracking-wide text-slate-400">
                      {videoAspectRatioPreview.label}
                    </p>
                  </div>
                </div>
              </label>

              <label className="label-ui">
                Video Resolution
                <select
                  className="input-ui"
                  value={forms.videoResolution}
                  onChange={(event) => setForms((prev) => ({ ...prev, videoResolution: event.target.value }))}
                >
                  {videoResolutionOptions.map((resolution) => (
                    <option key={resolution} value={resolution}>
                      {resolution}
                    </option>
                  ))}
                </select>
              </label>

              <label className="label-ui md:col-span-2">
                Image URL
                <div className="step5-url-field-stack">
                  <Step5ImageUrlPreview src={forms.videoImageUrl} />
                  {auth?.superadmin ? (
                    <input className="input-ui" value={forms.videoImageUrl} onChange={(event) => setForms((prev) => ({ ...prev, videoImageUrl: event.target.value }))} placeholder="https://..." />
                  ) : null}
                </div>
              </label>

              <label className="label-ui md:col-span-2">
                Audio URL
                <div className="step5-url-field-stack">
                  <Step5AudioUrlPreview src={forms.videoAudioUrl} />
                  {auth?.superadmin ? (
                    <input className="input-ui" value={forms.videoAudioUrl} onChange={(event) => setForms((prev) => ({ ...prev, videoAudioUrl: event.target.value }))} placeholder="https://..." />
                  ) : null}
                </div>
              </label>

              {showAuroraPromptOptimizer ? (
                <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-3 text-sm text-slate-300 md:col-span-2">
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-slate-100">Optimize Prompt for video generation</p>
                      <p className="mt-1 text-xs leading-5 text-slate-400">
                        Optional. Uses the current Image URL, Text for voice generation, and your Step 5 note to build an Aurora-ready video prompt automatically.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="wizard-choice-btn w-full"
                      disabled={loading.videoPromptOptimize || loading.video}
                      onClick={onOptimizeVideoPrompt}
                    >
                      {loading.videoPromptOptimize ? "Optimizing..." : "Optimize Prompt for video generation"}
                    </button>
                  </div>
                </div>
              ) : null}

              <label className="label-ui md:col-span-2">
                Video prompt (optional)
                <textarea
                  className="input-ui min-h-24"
                  value={forms.videoPrompt}
                  onChange={(event) => setForms((prev) => ({ ...prev, videoPrompt: event.target.value }))}
                  placeholder="Example: Vertical UGC with product focus and social pacing."
                />
                <span className="text-xs text-slate-400">This field is sent to fal Aurora as the generation prompt.</span>
              </label>

              {auth?.superadmin ? (
                <label className="label-ui md:col-span-2">
                  Prompt guidance scale
                  <div className="mt-1 rounded-xl border border-slate-700 bg-slate-950/50 p-3">
                    <div className="flex items-center justify-between gap-3 text-sm text-slate-200">
                      <span className="inline-flex items-center gap-2">
                        <span>Prompt adherence</span>
                        <InlineInfoTooltip message="Guidance scale to be used for text prompt adherence. Default value: 1" label="About prompt adherence" />
                      </span>
                      <span className="font-semibold text-cyan-200">{normalizeFalGuidanceScaleClient(forms.videoGuidanceScale)}</span>
                    </div>
                    <input
                      className="mt-3 w-full accent-cyan-400"
                      type="range"
                      min={String(FAL_GUIDANCE_SCALE_MIN)}
                      max={String(FAL_GUIDANCE_SCALE_MAX)}
                      step={String(FAL_GUIDANCE_SCALE_STEP)}
                      value={normalizeFalGuidanceScaleClient(forms.videoGuidanceScale)}
                      onChange={(event) =>
                        setForms((prev) => ({
                          ...prev,
                          videoGuidanceScale: normalizeFalGuidanceScaleClient(event.target.value)
                        }))
                      }
                    />
                    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                      <span>{FAL_GUIDANCE_SCALE_MIN.toFixed(1)}</span>
                      <span>fal default: 1.0</span>
                      <span>{FAL_GUIDANCE_SCALE_MAX.toFixed(1)}</span>
                    </div>
                    <p className="mt-2 text-xs text-slate-400">
                      Superadmin only. Default value: 1.0. Higher values usually push the model to follow the text prompt more closely. This `0.0-5.0` range is an app-level control; fal docs document the field but do not publish strict min/max bounds.
                    </p>
                  </div>
                </label>
              ) : null}

              <button type="submit" className="wizard-choice-btn md:col-span-2" disabled={loading.video}>
                {loading.video ? "Generating..." : "Generate Video"}
              </button>
            </form>
          </fieldset>

          <div className="space-y-3">
            {loading.video ? (
              <StepResultLoader label="Video generation in progress..." />
            ) : pipeline.video.url ? (
              <div className="space-y-3">
                <div className="social-preview-panel">
                  <div className="social-preview-toggle-group">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-200">Social Preview</p>
                    <div className="social-preview-switch-shell">
                      <input
                        id="social-preview-toggle"
                        className="social-preview-checkbox"
                        type="checkbox"
                        checked={forms.socialPreviewEnabled}
                        onChange={(event) => setForms((prev) => ({ ...prev, socialPreviewEnabled: event.target.checked }))}
                      />
                      <label className="social-preview-switch" htmlFor="social-preview-toggle" aria-label={`Social preview ${forms.socialPreviewEnabled ? "on" : "off"}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="social-preview-switch-icon" aria-hidden>
                          <path d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V256c0 17.7 14.3 32 32 32s32-14.3 32-32V32zM143.5 120.6c13.6-11.3 15.4-31.5 4.1-45.1s-31.5-15.4-45.1-4.1C49.7 115.4 16 181.8 16 256c0 132.5 107.5 240 240 240s240-107.5 240-240c0-74.2-33.8-140.6-86.6-184.6c-13.6-11.3-33.8-9.4-45.1 4.1s-9.4 33.8 4.1 45.1c38.9 32.3 63.5 81 63.5 135.4c0 97.2-78.8 176-176 176s-176-78.8-176-176c0-54.4 24.7-103.1 63.5-135.4z" />
                        </svg>
                      </label>
                    </div>
                  </div>

                  <div
                    className={cn("social-preview-mode-switch", !forms.socialPreviewEnabled && "social-preview-mode-switch-disabled")}
                    aria-label="Preview format"
                  >
                    <button
                      type="button"
                      className={cn(
                        "social-preview-mode-option",
                        forms.socialPreviewMode === "instagram_reels" && "social-preview-mode-option-active"
                      )}
                      disabled={!forms.socialPreviewEnabled}
                      onClick={() => setForms((prev) => ({ ...prev, socialPreviewMode: "instagram_reels" }))}
                    >
                      Reel
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "social-preview-mode-option",
                        forms.socialPreviewMode === "instagram_stories" && "social-preview-mode-option-active"
                      )}
                      disabled={!forms.socialPreviewEnabled}
                      onClick={() => setForms((prev) => ({ ...prev, socialPreviewMode: "instagram_stories" }))}
                    >
                      Stories
                    </button>
                  </div>
                </div>
                <Iphone className="md:h-[68vh] md:w-auto md:max-h-[68vh] md:max-w-full">
                  <div className="relative h-full w-full">
                    <video
                      ref={videoPreviewRef}
                      playsInline
                      src={pipeline.video.url}
                      className={cn("h-full w-full bg-black", shouldFillPhoneFrame ? "object-cover" : "object-contain")}
                    />
                    {forms.socialPreviewEnabled && <SocialPreviewOverlay mode={forms.socialPreviewMode} />}
                  </div>
                </Iphone>
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-slate-100 transition hover:bg-slate-700"
                    onClick={handleVideoPreviewPlay}
                    aria-label="Play"
                    title="Play"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
                      <path d="M8 6.5v11l9-5.5-9-5.5z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-slate-100 transition hover:bg-slate-700"
                    onClick={handleVideoPreviewStop}
                    aria-label="Stop"
                    title="Stop"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
                      <rect x="7" y="7" width="10" height="10" rx="1.5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-slate-100 transition hover:bg-slate-700"
                    onClick={handleVideoPreviewRestart}
                    aria-label="Restart"
                    title="Restart"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M20 11a8 8 0 10-2.34 5.66" />
                      <path d="M20 5v6h-6" />
                    </svg>
                  </button>
                </div>
                <ShimmerButton
                  type="button"
                  className="wizard-choice-btn-compact wizard-choice-btn-download w-full"
                  background={null}
                  onClick={() => handleDownloadAsset(pipeline.video.url, "ugc-video.mp4")}
                  aria-label="Download video"
                  title="Download video"
                >
                  <span className="inline-flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M12 3v11" />
                      <path d="M7.5 10.5L12 15l4.5-4.5" />
                      <path d="M4 18.5h16" />
                    </svg>
                    <span>Download video</span>
                  </span>
                </ShimmerButton>
                <button
                  type="button"
                  className="wizard-choice-btn wizard-step-action-confirm"
                  onClick={() => setActiveStep(6)}
                >
                  <span className="inline-flex items-center gap-2">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                    Continue to publish
                  </span>
                </button>
                <StepCostAlert billing={videoStepBilling} />
              </div>
            ) : (
              <StepResultPlaceholder text="Generated video will appear here." />
            )}
          </div>
        </div>
      </MagicCard>

      <MagicCard className={cn(activeStep !== 6 && "hidden", !pipeline.video.url && "opacity-60")}>
        <h2 className="section-title mb-3">Step 6: Publish</h2>
        <div className="grid gap-4 md:grid-cols-2 md:items-start">
          <fieldset>
            <div className="space-y-3">
              <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-sm font-semibold text-slate-100">Connected channels</p>
                    <p className="mt-1 text-xs leading-6 text-slate-400">
                      Choose one or more destinations. Instagram publishes directly, while TikTok sends the video as a draft that the user completes inside TikTok.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
                    onClick={onOpenConnections}
                  >
                    Open connections
                  </button>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {publishTargetCards.map((target) => {
                    const isSelected = selectedPublishTargetIds.includes(target.id);
                    const isAvailable = target.statusTone === "available";
                    const requiresConnection = target.statusTone === "requires_connection";

                    return (
                      <button
                        key={target.id}
                        type="button"
                        disabled={target.disabled}
                        onClick={() => togglePublishTarget(target.id)}
                        className={cn(
                          "flex h-full min-h-[184px] flex-col rounded-[1.7rem] border p-4 text-left transition",
                          isSelected
                            ? "border-cyan-300/80 bg-[linear-gradient(180deg,rgba(15,23,42,.96),rgba(8,13,27,.98))] shadow-[0_0_0_1px_rgba(34,211,238,.18),0_24px_54px_rgba(8,20,43,.28)]"
                            : "border-slate-700 bg-slate-900/70",
                          !target.disabled && "hover:border-slate-500 hover:bg-slate-800/85",
                          target.disabled && "cursor-not-allowed opacity-70"
                        )}
                      >
                        <div className="flex items-start gap-3.5">
                          <PublishTargetLogo network={target.network} />
                          <div className="min-w-0">
                            <p className="max-w-[10.5rem] text-[12px] font-semibold leading-[17px] tracking-[-0.01em] text-slate-100">
                              {target.title}
                            </p>
                            <p className="text-[10px] text-slate-500 tracking-[0.22em] uppercase">
                              {target.platform === "Meta" ? "Via Meta" : "TikTok"}
                            </p>
                          </div>
                        </div>
                        <p className="mt-4 text-[12px] text-slate-400">{target.description}</p>
                        <div className="mt-auto pt-4">
                          <p className="text-[14px] font-semibold leading-6 text-slate-100">{target.helperText}</p>
                          <span
                            className={cn(
                              "mt-2.5 inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em]",
                              isAvailable && "border border-emerald-400/35 bg-emerald-500/15 text-emerald-200",
                              requiresConnection && "border border-amber-400/35 bg-amber-500/15 text-amber-200",
                              target.statusTone === "coming_soon" && "border border-slate-600 bg-slate-800/80 text-slate-300"
                            )}
                          >
                            {isAvailable ? "Ready" : requiresConnection ? "Connect" : "Soon"}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-4">
                <p className="text-sm font-semibold text-slate-100">Caption & hashtags</p>
                <p className="mt-1 text-xs leading-6 text-slate-400">
                  Use one shared caption setup. When TikTok is selected, the privacy and interaction controls below are applied to that destination.
                </p>

                <div className="mt-3 space-y-3">
                  <label className="label-ui">
                    Caption
                    <textarea
                      className="input-ui min-h-24"
                      value={forms.publishCaption}
                      onChange={(event) => setForms((prev) => ({ ...prev, publishCaption: event.target.value }))}
                      placeholder="Write the caption that will accompany the published post."
                    />
                  </label>

                  <label className="label-ui">
                    Hashtags (optional)
                    <input
                      className="input-ui"
                      value={forms.publishHashtags}
                      onChange={(event) => setForms((prev) => ({ ...prev, publishHashtags: event.target.value }))}
                      placeholder="#ugc #reels #brand"
                    />
                  </label>
                </div>
              </div>

              {selectedPublishTargetIds.includes("tiktok") ? (
                <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-4">
                  <p className="text-sm font-semibold text-slate-100">TikTok draft upload</p>
                  <p className="mt-1 text-xs leading-6 text-slate-400">
                    We upload the video to the connected TikTok account as a draft. TikTok sends an inbox notification and the final post is completed inside the TikTok app.
                  </p>

                  <div className="mt-3 space-y-3 rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-200">Connected account</p>
                      <p className="mt-2 text-sm font-semibold text-slate-100">
                        {defaultTiktokAccount?.name || defaultTiktokAccount?.username || "TikTok account"}
                      </p>
                      <p className="mt-1 text-xs leading-6 text-slate-500">
                        {defaultTiktokAccount
                          ? "The draft is uploaded to this account. No privacy controls are needed here because posting happens inside TikTok."
                          : "Connect TikTok and choose a default account in Connections before uploading drafts."}
                      </p>
                    </div>

                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-200">What happens next</p>
                      <p className="mt-2 text-sm leading-6 text-slate-300">
                        Open the TikTok inbox notification, review the draft, and post it manually from TikTok.
                      </p>
                      <p className="mt-2 text-xs leading-6 text-slate-500">
                        TikTok does not expose a direct permalink for drafts, so this screen will only show a link if the post later becomes publicly available.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {!pipeline.video.url ? (
                <p className="rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                  Generate the final video in Step 5 before sending it to Instagram or TikTok.
                </p>
              ) : null}

              <button
                type="button"
                className="wizard-choice-btn w-full"
                disabled={loading.publishInstagram || loading.publishTiktok || !selectedPublishTargetIds.length}
                onClick={handlePublishSelectedTargets}
              >
                {loading.publishInstagram || loading.publishTiktok ? "Sending selected destinations..." : "Send selected destinations"}
              </button>
            </div>
          </fieldset>

          <div className="space-y-3">
            <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-4">
              <p className="text-sm font-semibold text-slate-100">Final delivery preview</p>
              <p className="mt-1 text-xs leading-6 text-slate-400">
                Your backend will use the saved OAuth tokens from Connections and upload or publish asynchronously while the app polls the status.
              </p>

              <div className="mt-4 space-y-3">
                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-200">Selected destinations</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedPublishTargets.length ? (
                      selectedPublishTargets.map((target) => (
                        <span
                          key={`selected-target-${target.id}`}
                          className="inline-flex rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-1 text-[11px] font-semibold text-cyan-100"
                        >
                          {target.label}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400">No destination selected yet.</span>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-200">Caption preview</p>
                  <pre className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-slate-200">
                    {finalPublishCaption || "Your final caption preview will appear here."}
                  </pre>
                </div>

                <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-200">Delivery status</p>
                  <p className="mt-1 text-xs leading-6 text-slate-500">
                    Instagram still publishes directly. TikTok now uploads a draft that the user finishes inside TikTok.
                  </p>
                  <div className="mt-2 space-y-2">
                    <div className="rounded-lg border border-slate-700 bg-slate-950/70 px-3 py-2 text-sm text-slate-300">
                      Instagram Reel:{" "}
                      <span className="font-semibold text-slate-100">
                        {loading.publishInstagram
                          ? "Publishing..."
                          : hasPublishedInstagramReel
                            ? "Published"
                            : canPublishInstagramReel
                              ? "Ready"
                              : "Not ready"}
                      </span>
                    </div>
                    <div className="rounded-lg border border-slate-700 bg-slate-950/70 px-3 py-2 text-sm text-slate-300">
                      TikTok:{" "}
                      <span className="font-semibold text-slate-100">
                        {loading.publishTiktok
                          ? "Uploading..."
                          : hasUploadedTiktok
                            ? "Draft uploaded"
                            : canUploadTiktok
                              ? "Ready"
                              : "Not ready"}
                      </span>
                    </div>
                    {publishResults?.instagramReel?.permalink ? (
                      <a
                        href={publishResults.instagramReel.permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 transition hover:text-cyan-100"
                      >
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                          <path d="M14 3h7v7" />
                          <path d="M10 14 21 3" />
                          <path d="M21 14v4a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h4" />
                        </svg>
                        View published reel
                      </a>
                    ) : null}
                    {publishResults?.tiktok?.permalink ? (
                      <a
                        href={publishResults.tiktok.permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-200 transition hover:text-cyan-100"
                      >
                        <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                          <path d="M14 3h7v7" />
                          <path d="M10 14 21 3" />
                          <path d="M21 14v4a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h4" />
                        </svg>
                        View TikTok post
                      </a>
                    ) : publishResults?.tiktok?.draftUploaded ? (
                      <p className="text-sm text-slate-300">
                        TikTok draft uploaded. Open TikTok to finish posting it manually.
                      </p>
                    ) : null}
                    {publishResults?.tiktok?.publishId ? (
                      <p className="text-xs text-slate-500">
                        Draft ID: <span className="font-mono">{publishResults.tiktok.publishId}</span>
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MagicCard>
      </div>
      </div>
      )}
    </div>
  );
}

function SettingsPage({
  settings,
  activeModels,
  forms,
  auth
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <MagicCard>
        <h2 className="section-title">Managed Configuration</h2>
        <p className="mt-1 text-sm text-slate-400">Provider keys and catalogs are managed exclusively by the superadmin backend. The browser only uses the authenticated Rails proxy.</p>
        <div className="mt-4 grid gap-3">
          {Object.entries(providerConfig).map(([provider, cfg]) => (
            <div key={provider} className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-slate-100">{cfg.label}</span>
                <span className={cn("text-xs font-semibold", settings.providerStatus?.[provider] ? "text-emerald-300" : "text-amber-300")}>
                  {settings.providerStatus?.[provider] ? "Configured" : "Missing"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </MagicCard>

      <MagicCard>
        <h2 className="section-title">Current Defaults</h2>
        <p className="mt-1 text-sm text-slate-400">These are the server-side defaults applied to the authenticated app.</p>
        <div className="mt-3 grid gap-3">
          <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
            Step 1 provider: <span className="font-semibold text-slate-100">{forms.imageProvider}</span>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
            Step 1 model: <span className="font-semibold text-slate-100">{forms.imageModel}</span>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
            Step 2 model: <span className="font-semibold text-slate-100">{forms.imageEditModel}</span>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
            Step 3 model: <span className="font-semibold text-slate-100">{forms.copyModel || "Not set"}</span>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
            Step 4 model: <span className="font-semibold text-slate-100">{forms.ttsModel || "Not set"}</span>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-slate-300">
            Step 5 model: <span className="font-semibold text-slate-100">{forms.videoModel}</span>
          </div>
        </div>
      </MagicCard>

      <MagicCard>
        <h2 className="section-title">Available Models</h2>
        <p className="mt-1 text-sm text-slate-400">Catalog exposed by the superadmin backend to the authenticated frontend.</p>
        <div className="mt-3 space-y-2">
          {Object.entries(providerConfig).map(([provider, cfg]) => (
            <div key={provider} className="rounded-xl border border-slate-700 bg-slate-900/70 p-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs font-semibold">{cfg.label}</span>
              </div>

              <div className="max-h-52 space-y-2 overflow-auto pr-1">
                {(settings.providerModels[provider] || []).length === 0 && <p className="text-xs text-slate-400">No models available.</p>}
                {(settings.providerModels[provider] || []).map((model) => (
                  <div
                    key={model}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-700 bg-slate-950/70 px-2 py-1 text-[11px]"
                  >
                    <span className="break-all text-slate-300">{model}</span>
                    <span className={cn("font-semibold", settings.enabledModels[provider]?.[model] !== false ? "text-emerald-300" : "text-slate-500")}>
                      {settings.enabledModels[provider]?.[model] !== false ? "enabled" : "disabled"}
                    </span>
                  </div>
                ))}
              </div>

              <p className="mt-2 text-[11px] text-slate-500">
                Active:{" "}
                {provider === "fal"
                  ? `${(activeModels.falImage || []).length} text2img / ${(activeModels.falEdit || []).length} edit / ${(activeModels.falVideo || []).length} video`
                  : (activeModels[provider] || []).length}
              </p>
            </div>
          ))}
        </div>
      </MagicCard>

      {auth?.superadmin ? (
        <MagicCard className="lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="section-title">Superadmin Access</h2>
              <p className="mt-1 text-sm text-slate-400">Manage provider keys, global defaults, and catalogs from the restricted admin panel.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => router.visit("/admin/discount_codes")}
                className="inline-flex items-center justify-center rounded-xl bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] px-4 py-2 text-sm font-semibold text-slate-50 shadow-[0_14px_32px_rgba(2,6,23,.32)] transition hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))]"
              >
                Manage discount codes
              </button>
              <button
                type="button"
                onClick={() => router.visit("/admin/settings")}
                className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:border-slate-500 hover:bg-slate-800"
              >
                Open superadmin dashboard
              </button>
            </div>
          </div>
        </MagicCard>
      ) : null}
    </div>
  );
}

function AccountPage({
  account,
  setAccount,
  accountLoading,
  passwordForm,
  setPasswordForm,
  onSaveProfile,
  onUpdatePassword,
  navigate
}) {
  const primaryActionButtonClass =
    "inline-flex items-center justify-center rounded-xl bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] px-4 py-2 text-sm font-semibold text-slate-50 shadow-[0_14px_32px_rgba(2,6,23,.32)] transition hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))] hover:shadow-[0_18px_40px_rgba(8,20,43,.45)] disabled:cursor-not-allowed disabled:bg-slate-900/85 disabled:text-slate-500";

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <MagicCard>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="section-title">Profile</h2>
            <p className="mt-1 text-sm text-slate-400">Update the identity shown on your UGC workspace and billing account.</p>
          </div>
          <span className="rounded-full border border-cyan-500/35 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-100">
            {account.accountRole}
          </span>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="label-ui md:col-span-2">
            Full name
            <input className="input-ui" value={account.fullName} onChange={(event) => setAccount((prev) => ({ ...prev, fullName: event.target.value }))} />
          </label>
          <label className="label-ui">
            Company
            <input className="input-ui" value={account.companyName} onChange={(event) => setAccount((prev) => ({ ...prev, companyName: event.target.value }))} />
          </label>
          <label className="label-ui">
            Job title
            <input className="input-ui" value={account.jobTitle} onChange={(event) => setAccount((prev) => ({ ...prev, jobTitle: event.target.value }))} />
          </label>
          <label className="label-ui">
            Timezone
            <input className="input-ui" value={account.timezone} onChange={(event) => setAccount((prev) => ({ ...prev, timezone: event.target.value }))} />
          </label>
          <label className="label-ui">
            Locale
            <select className="input-ui" value={account.locale} onChange={(event) => setAccount((prev) => ({ ...prev, locale: event.target.value }))}>
              <option value="en">English</option>
              <option value="it">Italian</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </select>
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className={primaryActionButtonClass} onClick={onSaveProfile} disabled={accountLoading.profile}>
            {accountLoading.profile ? "Saving profile..." : "Save profile"}
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
            onClick={() => navigate("/generation")}
          >
            Back to generation
          </button>
        </div>
      </MagicCard>

      <MagicCard>
        <h2 className="section-title">Login & Security</h2>
        <p className="mt-1 text-sm text-slate-400">Manage how you sign in and keep your workspace secure.</p>

        <div className="mt-4 grid gap-3">
          <label className="label-ui">
            Email
            <input className="input-ui" type="email" value={account.email} readOnly />
            <span className="mt-1 block text-xs text-slate-500">Email changes are kept restricted while Stripe account changes are still being integrated.</span>
          </label>
          <label className="label-ui">
            Current password
            <input
              className="input-ui"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))}
            />
          </label>
          <label className="label-ui">
            New password
            <input
              className="input-ui"
              type="password"
              value={passwordForm.password}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, password: event.target.value }))}
            />
          </label>
          <label className="label-ui">
            Confirm new password
            <input
              className="input-ui"
              type="password"
              value={passwordForm.passwordConfirmation}
              onChange={(event) => setPasswordForm((prev) => ({ ...prev, passwordConfirmation: event.target.value }))}
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className={primaryActionButtonClass} onClick={onUpdatePassword} disabled={accountLoading.password}>
            {accountLoading.password ? "Updating password..." : "Update password"}
          </button>
          <button
            type="button"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
            onClick={() => router.delete("/users/sign_out")}
          >
            Sign out
          </button>
        </div>
      </MagicCard>
    </div>
  );
}

function ConnectionsPage({
  socialConnections,
  socialFeatures,
  socialLoading,
  onConnectMeta,
  onRefreshMeta,
  onConnectTiktok,
  onRefreshTiktok,
  onDisconnectConnection,
  onSelectDefaultAccount
}) {
  const metaConnection = getMetaConnection(socialConnections);
  const tiktokConnection = getTiktokConnection(socialConnections);
  const instagramAccounts = Array.isArray(metaConnection?.accounts)
    ? metaConnection.accounts.filter((account) => account.platform === "instagram")
    : [];
  const tiktokAccounts = Array.isArray(tiktokConnection?.accounts)
    ? tiktokConnection.accounts.filter((account) => account.platform === "tiktok")
    : [];
  const shellCardClass = "rounded-[1.65rem] border-slate-700/80 bg-slate-900/70 p-3.5 shadow-[0_14px_36px_rgba(2,6,23,.34)] md:p-4";
  const secondaryActionClass =
    "inline-flex items-center justify-center rounded-xl border border-sky-300/45 bg-sky-400/12 px-4 py-2 text-sm font-semibold text-sky-100 shadow-[0_0_0_1px_rgba(125,211,252,0.12),0_12px_28px_rgba(14,165,233,0.08)] transition hover:border-sky-200/65 hover:bg-sky-400/18 hover:text-white disabled:cursor-not-allowed disabled:border-slate-700/80 disabled:bg-slate-900/80 disabled:text-slate-500 disabled:shadow-none";
  const primaryActionClass =
    "inline-flex items-center justify-center rounded-xl border border-sky-200/65 bg-[linear-gradient(135deg,rgba(56,189,248,.96),rgba(14,165,233,.94))] px-4 py-2 text-sm font-semibold text-white shadow-[0_16px_36px_rgba(14,165,233,.22),inset_0_1px_0_rgba(255,255,255,.2)] transition hover:border-sky-100/80 hover:bg-[linear-gradient(135deg,rgba(125,211,252,.98),rgba(56,189,248,.96))] hover:text-white hover:shadow-[0_20px_44px_rgba(14,165,233,.32),inset_0_1px_0_rgba(255,255,255,.24)] disabled:cursor-not-allowed disabled:border-slate-700/80 disabled:bg-slate-800/85 disabled:text-slate-400 disabled:shadow-none";
  const providerGroupClass =
    "rounded-[1.65rem] border p-4 shadow-[0_18px_44px_rgba(2,6,23,.38)] md:p-[18px]";
  const providerStatusCardClass = "rounded-[1.35rem] border border-slate-700/80 bg-slate-950/65 p-4";
  const providerDestinationCardClass = "rounded-[1.35rem] border border-slate-700/80 bg-slate-950/65 p-4";

  return (
    <div className="space-y-3">
      <MagicCard className={cn("xl:col-span-2", shellCardClass)}>
        <h2 className="text-[1.35rem] font-semibold tracking-tight text-slate-100 md:text-[1.45rem]">Social Connections</h2>
        <p className="mt-1 text-[13px] text-slate-400">
          Authorize UGC Persona to publish generated videos on your connected social accounts.
        </p>
      </MagicCard>

      <MagicCard className={cn(providerGroupClass, "border-cyan-400/20 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.12),transparent_34%),linear-gradient(180deg,rgba(15,23,42,0.88),rgba(2,6,23,0.96))]")}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <SocialProviderLogo provider="meta" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-cyan-300">Meta Group</p>
              <h3 className="mt-2 text-[1.25rem] font-semibold text-slate-100">Meta Connection</h3>
              <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-slate-400">
                Connect the Meta identity once, then choose which Account (instagram or facebook page) should be the default destination for Step 6.
              </p>
            </div>
          </div>
          <span
            className={cn(
              "inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]",
              socialFeatures?.meta_configured
                ? "border border-emerald-400/35 bg-emerald-500/15 text-emerald-200"
                : "border border-amber-400/35 bg-amber-500/15 text-amber-200"
            )}
          >
            {socialFeatures?.meta_configured ? "Available" : "Not configured"}
          </span>
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
          <div className={providerStatusCardClass}>
            <div className="flex items-center gap-2">
              <SocialNetworkBadge network="meta" />
              <p className="text-sm font-semibold text-slate-100">Meta connection</p>
            </div>

            {!socialFeatures?.meta_configured ? (
              <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                Meta app credentials are not configured yet by the superadmin.
              </div>
            ) : !metaConnection ? (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-300">
                  No Meta account connected yet. Use the secure Meta OAuth flow to authorize publishing access for your Instagram professional account.
                </div>
                <button type="button" className={primaryActionClass} onClick={onConnectMeta} disabled={socialLoading.connectMeta}>
                  {socialLoading.connectMeta ? "Opening Meta..." : "Connect Meta"}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-slate-700 bg-slate-950/60 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-100">Connection status</p>
                      <p className="mt-1 text-sm text-slate-300 capitalize">{String(metaConnection.status || "connected").replaceAll("_", " ")}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Last synced {metaConnection.last_synced_at ? formatAccountDate(metaConnection.last_synced_at) : "Not available"}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={secondaryActionClass} onClick={onRefreshMeta} disabled={socialLoading.refreshMeta}>
                        {socialLoading.refreshMeta ? "Refreshing..." : "Refresh accounts"}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-xl border border-rose-500/35 bg-rose-500/10 px-4 py-2 text-sm font-semibold text-rose-100 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => onDisconnectConnection(metaConnection.id)}
                        disabled={socialLoading.disconnect}
                      >
                        {socialLoading.disconnect ? "Disconnecting..." : "Disconnect"}
                      </button>
                    </div>
                  </div>
                  {metaConnection.error_message ? (
                    <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                      {metaConnection.error_message}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <div className={providerDestinationCardClass}>
            <div className="flex items-center gap-2">
              <SocialNetworkBadge network="instagram" />
              <div>
                <h3 className="text-[1.05rem] font-semibold tracking-tight text-slate-100">Instagram destinations</h3>
                <p className="mt-1 text-[13px] text-slate-400">
                  Select the default Instagram destination to use when you publish from Step 6.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {instagramAccounts.length ? (
                instagramAccounts.map((account) => (
                  <SocialDestinationCard
                    key={account.id}
                    network="instagram"
                    account={account}
                    socialLoading={socialLoading}
                    primaryActionClass={primaryActionClass}
                    secondaryActionClass={secondaryActionClass}
                    onSelectDefaultAccount={onSelectDefaultAccount}
                  />
                ))
              ) : (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-6">
                  <p className="text-sm font-medium text-amber-100">
                    {metaConnection?.error_message || "No Instagram professional accounts are available yet on this Meta connection."}
                  </p>
                  <p className="mt-2 text-xs leading-6 text-amber-200/80">
                    Make sure the same Facebook user manages a Facebook Page linked to an Instagram professional account, then click{" "}
                    <span className="font-semibold text-amber-100">Refresh accounts</span>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </MagicCard>

      <MagicCard className={cn(providerGroupClass, "border-fuchsia-400/20 bg-[radial-gradient(circle_at_top_left,rgba(236,72,153,0.12),transparent_34%),linear-gradient(180deg,rgba(15,23,42,0.88),rgba(2,6,23,0.96))]")}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <SocialProviderLogo provider="tiktok" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-fuchsia-300">TikTok Group</p>
              <h3 className="mt-2 text-[1.25rem] font-semibold text-slate-100">TikTok direct publishing</h3>
              <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-slate-400">
                Connect your TikTok profile once and keep one default destination ready for Step 6 publishing with privacy and interaction controls.
              </p>
            </div>
          </div>
          <span
            className={cn(
              "inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]",
              socialFeatures?.tiktok_configured
                ? "border border-emerald-400/35 bg-emerald-500/15 text-emerald-200"
                : "border border-amber-400/35 bg-amber-500/15 text-amber-200"
            )}
          >
            {socialFeatures?.tiktok_configured ? "Available" : "Not configured"}
          </span>
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]">
          <div className={providerStatusCardClass}>
            <div className="flex items-center gap-2">
              <SocialNetworkBadge network="tiktok" />
              <p className="text-sm font-semibold text-slate-100">TikTok connection</p>
            </div>

            {!socialFeatures?.tiktok_configured ? (
              <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                TikTok app credentials are not configured yet by the superadmin.
              </div>
            ) : !tiktokConnection ? (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-300">
                  No TikTok account connected yet. Use the secure TikTok OAuth flow to authorize direct posting access.
                </div>
                <button type="button" className={primaryActionClass} onClick={onConnectTiktok} disabled={socialLoading.connectTiktok}>
                  {socialLoading.connectTiktok ? "Opening TikTok..." : "Connect TikTok"}
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="rounded-xl border border-slate-700 bg-slate-950/60 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-100">Connection status</p>
                      <p className="mt-1 text-sm text-slate-300 capitalize">{String(tiktokConnection.status || "connected").replaceAll("_", " ")}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Last synced {tiktokConnection.last_synced_at ? formatAccountDate(tiktokConnection.last_synced_at) : "Not available"}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={secondaryActionClass} onClick={onRefreshTiktok} disabled={socialLoading.refreshTiktok}>
                        {socialLoading.refreshTiktok ? "Refreshing..." : "Refresh account"}
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-xl border border-rose-500/35 bg-rose-500/10 px-4 py-2 text-sm font-semibold text-rose-100 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => onDisconnectConnection(tiktokConnection.id)}
                        disabled={socialLoading.disconnect}
                      >
                        {socialLoading.disconnect ? "Disconnecting..." : "Disconnect"}
                      </button>
                    </div>
                  </div>
                  {tiktokConnection.error_message ? (
                    <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                      {tiktokConnection.error_message}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <div className={providerDestinationCardClass}>
            <div className="flex items-center gap-2">
              <SocialNetworkBadge network="tiktok" />
              <div>
                <h3 className="text-[1.05rem] font-semibold tracking-tight text-slate-100">TikTok destinations</h3>
                <p className="mt-1 text-[13px] text-slate-400">
                  Select the default TikTok destination to use when you publish from Step 6.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {tiktokAccounts.length ? (
                tiktokAccounts.map((account) => (
                  <SocialDestinationCard
                    key={account.id}
                    network="tiktok"
                    account={account}
                    socialLoading={socialLoading}
                    primaryActionClass={primaryActionClass}
                    secondaryActionClass={secondaryActionClass}
                    onSelectDefaultAccount={onSelectDefaultAccount}
                  />
                ))
              ) : (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-6">
                  <p className="text-sm font-medium text-amber-100">
                    {tiktokConnection?.error_message || "No TikTok account is available yet on this connection."}
                  </p>
                  <p className="mt-2 text-xs leading-6 text-amber-200/80">
                    Make sure the connected TikTok user completed OAuth and granted posting access, then click{" "}
                    <span className="font-semibold text-amber-100">Refresh account</span>.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </MagicCard>
    </div>
  );
}

function SocialDestinationCard({
  network,
  account,
  socialLoading,
  primaryActionClass,
  secondaryActionClass,
  onSelectDefaultAccount
}) {
  const label = account.name || account.username || `${network === "instagram" ? "Instagram" : "TikTok"} account`;

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-950/60 p-4">
      <div className="flex items-start gap-3">
        {account.profile_picture_url ? (
          <img
            src={account.profile_picture_url}
            alt={label}
            className="h-12 w-12 rounded-full border border-slate-700 object-cover"
          />
        ) : (
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-900 text-sm font-semibold text-slate-200">
            {network === "instagram" ? "IG" : "TT"}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <SocialNetworkBadge network={network} compact />
            {account.default_account ? (
              <span className="inline-flex rounded-full border border-cyan-400/35 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-200">
                Default
              </span>
            ) : null}
          </div>
          <p className="mt-2 truncate text-sm font-semibold text-slate-100">{label}</p>
          {account.username ? <p className="mt-1 text-sm text-slate-300">@{account.username}</p> : null}
          <p className="mt-1 text-xs text-slate-500">
            {network === "instagram" ? "Instagram" : "TikTok"} ID: {account.external_account_id}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          className={cn(account.default_account ? secondaryActionClass : primaryActionClass)}
          onClick={() => onSelectDefaultAccount(account.id)}
          disabled={account.default_account || socialLoading.select}
        >
          {account.default_account ? "Selected destination" : socialLoading.select ? "Saving..." : "Use as default"}
        </button>
      </div>
    </div>
  );
}

function SocialProviderLogo({ provider }) {
  const isMeta = provider === "meta";
  const isTiktok = provider === "tiktok";

  return (
    <div
      className={cn(
        "inline-flex items-center justify-center rounded-2xl border shadow-[0_12px_28px_rgba(2,6,23,.28)]",
        isTiktok ? "h-12 min-w-[88px] px-3" : "h-12 w-12",
        isMeta ? "border-cyan-400/35 bg-cyan-500/12 text-cyan-100" : "border-fuchsia-400/35 bg-fuchsia-500/12 text-fuchsia-100"
      )}
    >
      {isMeta ? <MetaIcon className="h-7 w-7" /> : <TikTokIcon className="h-4 w-[56px]" />}
    </div>
  );
}

function SocialNetworkBadge({ network, compact = false }) {
  const classes = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";

  if (network === "instagram") {
    return (
      <span className={cn("inline-flex items-center gap-1.5 rounded-full border border-pink-400/25 bg-pink-500/10 font-semibold uppercase tracking-[0.14em] text-pink-100", classes)}>
        <InstagramIcon className="h-3.5 w-3.5" />
        {compact ? "IG" : "Instagram"}
      </span>
    );
  }

  if (network === "meta") {
    return (
      <span className={cn("inline-flex items-center gap-1.5 rounded-full border border-cyan-400/25 bg-cyan-500/10 font-semibold uppercase tracking-[0.14em] text-cyan-100", classes)}>
        <MetaIcon className="h-3.5 w-3.5" />
        Meta
      </span>
    );
  }

  if (network === "facebook") {
    return (
      <span className={cn("inline-flex items-center gap-1.5 rounded-full border border-blue-400/25 bg-blue-500/10 font-semibold uppercase tracking-[0.14em] text-blue-100", classes)}>
        <FacebookIcon className="h-3.5 w-3.5" />
        {compact ? "FB" : "Facebook"}
      </span>
    );
  }

  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border border-fuchsia-400/25 bg-fuchsia-500/10 font-semibold uppercase tracking-[0.14em] text-fuchsia-100", classes)}>
      <TikTokIcon className={compact ? "h-3.5 w-[22px]" : "h-4 w-[28px]"} />
      TikTok
    </span>
  );
}

function PublishTargetLogo({ network }) {
  if (network === "instagram") {
    return (
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-[1.45rem] border border-pink-400/25 bg-[radial-gradient(circle_at_50%_20%,rgba(244,114,182,.16),rgba(76,29,149,.08)_72%)] shadow-[0_18px_34px_rgba(190,24,93,.16)]">
        <InstagramIcon className="h-5 w-5" />
      </div>
    );
  }

  if (network === "facebook") {
    return (
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-[1.45rem] border border-blue-400/25 bg-[radial-gradient(circle_at_50%_20%,rgba(59,130,246,.18),rgba(15,23,42,.08)_72%)] shadow-[0_18px_34px_rgba(37,99,235,.16)]">
        <FacebookIcon className="h-5 w-5" />
      </div>
    );
  }

  return (
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-[1.45rem] border border-fuchsia-400/25 bg-[radial-gradient(circle_at_50%_20%,rgba(217,70,239,.14),rgba(15,23,42,.08)_72%)] shadow-[0_18px_34px_rgba(217,70,239,.14)]">
      <TikTokMarkIcon className="h-5 w-5" />
    </div>
  );
}

function MetaIcon({ className }) {
  return (
    <svg viewBox="0 0 290 191" className={className} fill="none" aria-hidden>
      <defs>
        <linearGradient id="meta-logo-grad-1" x1="61" y1="117" x2="259" y2="127" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0064e1" />
          <stop offset="0.4" stopColor="#0064e1" />
          <stop offset="0.83" stopColor="#0073ee" />
          <stop offset="1" stopColor="#0082fb" />
        </linearGradient>
        <linearGradient id="meta-logo-grad-2" x1="45" y1="139" x2="45" y2="66" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0082fb" />
          <stop offset="1" stopColor="#0064e0" />
        </linearGradient>
      </defs>
      <path
        d="m31.06 125.96c0 10.98 2.41 19.41 5.56 24.51 4.13 6.68 10.29 9.51 16.57 9.51 8.1 0 15.51-2.01 29.79-21.76 11.44-15.83 24.92-38.05 33.99-51.98l15.36-23.6c10.67-16.39 23.02-34.61 37.18-46.96C181.07 5.6 193.54 0 206.09 0c21.07 0 41.14 12.21 56.5 35.11 16.81 25.08 24.97 56.67 24.97 89.27 0 19.38-3.82 33.62-10.32 44.87-6.28 10.88-18.52 21.75-39.11 21.75v-31.02c17.63 0 22.03-16.2 22.03-34.74 0-26.42-6.16-55.74-19.73-76.69-9.63-14.86-22.11-23.94-35.84-23.94-14.85 0-26.8 11.2-40.23 31.17-7.14 10.61-14.47 23.54-22.7 38.13l-9.06 16.05c-18.2 32.27-22.81 39.62-31.91 51.75-15.95 21.24-29.57 29.29-47.5 29.29-21.27 0-34.72-9.21-43.05-23.09C3.34 156.6 0 141.76 0 124.85Z"
        fill="#0081fb"
      />
      <path
        d="m24.49 37.3C38.73 15.35 59.28 0 82.85 0c13.65 0 27.22 4.04 41.39 15.61 15.5 12.65 32.02 33.48 52.63 67.81l7.39 12.32c17.84 29.72 27.99 45.01 33.93 52.22 7.64 9.26 12.99 12.02 19.94 12.02 17.63 0 22.03-16.2 22.03-34.74l27.4-.86c0 19.38-3.82 33.62-10.32 44.87-6.28 10.88-18.52 21.75-39.11 21.75-12.8 0-24.14-2.78-36.68-14.61-9.64-9.08-20.91-25.21-29.58-39.71l-25.79-43.08c-12.94-21.62-24.81-37.74-31.68-45.04-7.39-7.85-16.89-17.33-32.05-17.33-12.27 0-22.69 8.61-31.41 21.78Z"
        fill="url(#meta-logo-grad-1)"
      />
      <path
        d="M82.35 31.23c-12.27 0-22.69 8.61-31.41 21.78-12.33 18.61-19.88 46.33-19.88 72.95 0 10.98 2.41 19.41 5.56 24.51l-26.48 17.44C3.34 156.6 0 141.76 0 124.85 0 94.1 8.44 62.05 24.49 37.3 38.73 15.35 59.28 0 82.85 0Z"
        fill="url(#meta-logo-grad-2)"
      />
    </svg>
  );
}

function InstagramIcon({ className }) {
  const gradientIdA = useId().replace(/:/g, "");
  const gradientIdB = `${gradientIdA}-b`;
  const gradientIdC = `${gradientIdA}-c`;
  const gradientIdD = `${gradientIdA}-d`;

  return (
    <svg viewBox="0 0 132.004 132" className={className} aria-hidden>
      <defs>
        <linearGradient id={gradientIdB}>
          <stop offset="0" stopColor="#3771c8" />
          <stop offset=".128" stopColor="#3771c8" />
          <stop offset="1" stopColor="#60f" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={gradientIdA}>
          <stop offset="0" stopColor="#fd5" />
          <stop offset=".1" stopColor="#fd5" />
          <stop offset=".5" stopColor="#ff543e" />
          <stop offset="1" stopColor="#c837ab" />
        </linearGradient>
        <radialGradient
          id={gradientIdC}
          cx="158.429"
          cy="578.088"
          r="65"
          href={`#${gradientIdA}`}
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(0 -1.98198 1.8439 0 -1031.402 454.004)"
          fx="158.429"
          fy="578.088"
        />
        <radialGradient
          id={gradientIdD}
          cx="147.694"
          cy="473.455"
          r="65"
          href={`#${gradientIdB}`}
          gradientUnits="userSpaceOnUse"
          gradientTransform="matrix(.17394 .86872 -3.5818 .71718 1648.348 -458.493)"
          fx="147.694"
          fy="473.455"
        />
      </defs>
      <path
        fill={`url(#${gradientIdC})`}
        d="M65.03 0C37.888 0 29.95.028 28.407.156c-5.57.463-9.036 1.34-12.812 3.22-2.91 1.445-5.205 3.12-7.47 5.468C4 13.126 1.5 18.394.595 24.656c-.44 3.04-.568 3.66-.594 19.188-.01 5.176 0 11.988 0 21.125 0 27.12.03 35.05.16 36.59.45 5.42 1.3 8.83 3.1 12.56 3.44 7.14 10.01 12.5 17.75 14.5 2.68.69 5.64 1.07 9.44 1.25 1.61.07 18.02.12 34.44.12 16.42 0 32.84-.02 34.41-.1 4.4-.207 6.955-.55 9.78-1.28 7.79-2.01 14.24-7.29 17.75-14.53 1.765-3.64 2.66-7.18 3.065-12.317.088-1.12.125-18.977.125-36.81 0-17.836-.04-35.66-.128-36.78-.41-5.22-1.305-8.73-3.127-12.44-1.495-3.037-3.155-5.305-5.565-7.624C116.9 4 111.64 1.5 105.372.596 102.335.157 101.73.027 86.19 0H65.03z"
        transform="translate(1.004 1)"
      />
      <path
        fill={`url(#${gradientIdD})`}
        d="M65.03 0C37.888 0 29.95.028 28.407.156c-5.57.463-9.036 1.34-12.812 3.22-2.91 1.445-5.205 3.12-7.47 5.468C4 13.126 1.5 18.394.595 24.656c-.44 3.04-.568 3.66-.594 19.188-.01 5.176 0 11.988 0 21.125 0 27.12.03 35.05.16 36.59.45 5.42 1.3 8.83 3.1 12.56 3.44 7.14 10.01 12.5 17.75 14.5 2.68.69 5.64 1.07 9.44 1.25 1.61.07 18.02.12 34.44.12 16.42 0 32.84-.02 34.41-.1 4.4-.207 6.955-.55 9.78-1.28 7.79-2.01 14.24-7.29 17.75-14.53 1.765-3.64 2.66-7.18 3.065-12.317.088-1.12.125-18.977.125-36.81 0-17.836-.04-35.66-.128-36.78-.41-5.22-1.305-8.73-3.127-12.44-1.495-3.037-3.155-5.305-5.565-7.624C116.9 4 111.64 1.5 105.372.596 102.335.157 101.73.027 86.19 0H65.03z"
        transform="translate(1.004 1)"
      />
      <path
        fill="#fff"
        d="M66.004 18c-13.036 0-14.672.057-19.792.29-5.11.234-8.598 1.043-11.65 2.23-3.157 1.226-5.835 2.866-8.503 5.535-2.67 2.668-4.31 5.346-5.54 8.502-1.19 3.053-2 6.542-2.23 11.65C18.06 51.327 18 52.964 18 66s.058 14.667.29 19.787c.235 5.11 1.044 8.598 2.23 11.65 1.227 3.157 2.867 5.835 5.536 8.503 2.667 2.67 5.345 4.314 8.5 5.54 3.054 1.187 6.543 1.996 11.652 2.23 5.12.233 6.755.29 19.79.29 13.037 0 14.668-.057 19.788-.29 5.11-.234 8.602-1.043 11.656-2.23 3.156-1.226 5.83-2.87 8.497-5.54 2.67-2.668 4.31-5.346 5.54-8.502 1.18-3.053 1.99-6.542 2.23-11.65.23-5.12.29-6.752.29-19.788 0-13.036-.06-14.672-.29-19.792-.24-5.11-1.05-8.598-2.23-11.65-1.23-3.157-2.87-5.835-5.54-8.503-2.67-2.67-5.34-4.31-8.5-5.535-3.06-1.187-6.55-1.996-11.66-2.23-5.12-.233-6.75-.29-19.79-.29zm-4.306 8.65c1.278-.002 2.704 0 4.306 0 12.816 0 14.335.046 19.396.276 4.68.214 7.22.996 8.912 1.653 2.24.87 3.837 1.91 5.516 3.59 1.68 1.68 2.72 3.28 3.592 5.52.657 1.69 1.44 4.23 1.653 8.91.23 5.06.28 6.58.28 19.39s-.05 14.33-.28 19.39c-.214 4.68-.996 7.22-1.653 8.91-.87 2.24-1.912 3.835-3.592 5.514-1.68 1.68-3.275 2.72-5.516 3.59-1.69.66-4.232 1.44-8.912 1.654-5.06.23-6.58.28-19.396.28-12.817 0-14.336-.05-19.396-.28-4.68-.216-7.22-.998-8.913-1.655-2.24-.87-3.84-1.91-5.52-3.59-1.68-1.68-2.72-3.276-3.592-5.517-.657-1.69-1.44-4.23-1.653-8.91-.23-5.06-.276-6.58-.276-19.398s.046-14.33.276-19.39c.214-4.68.996-7.22 1.653-8.912.87-2.24 1.912-3.84 3.592-5.52 1.68-1.68 3.28-2.72 5.52-3.592 1.692-.66 4.233-1.44 8.913-1.655 4.428-.2 6.144-.26 15.09-.27zm29.928 7.97c-3.18 0-5.76 2.577-5.76 5.758 0 3.18 2.58 5.76 5.76 5.76 3.18 0 5.76-2.58 5.76-5.76 0-3.18-2.58-5.76-5.76-5.76zm-25.622 6.73c-13.613 0-24.65 11.037-24.65 24.65 0 13.613 11.037 24.645 24.65 24.645C79.617 90.645 90.65 79.613 90.65 66S79.616 41.35 66.003 41.35zm0 8.65c8.836 0 16 7.163 16 16 0 8.836-7.164 16-16 16-8.837 0-16-7.164-16-16 0-8.837 7.163-16 16-16z"
      />
    </svg>
  );
}

function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 1000 291.379" className={className} aria-hidden>
      <path
        fill="#FF004F"
        d="M191.102 105.182c18.814 13.442 41.862 21.351 66.755 21.351V78.656c-4.711.001-9.41-.49-14.019-1.466v37.686c-24.891 0-47.936-7.909-66.755-21.35v97.703c0 48.876-39.642 88.495-88.54 88.495-18.245 0-35.203-5.513-49.29-14.968 16.078 16.431 38.5 26.624 63.306 26.624 48.901 0 88.545-39.619 88.545-88.497v-97.701h-.002Zm17.294-48.302c-9.615-10.499-15.928-24.067-17.294-39.067v-6.158h-13.285c3.344 19.065 14.75 35.353 30.579 45.225ZM70.181 227.25c-5.372-7.04-8.275-15.652-8.262-24.507 0-22.354 18.132-40.479 40.502-40.479 4.169-.001 8.313.637 12.286 1.897v-48.947c-4.643-.636-9.329-.906-14.013-.807v38.098c-3.976-1.26-8.122-1.9-12.292-1.896-22.37 0-40.501 18.123-40.501 40.48 0 15.808 9.063 29.494 22.28 36.161Z"
      />
      <path
        d="M177.083 93.525c18.819 13.441 41.864 21.35 66.755 21.35V77.189c-13.894-2.958-26.194-10.215-35.442-20.309-15.83-9.873-27.235-26.161-30.579-45.225h-34.896v191.226c-.079 22.293-18.18 40.344-40.502 40.344-13.154 0-24.84-6.267-32.241-15.975-13.216-6.667-22.279-20.354-22.279-36.16 0-22.355 18.131-40.48 40.501-40.48 4.286 0 8.417.667 12.292 1.896v-38.098c-48.039.992-86.674 40.224-86.674 88.474 0 24.086 9.621 45.921 25.236 61.875 14.087 9.454 31.045 14.968 49.29 14.968 48.899 0 88.54-39.621 88.54-88.496V93.525h-.001ZM802.126 239.659c34.989 0 63.354-28.136 63.354-62.84 0-34.703-28.365-62.844-63.354-62.844h-9.545c34.99 0 63.355 28.14 63.355 62.844s-28.365 62.84-63.355 62.84h9.545Z"
      />
      <path
        fill="#00F2EA"
        d="M243.838 77.189V66.999c-12.529.019-24.812-3.488-35.442-10.12 9.41 10.297 21.801 17.397 35.442 20.31ZM177.817 11.655c-.319-1.822-.564-3.656-.734-5.497V0h-48.182v191.228c-.077 22.29-18.177 40.341-40.501 40.341-6.554 0-12.742-1.555-18.222-4.318 7.401 9.707 19.087 15.973 32.241 15.973 22.32 0 40.424-18.049 40.502-40.342V11.655h34.896ZM100.694 114.408V103.56c-4.026-.55-8.085-.826-12.149-.824C39.642 102.735 0 142.356 0 191.228c0 30.64 15.58 57.643 39.255 73.527-15.615-15.953-25.236-37.789-25.236-61.874 0-48.249 38.634-87.481 86.675-88.473Z"
      />
      <path
        d="M310.062 85.572v31.853h37.311v121.374h37.326V118.285h30.372l10.414-32.712H310.062Zm305.482 0v31.853h37.311v121.374h37.326V118.285h30.371l10.413-32.712H615.544Zm-183.11 18.076c0-9.981 8.146-18.076 18.21-18.076 10.073 0 18.228 8.095 18.228 18.076 0 9.982-8.15 18.077-18.228 18.077-10.064-.005-18.21-8.095-18.21-18.077Zm0 30.993h36.438v104.158h-36.438V134.641Zm52.062-49.069v153.226h36.452v-39.594l11.283-10.339 35.577 50.793h39.05l-51.207-74.03 45.997-44.768h-44.258l-36.442 36.153V85.572h-36.452Zm393.127 0v153.226h36.457v-39.594l11.278-10.339 35.587 50.793H1000l-51.207-74.03 45.995-44.768h-44.256l-36.452 36.153V85.572h-36.457ZM792.578 239.659c34.988 0 63.358-28.136 63.358-62.84 0-34.703-28.37-62.844-63.358-62.844h-.865c-34.99 0-63.355 28.14-63.355 62.844s28.365 62.84 63.355 62.84h.865Zm-31.242-62.84c0-16.881 13.8-30.555 30.817-30.555 17.005 0 30.804 13.674 30.804 30.555s-13.799 30.563-30.804 30.563c-17.017-.003-30.817-13.682-30.817-30.563Z"
        fill="#fff"
      />
    </svg>
  );
}

function TikTokMarkIcon({ className }) {
  return (
    <svg viewBox="-0.32296740998066475 -3.1283528999801873 42.68446958125966 42.128352899980186" className={className} aria-hidden>
      <g fill="none">
        <path
          d="M14 15.599v-1.486A13.1 13.1 0 0 0 12.337 14C5.535 14 0 19.18 0 25.547 0 29.452 2.086 32.91 5.267 35c-2.13-2.132-3.315-4.942-3.313-7.861 0-6.276 5.377-11.394 12.046-11.54"
          fill="#00f2ea"
        />
        <path
          d="M14.327 32c2.876 0 5.221-2.273 5.328-5.107l.01-25.292h4.65A8.72 8.72 0 0 1 24.164 0h-6.35l-.011 25.293c-.106 2.832-2.453 5.105-5.328 5.105a5.329 5.329 0 0 1-2.476-.61A5.34 5.34 0 0 0 14.327 32m18.672-21.814V8.78a8.818 8.818 0 0 1-4.81-1.421A8.85 8.85 0 0 0 33 10.186"
          fill="#00f2ea"
        />
        <path
          d="M28 7.718A8.63 8.63 0 0 1 25.832 2h-1.697A8.735 8.735 0 0 0 28 7.718M12.325 20.065c-2.94.004-5.322 2.361-5.325 5.27A5.267 5.267 0 0 0 9.854 30a5.2 5.2 0 0 1-1.008-3.073c.003-2.91 2.385-5.268 5.325-5.271.55 0 1.075.09 1.572.244v-6.4a11.72 11.72 0 0 0-1.572-.114c-.092 0-.183.006-.274.007v4.916a5.286 5.286 0 0 0-1.572-.244"
          fill="#ff004f"
        />
        <path
          d="M32.153 11v4.884a15.15 15.15 0 0 1-8.813-2.811V25.84c0 6.377-5.23 11.565-11.658 11.565-2.485 0-4.789-.778-6.682-2.097A11.67 11.67 0 0 0 13.528 39c6.429 0 11.659-5.188 11.659-11.564V14.668A15.15 15.15 0 0 0 34 17.478v-6.283A8.87 8.87 0 0 1 32.153 11"
          fill="#ff004f"
        />
        <path
          d="M23.979 25.42V12.632A15.741 15.741 0 0 0 33 15.448v-4.89a9.083 9.083 0 0 1-4.912-2.82C26.016 6.431 24.586 4.358 24.132 2h-4.747l-.01 25.215c-.11 2.824-2.505 5.09-5.44 5.09-1.754-.002-3.398-.822-4.42-2.204-1.794-.913-2.919-2.716-2.92-4.682.003-2.92 2.44-5.285 5.45-5.289.56 0 1.098.09 1.608.245v-4.933C7.202 15.589 2 20.722 2 27.016c0 3.045 1.219 5.816 3.205 7.885A12.115 12.115 0 0 0 12.045 37c6.58 0 11.934-5.195 11.934-11.58"
          fill="#fff"
        />
      </g>
    </svg>
  );
}

function FacebookIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="#1877F2"
        d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073c0 6.026 4.388 11.022 10.125 11.927v-8.438H7.078v-3.49h3.047V9.41c0-3.024 1.792-4.694 4.533-4.694 1.313 0 2.686.236 2.686.236v2.97h-1.514c-1.49 0-1.955.93-1.955 1.887v2.263h3.328l-.532 3.49h-2.796V24C19.612 23.095 24 18.099 24 12.073Z"
      />
      <path
        fill="#fff"
        d="m16.671 15.562.532-3.49h-3.328V9.81c0-.957.465-1.887 1.955-1.887h1.514v-2.97s-1.373-.236-2.686-.236c-2.741 0-4.533 1.67-4.533 4.694v2.662H7.078v3.49h3.047V24a12.105 12.105 0 0 0 3.75 0v-8.438h2.796Z"
      />
    </svg>
  );
}

function BillingPage({
  billingOverview,
  accountSubscription,
  recentCreditActivity,
  recentProviderUsage,
  accountLoading,
  pricingCatalog,
  onToggleSubscriptionRenewal,
  onOpenBillingPortal,
  onCreateCheckoutSession
}) {
  const hasActiveSubscription = Boolean(accountSubscription?.has_active_subscription);
  const isByoApi = accountSubscription?.billing_mode === "byo_api" || billingOverview?.billing_mode === "byo_api";
  const planName = accountSubscription?.plan?.name || billingOverview?.plan?.name || "No active plan";
  const currentPlanSlug = accountSubscription?.plan?.slug || billingOverview?.plan?.slug || "";
  const monthlyCredits = Number(accountSubscription?.monthly_credits || billingOverview?.monthly_credits || 0);
  const creditsBalance = Number(billingOverview?.credits_balance || 0);
  const remainingCreditsPercentage = monthlyCredits > 0 ? Math.min((creditsBalance / monthlyCredits) * 100, 100) : 0;
  const renewalLabel = formatAccountDate(accountSubscription?.current_period_end || billingOverview?.renewal_date);
  const creditPlans = Array.isArray(pricingCatalog?.plans) ? pricingCatalog.plans.filter((plan) => plan.billing_mode === "credits") : [];
  const agencyPlan = Array.isArray(pricingCatalog?.plans) ? pricingCatalog.plans.find((plan) => plan.billing_mode === "byo_api") : null;
  const supportEmail = agencyPlan?.contact_email || "hello@ugcpersona.com";
  const shellCardClass = "rounded-[1.65rem] border-slate-700/80 bg-slate-900/70 p-3.5 shadow-[0_14px_36px_rgba(2,6,23,.34)] md:p-4";
  const ghostButtonClass =
    "inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-900/90 px-3.5 py-2.5 text-[13px] font-semibold text-slate-100 transition hover:bg-slate-800";
  const primaryActionButtonClass =
    "inline-flex items-center justify-center rounded-xl bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] px-4 py-2.5 text-[13px] font-semibold text-slate-50 shadow-[0_14px_32px_rgba(2,6,23,.32)] transition hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))] hover:shadow-[0_18px_40px_rgba(8,20,43,.45)] disabled:cursor-not-allowed disabled:bg-slate-900/85 disabled:text-slate-500";
  const planActionClass =
    "inline-flex w-full items-center justify-center rounded-xl border px-3.5 py-2.5 text-[13px] font-semibold transition";

  return (
    <div className="grid gap-3 xl:grid-cols-2">
      <MagicCard className={cn("xl:col-span-2", shellCardClass)}>
        <div>
          <h2 className="text-[1.35rem] font-semibold tracking-tight text-slate-100 md:text-[1.45rem]">Plan & Billing</h2>
          <p className="mt-1 text-[13px] text-slate-400">Your subscription, renewal status, and current credit balance.</p>
        </div>

        <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-stretch">
          <div className="billing-surface min-w-0 flex-1 lg:basis-0 rounded-[1.5rem] p-4 md:p-[18px]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium text-slate-100">{isByoApi ? "Usage mode" : "Credits remaining"}</p>
              <p className="text-[15px] font-semibold text-slate-100 md:text-base">
                {isByoApi ? "Bring Your Own API" : `${creditsBalance.toLocaleString("en-US")} credits / ${monthlyCredits.toLocaleString("en-US")} credits`}
              </p>
            </div>
            {!isByoApi ? (
              <>
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-800/90">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-slate-50/95 via-cyan-300/90 to-cyan-400/95 transition-all"
                    style={{ width: `${remainingCreditsPercentage}%` }}
                  />
                </div>
                <p className="mt-2.5 text-[11px] text-slate-500">
                  {creditsBalance.toLocaleString("en-US")} credits remaining. Credits reset every month and do not roll over.
                </p>
              </>
            ) : (
              <p className="mt-4 text-[13px] text-slate-300">This workspace uses the agency billing mode. Provider costs are charged directly on your own API accounts.</p>
            )}
          </div>

          <div className="billing-surface min-w-0 flex-1 lg:basis-0 rounded-[1.5rem] p-4 md:p-[18px]">
            <div className="flex flex-col gap-3">
              {hasActiveSubscription ? (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-400">
                    Active
                  </p>
                  <div>
                    <p className="text-sm font-medium text-slate-100 md:text-[15px]">
                      You're currently on{" "}
                      <span className="inline-flex items-center rounded-full border border-slate-600 bg-slate-800/80 px-3 py-1.5 text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[inset_0_1px_0_rgba(255,255,255,.08)]">
                        {planName}
                      </span>
                    </p>
                    <p className="mt-2 text-[13px] text-slate-400">
                      {accountSubscription?.cancel_at_period_end ? "Access ends on " : "Renews on "}
                      <strong className="font-semibold text-slate-100">{renewalLabel}</strong>
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-amber-400">
                    No Active Plan
                  </p>
                  <div>
                    <p className="text-sm font-medium text-slate-100 md:text-[15px]">
                      <span className="text-amber-200">No active subscription</span>
                    </p>
                    <p className="mt-2 text-[13px] text-slate-400">
                      Subscribe to a plan below to start generating content
                    </p>
                  </div>
                </>
              )}
              <p className="text-[12px] text-slate-500">
                {hasActiveSubscription
                  ? "Choose a new plan below to upgrade or downgrade your subscription."
                  : "Select a plan and complete checkout to activate your account."}
              </p>
            </div>
          </div>
        </div>

        <div className="billing-surface billing-surface-allow-overflow mt-3 rounded-[1.5rem] p-4 md:p-[18px]">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="inline-flex items-center rounded-xl border border-slate-700 bg-slate-950/80 p-1">
              <button type="button" className="rounded-lg bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-950">
                Monthly
              </button>
              <button type="button" disabled className="rounded-lg px-3.5 py-2 text-[13px] font-semibold text-slate-500">
                Yearly (coming soon)
              </button>
            </div>

            <a
              href={`mailto:${supportEmail}?subject=UGC%20Persona%20-%20Unlimited%20Plan`}
              className={ghostButtonClass}
            >
              Contact support
            </a>
          </div>

          <div className="mt-4 overflow-x-auto px-1 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max gap-4">
              {creditPlans.map((plan) => {
                const isCurrentPlan = plan.slug === currentPlanSlug;
                const isUpgrade = Number(plan.monthly_credits || 0) > monthlyCredits;
                let actionLabel;
                if (!hasActiveSubscription) {
                  actionLabel = "Subscribe";
                } else if (isCurrentPlan) {
                  actionLabel = "Current plan";
                } else if (isUpgrade) {
                  actionLabel = "Upgrade";
                } else {
                  actionLabel = "Downgrade";
                }
                const badgeLabel = plan.slug === "starter" ? null : plan.badge_label;

                return (
                  <div
                    key={plan.slug}
                    className={cn(
                      "pricing-card w-[286px] p-4 md:w-[298px] md:p-[18px]",
                      isCurrentPlan
                        ? "pricing-card-current"
                        : "border-slate-700"
                    )}
                  >
                    {isCurrentPlan ? (
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-200">
                        Your Current Plan
                      </p>
                    ) : null}
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[1.75rem] font-semibold tracking-tight text-slate-100">{plan.name}</p>
                        <p className="mt-2.5 text-[2.2rem] font-semibold tracking-tight text-white">
                          {formatPlanPrice(plan.price_cents, plan.currency)}
                          <span className="ml-1 text-[0.48em] font-medium text-slate-400">/mo</span>
                        </p>
                      </div>
                      {badgeLabel ? (
                        <span className="rounded-full bg-gradient-to-r from-amber-300 via-orange-400 to-rose-400 px-3 py-1 text-xs font-semibold text-slate-950">
                          {badgeLabel}
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-4">
                      <button
                        type="button"
                        disabled={isCurrentPlan || accountLoading.subscription || accountLoading.checkout}
                        className={cn(
                          planActionClass,
                          isCurrentPlan
                            ? "border-slate-700 bg-slate-900/85 text-slate-500"
                            : "border-cyan-400/35 bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] text-slate-50 hover:border-cyan-300/50 hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))]"
                        )}
                        onClick={() => {
                          if (!hasActiveSubscription) {
                            onCreateCheckoutSession(plan.slug);
                          } else {
                            onToggleSubscriptionRenewal("change_plan", plan.slug);
                          }
                        }}
                      >
                        {accountLoading.checkout ? "Redirecting to Stripe..." : accountLoading.subscription && !isCurrentPlan ? "Updating..." : actionLabel}
                      </button>
                    </div>

                    <div className="mt-4 border-t border-slate-800 pt-4">
                      <ul className="space-y-2.5 text-[13px] leading-relaxed text-slate-300">
                        {(plan.marketing_features || []).map((item) => (
                          <li key={`${plan.slug}-${item}`} className="flex items-start gap-3">
                            <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-cyan-300" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}

              <div className="pricing-card w-[286px] p-4 md:w-[298px] md:p-[18px]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-200">
                      {agencyPlan?.eyebrow || "B2B / Private Label"}
                    </p>
                    <p className="mt-2 text-[1.55rem] font-semibold leading-snug tracking-tight text-slate-100">
                      {agencyPlan?.tagline || agencyPlan?.name || "Want to use your own API keys?"}
                    </p>
                    {agencyPlan?.description ? (
                      <p className="mt-2 text-[13px] leading-relaxed text-slate-300">{agencyPlan.description}</p>
                    ) : null}
                  </div>
                  <span className="rounded-full border border-cyan-400/35 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-100">
                    B2B
                  </span>
                </div>

                <div className="mt-4">
                  <a
                    href={`mailto:${supportEmail}?subject=UGC%20Persona%20-%20B2B%20Inquiry`}
                    className="inline-flex w-full items-center justify-center rounded-xl border border-cyan-400/35 bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] px-3.5 py-2.5 text-[13px] font-semibold text-slate-50 transition hover:border-cyan-300/50 hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))]"
                  >
                    {agencyPlan?.cta_label || "Contact sales"}
                  </a>
                </div>

                <div className="mt-4 border-t border-slate-800 pt-4">
                  <ul className="space-y-2.5 text-[13px] leading-relaxed text-slate-300">
                    {(agencyPlan?.marketing_features || [
                      "Bring your own API keys",
                      "White label ready",
                      "Agency usage",
                      "Custom limits",
                      "No credit system"
                    ]).map((item) => (
                      <li key={`agency-${item}`} className="flex items-start gap-3">
                        <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-cyan-300" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="billing-actions" className="mt-4 flex flex-wrap gap-2">
          {hasActiveSubscription ? (
            <button type="button" className={primaryActionButtonClass} onClick={onToggleSubscriptionRenewal} disabled={accountLoading.subscription}>
              {accountLoading.subscription
                ? "Saving subscription..."
                : accountSubscription?.cancel_at_period_end
                  ? "Resume renewal"
                  : "Cancel renewal"}
            </button>
          ) : null}
          <a
            href={`mailto:${supportEmail}?subject=UGC%20Persona%20-%20Billing%20support`}
            className={ghostButtonClass}
          >
            Contact support
          </a>
        </div>
      </MagicCard>

      <MagicCard className={cn("xl:col-span-2", shellCardClass)}>
        <h2 className="text-[1.15rem] font-semibold tracking-tight text-slate-100">Invoices</h2>
        <p className="mt-1 text-[13px] text-slate-400">
          Open Stripe to review invoices, payment history, and downloadable receipts for this subscription.
        </p>

        <div className="billing-surface mt-3 rounded-[1.2rem] p-4 md:p-[18px]">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-medium text-slate-100">Manage invoices in Stripe Billing Portal</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-slate-400">
                Stripe hosts the invoice history, downloadable PDF receipts, payment methods, and billing details for your account.
              </p>
            </div>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/35 bg-[linear-gradient(180deg,rgba(8,20,43,.96),rgba(6,10,24,.98))] px-4 py-2.5 text-[13px] font-semibold text-slate-50 transition hover:border-cyan-300/50 hover:bg-[linear-gradient(180deg,rgba(12,28,58,.98),rgba(7,12,28,.98))] disabled:cursor-not-allowed disabled:border-slate-700 disabled:bg-slate-900/85 disabled:text-slate-500"
              disabled={!hasActiveSubscription || accountLoading.portal}
              onClick={onOpenBillingPortal}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current stroke-[2]" aria-hidden>
                <path d="M14 3h7v7" />
                <path d="M10 14 21 3" />
                <path d="M21 14v4a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3h4" />
              </svg>
              {accountLoading.portal ? "Opening Stripe..." : "Open invoices in Stripe"}
            </button>
          </div>
          {!hasActiveSubscription ? (
            <p className="mt-3 text-[12px] text-slate-500">A Stripe-backed active subscription is required before invoices can be opened here.</p>
          ) : null}
        </div>
      </MagicCard>

      <MagicCard className={shellCardClass}>
        <h2 className="text-[1.15rem] font-semibold tracking-tight text-slate-100">Credit Activity</h2>
        <p className="mt-1 text-[13px] text-slate-400">Recent balance changes on your account.</p>

        <div className="billing-surface mt-3 overflow-hidden rounded-[1.2rem]">
          {recentCreditActivity.length ? (
            <table className="w-full text-left text-[13px]">
              <thead className="bg-slate-900/85 text-[10px] uppercase tracking-[0.14em] text-slate-400">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Credits</th>
                  <th className="px-3 py-2">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-950/60">
                {recentCreditActivity.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-3 py-2 text-[12px] text-slate-300">{formatAccountDate(entry.created_at)}</td>
                    <td className="px-3 py-2 text-[12px] capitalize text-slate-300">{String(entry.kind || "").replaceAll("_", " ")}</td>
                    <td className="px-3 py-2 text-[12px] text-slate-300">{entry.description || "-"}</td>
                    <td className={cn("px-3 py-2 font-semibold", Number(entry.credits_delta) < 0 ? "text-rose-200" : "text-emerald-200")}>
                      {formatCreditDelta(entry.credits_delta)}
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-300">{Number(entry.credits_balance_after || 0).toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-6 text-[13px] text-slate-400">No credit activity yet.</div>
          )}
        </div>
      </MagicCard>

      <MagicCard className={shellCardClass}>
        <h2 className="text-[1.15rem] font-semibold tracking-tight text-slate-100">Recent API Usage</h2>
        <p className="mt-1 text-[13px] text-slate-400">Latest billed provider operations recorded on your workspace.</p>

        <div className="billing-surface mt-3 overflow-hidden rounded-[1.2rem]">
          {recentProviderUsage.length ? (
            <table className="w-full text-left text-[13px]">
              <thead className="bg-slate-900/85 text-[10px] uppercase tracking-[0.14em] text-slate-400">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Provider</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Model</th>
                  <th className="px-3 py-2">Credits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 bg-slate-950/60">
                {recentProviderUsage.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-3 py-2 text-[12px] text-slate-300">{formatAccountDate(entry.created_at)}</td>
                    <td className="px-3 py-2 text-[12px] text-slate-300">{entry.provider || "-"}</td>
                    <td className="px-3 py-2 text-[12px] text-slate-300">{String(entry.action || "").replaceAll("_", " ")}</td>
                    <td className="px-3 py-2 text-[12px] text-slate-300">{entry.model || "-"}</td>
                    <td className="px-3 py-2 font-semibold text-cyan-100">{Number(entry.credits_used || 0).toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-6 text-[13px] text-slate-400">No provider usage recorded yet.</div>
          )}
        </div>
      </MagicCard>
    </div>
  );
}

export default function App({
  initial_route: initialRoute = "/generation",
  auth,
  app_config: appConfig,
  pricing_catalog: pricingCatalog = { plans: [], creditsInfo: {} },
  billing_overview: initialBillingOverviewProp = initialBillingOverview,
  account_profile: initialAccountProfileProp = {},
  account_subscription: initialAccountSubscriptionProp = initialAccountSubscription,
  social_connections: initialSocialConnectionsProp = initialSocialConnections,
  social_features: initialSocialFeaturesProp = initialSocialFeatures,
  recent_credit_activity: initialCreditActivityProp = [],
  recent_provider_usage: initialProviderUsageProp = [],
  selected_plan_slug: selectedPlanSlugProp = null,
  selected_discount_code: selectedDiscountCodeProp = null
}) {
  const route = !auth?.superadmin && normalizeRoute(initialRoute) === "/settings" ? "/account" : normalizeRoute(initialRoute);
  const lockedDefaults = useMemo(() => lockedFormDefaultsFromAppConfig(appConfig), [appConfig]);
  const [pipeline, setPipeline] = useState(initialPipeline);
  const [operationCosts, setOperationCosts] = useState(initialOperationCosts);
  const [settings, setSettings] = useState(() => buildSettingsFromAppConfig(appConfig));
  const [billingOverview, setBillingOverview] = useState(initialBillingOverviewProp);
  const [accountSubscription, setAccountSubscription] = useState(initialAccountSubscriptionProp || initialAccountSubscription);
  const [socialConnections, setSocialConnections] = useState(Array.isArray(initialSocialConnectionsProp) ? initialSocialConnectionsProp : []);
  const [socialFeatures, setSocialFeatures] = useState(initialSocialFeaturesProp || initialSocialFeatures);
  const [recentCreditActivity, setRecentCreditActivity] = useState(Array.isArray(initialCreditActivityProp) ? initialCreditActivityProp : []);
  const [recentProviderUsage, setRecentProviderUsage] = useState(Array.isArray(initialProviderUsageProp) ? initialProviderUsageProp : []);
  const [elevenlabsSettings, setElevenlabsSettings] = useState(() => buildElevenlabsSettingsFromAppConfig(appConfig));
  const [account, setAccount] = useState(() => buildAccountFromProps(auth, initialAccountProfileProp));
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", password: "", passwordConfirmation: "" });
  const [accountLoading, setAccountLoading] = useState({ profile: false, password: false, subscription: false, portal: false, checkout: false });
  const [socialLoading, setSocialLoading] = useState({
    connectMeta: false,
    refreshMeta: false,
    connectTiktok: false,
    refreshTiktok: false,
    disconnect: false,
    select: false
  });
  const [publishResults, setPublishResults] = useState({ instagramReel: null, tiktok: null });
  const [forms, setForms] = useState(() => buildFormsFromAppConfig(appConfig));
  const [approvedVoices, setApprovedVoices] = useState([]);
  const [feedback, setFeedback] = useState({ message: "", kind: "info" });
  const [hydrated, setHydrated] = useState(false);
  const [loading, setLoading] = useState(initialLoadingState);
  const [wizardUi, setWizardUi] = useState(() => buildWizardUiState(initialPipeline));
  const [showTiktokVideoHelpModal, setShowTiktokVideoHelpModal] = useState(false);
  const generationTaskPollersRef = useRef({});
  const tiktokConnection = getTiktokConnection(socialConnections);
  const tiktokUploadScopeGranted = Boolean(
    Array.isArray(tiktokConnection?.granted_scopes) && tiktokConnection.granted_scopes.includes("video.upload")
  );

  useEffect(() => {
    const handlePageHide = () => {
      Object.values(generationTaskPollersRef.current).forEach((timerId) => window.clearTimeout(timerId));
      generationTaskPollersRef.current = {};
      setLoading(initialLoadingState);
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      Object.values(generationTaskPollersRef.current).forEach((timerId) => window.clearTimeout(timerId));
      generationTaskPollersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!showTiktokVideoHelpModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowTiktokVideoHelpModal(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showTiktokVideoHelpModal]);

  useEffect(() => {
    setSettings(buildSettingsFromAppConfig(appConfig));
    setElevenlabsSettings(buildElevenlabsSettingsFromAppConfig(appConfig));
    setApprovedVoices([]);
    setBillingOverview(initialBillingOverviewProp);
    setAccount(buildAccountFromProps(auth, initialAccountProfileProp));
    setAccountSubscription(initialAccountSubscriptionProp || initialAccountSubscription);
    setSocialConnections(Array.isArray(initialSocialConnectionsProp) ? initialSocialConnectionsProp : []);
    setSocialFeatures(initialSocialFeaturesProp || initialSocialFeatures);
    setRecentCreditActivity(Array.isArray(initialCreditActivityProp) ? initialCreditActivityProp : []);
    setRecentProviderUsage(Array.isArray(initialProviderUsageProp) ? initialProviderUsageProp : []);
    setForms((prev) => ({
      ...prev,
      ...lockedDefaults
    }));
  }, [appConfig, auth, initialBillingOverviewProp, initialAccountProfileProp, initialAccountSubscriptionProp, initialSocialConnectionsProp, initialSocialFeaturesProp, initialCreditActivityProp, initialProviderUsageProp, lockedDefaults]);

  const activeModels = useMemo(
    () => {
      const falAll = getActiveModels("fal", settings);
      const wavespeedAll = getActiveModels("wavespeed", settings);
      return {
        fal: falAll,
        falImage: falAll.filter((model) => FAL_IMAGE_MODELS.includes(model)),
        falEdit: falAll.filter((model) => FAL_IMAGE_EDIT_MODELS.includes(model)),
        falVideo: falAll.filter((model) => FAL_VIDEO_MODELS.includes(model)),
        wavespeed: wavespeedAll,
        wavespeedImage: wavespeedAll.filter((model) => WAVESPEED_IMAGE_MODELS.includes(model)),
        wavespeedEdit: wavespeedAll.filter((model) => WAVESPEED_IMAGE_EDIT_MODELS.includes(model)),
        openrouter: getActiveModels("openrouter", settings),
        elevenlabs: getActiveModels("elevenlabs", settings)
      };
    },
    [settings]
  );

  const hasProviderModels = useMemo(
    () => Object.values(settings.providerModels).some((models) => models.length > 0),
    [settings.providerModels]
  );

  function navigate(nextRoute) {
    const normalizedNextRoute = normalizeRoute(nextRoute);
    const target = !auth?.superadmin && normalizedNextRoute === "/settings" ? "/account" : normalizedNextRoute;
    if (target === route) return;
    router.visit(appPathFor(target), {
      preserveScroll: true
    });
  }

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setHydrated(true);
        return;
      }

      const parsed = JSON.parse(raw);
      let nextPipeline = initialPipeline;
      let nextForms = null;

      if (parsed.pipeline) {
        nextPipeline = {
          ...initialPipeline,
          ...parsed.pipeline,
          audio: {
            ...initialPipeline.audio,
            ...(parsed.pipeline.audio || {}),
            url: normalizeInternalAssetUrl(parsed.pipeline?.audio?.url)
          }
        };
        setPipeline(nextPipeline);
      }
      if (parsed.operationCosts) setOperationCosts({ ...initialOperationCosts, ...parsed.operationCosts });
      if (parsed.forms) {
        nextForms = {
          ...initialForms,
          ...parsed.forms,
          ...lockedDefaults,
          videoAudioUrl: normalizeInternalAssetUrl(parsed.forms.videoAudioUrl)
        };
        const normalizedVideoModel = normalizeFalVideoModelId(nextForms.videoModel || initialForms.videoModel);
        const normalizedVideoResolution =
          getVideoResolutionOptions(normalizedVideoModel).includes(nextForms.videoResolution)
            ? nextForms.videoResolution
            : getVideoResolutionOptions(normalizedVideoModel)[0] || "720p";
        setForms((prev) => ({
          ...prev,
          ...nextForms,
          videoModel: normalizedVideoModel,
          videoResolution: normalizedVideoResolution,
          socialPreviewEnabled: initialForms.socialPreviewEnabled,
          socialPreviewMode: nextForms.socialPreviewMode || initialForms.socialPreviewMode
        }));
      }
      setWizardUi(buildWizardUiState(nextPipeline, parsed.ui));
    } catch {
      // Ignore malformed localStorage state.
    } finally {
      setHydrated(true);
    }
  }, [lockedDefaults]);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        pipeline,
        operationCosts,
        forms: buildPersistableForms(forms),
        ui: wizardUi
      })
    );
  }, [pipeline, operationCosts, forms, wizardUi, hydrated]);

  useEffect(() => {
    if (!hydrated || !settings.providerStatus?.elevenlabs) return;
    loadApprovedVoices({ silent: true });
  }, [hydrated, settings.providerStatus?.elevenlabs, elevenlabsSettings.voiceScope]);

  useEffect(() => {
    if (!hydrated || hasProviderModels) return;

    (async () => {
      try {
        const data = await apiCall("/api/models", null, { method: "GET" });
        setSettings((prev) => {
          const fal = uniq([...(data.imageFal || []), ...(data.imageEditFal || []), ...(data.videoFal || []), ...FAL_IMAGE_EDIT_MODELS]);
          const wavespeed = uniq([...(data.imageWavespeed || []), ...(data.imageEditWavespeed || []), ...WAVESPEED_IMAGE_EDIT_MODELS]);
          const openrouter = uniq([...(data.copyOpenRouter || []), ...OPENROUTER_COPY_MODELS]);
          const elevenlabs = uniq([...(data.ttsElevenLabs || []), ...ELEVENLABS_TTS_MODELS]);

          return {
            ...prev,
            providerModels: {
              fal,
              wavespeed,
              openrouter,
              elevenlabs
            },
            enabledModels: {
              fal: buildEnabledMap(fal, prev.enabledModels.fal),
              wavespeed: buildEnabledMap(wavespeed, prev.enabledModels.wavespeed),
              openrouter: ensureAtLeastOneEnabled(openrouter, prev.enabledModels.openrouter),
              elevenlabs: buildEnabledMap(elevenlabs, prev.enabledModels.elevenlabs)
            }
          };
        });
      } catch (error) {
        setFeedback({ message: `Base model loading error: ${error.message}`, kind: "error" });
      }
    })();
  }, [hydrated, hasProviderModels]);

  useEffect(() => {
    if (!hydrated) return;
    setSettings((prev) => {
      const currentOpenRouterModels = prev.providerModels.openrouter || [];
      if (currentOpenRouterModels.length > 0) return prev;

      const fallbackOpenRouterModels = [...OPENROUTER_COPY_MODELS];
      return {
        ...prev,
        providerModels: {
          ...prev.providerModels,
          openrouter: fallbackOpenRouterModels
        },
        enabledModels: {
          ...prev.enabledModels,
          openrouter: ensureAtLeastOneEnabled(fallbackOpenRouterModels, prev.enabledModels.openrouter)
        }
      };
    });
  }, [hydrated]);

  useEffect(() => {
    setForms((prev) => {
      let changed = false;
      const next = { ...prev };

      const imageModels = getImageModelsByProvider(prev.imageProvider, activeModels);
      if (imageModels.length > 0 && !imageModels.includes(next.imageModel)) {
        next.imageModel = imageModels.includes(lockedDefaults.imageModel) ? lockedDefaults.imageModel : imageModels[0] || "";
        changed = true;
      }

      const imageEditModels = getImageEditModelsByProvider(prev.imageEditProvider, activeModels);
      if (imageEditModels.length > 0 && !imageEditModels.includes(next.imageEditModel)) {
        next.imageEditModel =
          (imageEditModels.includes(lockedDefaults.imageEditModel) ? lockedDefaults.imageEditModel : imageEditModels[0]) ||
          (prev.imageEditProvider === "fal" ? DEFAULT_FAL_IMAGE_EDIT_MODEL : DEFAULT_WAVESPEED_IMAGE_EDIT_MODEL);
        changed = true;
      }

      if (activeModels.openrouter.length > 0 && !activeModels.openrouter.includes(next.copyModel)) {
        next.copyModel = activeModels.openrouter.includes(lockedDefaults.copyModel) ? lockedDefaults.copyModel : activeModels.openrouter[0] || "";
        changed = true;
      }
      if (activeModels.elevenlabs.length > 0 && !activeModels.elevenlabs.includes(next.ttsModel)) {
        next.ttsModel = activeModels.elevenlabs.includes(lockedDefaults.ttsModel) ? lockedDefaults.ttsModel : activeModels.elevenlabs[0] || "";
        changed = true;
      }
      if (activeModels.falVideo.length > 0 && !activeModels.falVideo.includes(next.videoModel)) {
        next.videoModel = activeModels.falVideo.includes(lockedDefaults.videoModel) ? lockedDefaults.videoModel : activeModels.falVideo[0] || FAL_VIDEO_MODELS[0];
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [activeModels, lockedDefaults]);

  async function handleSaveProfile() {
    setAccountLoading((prev) => ({ ...prev, profile: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall(
        "/api/account/update_profile",
        {
          account: {
            name: account.fullName,
            company_name: account.companyName,
            job_title: account.jobTitle,
            timezone: account.timezone,
            locale: account.locale
          }
        },
        { method: "PATCH" }
      );
      setAccount(buildAccountFromProps(auth, data.accountProfile || {}));
      setFeedback({ message: "Profile updated successfully.", kind: "ok" });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setAccountLoading((prev) => ({ ...prev, profile: false }));
    }
  }

  async function handleUpdatePassword() {
    setAccountLoading((prev) => ({ ...prev, password: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall(
        "/api/account/update_password",
        {
          currentPassword: passwordForm.currentPassword,
          password: passwordForm.password,
          passwordConfirmation: passwordForm.passwordConfirmation
        },
        { method: "PATCH" }
      );
      setPasswordForm({ currentPassword: "", password: "", passwordConfirmation: "" });
      setFeedback({ message: data.message || "Password updated successfully.", kind: "ok" });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setAccountLoading((prev) => ({ ...prev, password: false }));
    }
  }

  async function handleOpenBillingPortal() {
    setAccountLoading((prev) => ({ ...prev, portal: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall("/api/account/create_billing_portal_session", {}, { method: "POST" });
      if (!data?.url) throw new Error("Stripe billing portal URL was not returned.");
      window.location.assign(data.url);
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setAccountLoading((prev) => ({ ...prev, portal: false }));
    }
  }

  async function handleToggleSubscriptionRenewal(intentOverride = null, planSlug = null) {
    if (!accountSubscription?.has_active_subscription && intentOverride !== "change_plan") return;

    setAccountLoading((prev) => ({ ...prev, subscription: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const intent = intentOverride || (accountSubscription.cancel_at_period_end ? "resume_renewal" : "cancel_renewal");
      const data = await apiCall(
        "/api/account/update_subscription",
        {
          intent,
          planSlug
        },
        { method: "PATCH" }
      );
      if (data.accountSubscription) setAccountSubscription(data.accountSubscription);
      if (data.billingOverview) setBillingOverview(data.billingOverview);
      setFeedback({ message: data.message || "Subscription updated successfully.", kind: "ok" });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setAccountLoading((prev) => ({ ...prev, subscription: false }));
    }
  }

  async function handleCreateCheckoutSession(planSlug, discountCodeOverride = null) {
    if (accountSubscription?.has_active_subscription) return;

    setAccountLoading((prev) => ({ ...prev, checkout: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall(
        "/api/account/create_checkout_session",
        {
          plan_slug: planSlug,
          discount_code: discountCodeOverride || selectedDiscountCodeProp || ""
        },
        { method: "POST" }
      );
      if (!data?.url) throw new Error("Stripe checkout URL was not returned.");
      window.location.assign(data.url);
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
      setAccountLoading((prev) => ({ ...prev, checkout: false }));
    }
  }

  async function handleConnectMeta() {
    setSocialLoading((prev) => ({ ...prev, connectMeta: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall("/api/social_connections/meta/authorize", {}, { method: "POST" });
      if (!data?.url) throw new Error("Meta authorization URL was not returned.");
      window.location.assign(data.url);
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
      setSocialLoading((prev) => ({ ...prev, connectMeta: false }));
    }
  }

  async function handleRefreshMeta() {
    setSocialLoading((prev) => ({ ...prev, refreshMeta: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall("/api/social_connections/meta/refresh", {}, { method: "POST" });
      setSocialConnections(Array.isArray(data.connections) ? data.connections : []);
      if (typeof data.metaConfigured === "boolean") {
        setSocialFeatures((prev) => ({ ...prev, meta_configured: data.metaConfigured }));
      }
      if (typeof data.tiktokConfigured === "boolean") {
        setSocialFeatures((prev) => ({ ...prev, tiktok_configured: data.tiktokConfigured }));
      }
      setFeedback({ message: data.message || "Meta accounts refreshed.", kind: "ok" });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setSocialLoading((prev) => ({ ...prev, refreshMeta: false }));
    }
  }

  async function handleConnectTiktok() {
    setSocialLoading((prev) => ({ ...prev, connectTiktok: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall("/api/social_connections/tiktok/authorize", {}, { method: "POST" });
      if (!data?.url) throw new Error("TikTok authorization URL was not returned.");
      window.location.assign(data.url);
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
      setSocialLoading((prev) => ({ ...prev, connectTiktok: false }));
    }
  }

  async function handleRefreshTiktok() {
    setSocialLoading((prev) => ({ ...prev, refreshTiktok: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall("/api/social_connections/tiktok/refresh", {}, { method: "POST" });
      setSocialConnections(Array.isArray(data.connections) ? data.connections : []);
      if (typeof data.metaConfigured === "boolean") {
        setSocialFeatures((prev) => ({ ...prev, meta_configured: data.metaConfigured }));
      }
      if (typeof data.tiktokConfigured === "boolean") {
        setSocialFeatures((prev) => ({ ...prev, tiktok_configured: data.tiktokConfigured }));
      }
      setFeedback({ message: data.message || "TikTok account refreshed.", kind: "ok" });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setSocialLoading((prev) => ({ ...prev, refreshTiktok: false }));
    }
  }

  async function handleDisconnectSocialConnection(connectionId) {
    setSocialLoading((prev) => ({ ...prev, disconnect: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall(`/api/social_connections/${connectionId}`, {}, { method: "DELETE" });
      setSocialConnections(Array.isArray(data.connections) ? data.connections : []);
      if (typeof data.metaConfigured === "boolean" || typeof data.tiktokConfigured === "boolean") {
        setSocialFeatures((prev) => ({
          ...prev,
          ...(typeof data.metaConfigured === "boolean" ? { meta_configured: data.metaConfigured } : {}),
          ...(typeof data.tiktokConfigured === "boolean" ? { tiktok_configured: data.tiktokConfigured } : {})
        }));
      }
      setPublishResults({ instagramReel: null, tiktok: null });
      setFeedback({ message: data.message || "Social connection removed.", kind: "ok" });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setSocialLoading((prev) => ({ ...prev, disconnect: false }));
    }
  }

  async function handleSelectDefaultSocialAccount(accountId) {
    setSocialLoading((prev) => ({ ...prev, select: true }));
    setFeedback({ message: "", kind: "info" });

    try {
      const data = await apiCall(`/api/social_accounts/${accountId}/select_default`, {}, { method: "PATCH" });
      setSocialConnections(Array.isArray(data.connections) ? data.connections : []);
      setFeedback({ message: data.message || "Default social account updated.", kind: "ok" });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    } finally {
      setSocialLoading((prev) => ({ ...prev, select: false }));
    }
  }

  useEffect(() => {
    if (route !== "/billing") return;
    if (accountSubscription?.has_active_subscription) return;
    if (accountLoading.checkout) return;
    if (!selectedPlanSlugProp) return;

    const planExists = pricingCatalog?.plans?.some((p) => p.slug === selectedPlanSlugProp);
    if (planExists) {
      handleCreateCheckoutSession(selectedPlanSlugProp, selectedDiscountCodeProp);
    }
  }, [route, accountSubscription?.has_active_subscription, selectedPlanSlugProp, selectedDiscountCodeProp, pricingCatalog?.plans]);

  // Auto-refresh billing data after successful checkout
  useEffect(() => {
    if (route !== "/billing") return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get("checkout");
    
    if (checkoutStatus === "success" && !accountSubscription?.has_active_subscription) {
      // Refresh the page after a short delay to get updated subscription data
      const timer = setTimeout(() => {
        window.location.href = "/app/billing";
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [route, accountSubscription?.has_active_subscription]);

  useEffect(() => {
    setForms((prev) => {
      const selectedImageModel = prev.imageModel;
      const aspectOptions = getImageAspectRatioOptions(
        prev.imageProvider,
        selectedImageModel
      );

      const next = { ...prev };
      let changed = false;

      if (!aspectOptions.includes(next.imageAspectRatio)) {
        next.imageAspectRatio = aspectOptions[0] || DEFAULT_ASPECT_RATIO;
        changed = true;
      }

      const imageResolutionOptions = resolveImageResolutionOptions(next.imageProvider, selectedImageModel);
      const nextImageResolution = resolveManagedResolution(lockedDefaults.imageResolution, imageResolutionOptions);
      if (next.imageResolution !== nextImageResolution) {
        next.imageResolution = nextImageResolution;
        changed = true;
      }

      if (next.imageProvider === "wavespeed") {
        const params = getWavespeedImageModelParams(selectedImageModel);
        if (
          params.outputFormats.length &&
          !params.outputFormats.includes(next.wavespeedOutputFormat)
        ) {
          next.wavespeedOutputFormat = params.outputFormats[0];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [forms.imageProvider, forms.imageModel, lockedDefaults.imageResolution]);

  useEffect(() => {
    setForms((prev) => {
      const selectedImageEditModel = prev.imageEditModel;
      const aspectOptions = getImageAspectRatioOptions(
        prev.imageEditProvider,
        selectedImageEditModel
      );

      const next = { ...prev };
      let changed = false;

      if (!aspectOptions.includes(next.imageEditAspectRatio)) {
        next.imageEditAspectRatio = aspectOptions[0] || prev.imageAspectRatio || DEFAULT_ASPECT_RATIO;
        changed = true;
      }

      const imageEditResolutionOptions = resolveImageResolutionOptions(next.imageEditProvider, selectedImageEditModel);
      const nextImageEditResolution = resolveManagedResolution(lockedDefaults.imageEditResolution, imageEditResolutionOptions);
      if (next.imageEditResolution !== nextImageEditResolution) {
        next.imageEditResolution = nextImageEditResolution;
        changed = true;
      }

      if (next.imageEditProvider === "wavespeed") {
        const params = getWavespeedImageModelParams(selectedImageEditModel);
        if (
          params.outputFormats.length &&
          !params.outputFormats.includes(next.imageEditOutputFormat)
        ) {
          next.imageEditOutputFormat = params.outputFormats[0];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [forms.imageEditProvider, forms.imageEditModel, forms.imageAspectRatio, lockedDefaults.imageEditResolution]);

  useEffect(() => {
    setForms((prev) => {
      const aspectOptions = getVideoAspectRatioOptions(prev.videoModel);
      const resolutionOptions = getVideoResolutionOptions(prev.videoModel);
      const nextAspectRatio = aspectOptions.includes(prev.videoAspectRatio)
        ? prev.videoAspectRatio
        : aspectOptions[0] || DEFAULT_ASPECT_RATIO;
      const nextResolution = resolutionOptions.includes(prev.videoResolution)
        ? prev.videoResolution
        : resolutionOptions[0] || "720p";
      if (nextAspectRatio === prev.videoAspectRatio && nextResolution === prev.videoResolution) {
        return prev;
      }
      return { ...prev, videoAspectRatio: nextAspectRatio, videoResolution: nextResolution };
    });
  }, [forms.videoModel, forms.videoResolution]);

  useEffect(() => {
    if (forms.ttsModel !== "eleven_v3") return;
    if (["0", "0.0", "0.5", "1", "1.0"].includes(String(forms.stability).trim())) return;
    setForms((prev) => ({ ...prev, stability: "0.5" }));
  }, [forms.ttsModel, forms.stability]);

  useEffect(() => {
    if (!pipeline.image.url) return;
    setForms((prev) => ({ ...prev, videoImageUrl: pipeline.image.url }));
  }, [pipeline.image.url]);

  useEffect(() => {
    if (!pipeline.audio.url) return;
    setForms((prev) => ({ ...prev, videoAudioUrl: pipeline.audio.url }));
  }, [pipeline.audio.url]);

  useEffect(() => {
    if (!pipeline.copy.text) return;
    setForms((prev) => ({ ...prev, generatedCopy: pipeline.copy.text, ttsText: pipeline.copy.text }));
  }, [pipeline.copy.text]);

  function handleSaveImageGenerationSettings(nextSettings) {
    const normalizedProvider = String(nextSettings?.provider || "fal").trim().toLowerCase() === "wavespeed" ? "wavespeed" : "fal";
    const providerModels = getImageCatalogByProvider(normalizedProvider, settings.providerModels);
    const preferredDefault = normalizedProvider === "wavespeed" ? WAVESPEED_IMAGE_MODELS[0] : FAL_IMAGE_MODELS[0];
    const normalizedModel = String(nextSettings?.model || "").trim();
    if (!providerModels.length) {
      setFeedback({ message: "No image generation models available for the selected provider.", kind: "error" });
      return false;
    }
    const nextModel = providerModels.includes(normalizedModel)
      ? normalizedModel
      : providerModels.includes(preferredDefault)
        ? preferredDefault
        : providerModels[0] || preferredDefault;

    setSettings((prev) => ({
      ...prev,
      enabledModels: {
        ...prev.enabledModels,
        [normalizedProvider]: {
          ...(prev.enabledModels[normalizedProvider] || {}),
          [nextModel]: true
        }
      }
    }));

    setForms((prev) => ({
      ...prev,
      imageProvider: normalizedProvider,
      imageModel: nextModel
    }));
    setFeedback({ message: "Image generation settings saved.", kind: "ok" });
    return true;
  }

  function handleSaveCopyGenerationSettings(nextModel) {
    const normalizedModel = String(nextModel || "").trim();
    if (!normalizedModel) {
      setFeedback({ message: "Select a model for Step 3 copy generation.", kind: "error" });
      return false;
    }
    if (!activeModels.openrouter.includes(normalizedModel)) {
      setFeedback({ message: "Selected model is not available among active OpenRouter models.", kind: "error" });
      return false;
    }
    setForms((prev) => ({ ...prev, copyModel: normalizedModel }));
    setFeedback({ message: "Copy generation settings saved.", kind: "ok" });
    return true;
  }

  function handleSaveImageEditSettings(nextSettings) {
    const normalizedProvider = String(nextSettings?.provider || "fal").trim().toLowerCase() === "wavespeed" ? "wavespeed" : "fal";
    const providerModels = getImageEditCatalogByProvider(normalizedProvider, settings.providerModels);
    const preferredDefault = normalizedProvider === "wavespeed" ? DEFAULT_WAVESPEED_IMAGE_EDIT_MODEL : DEFAULT_FAL_IMAGE_EDIT_MODEL;
    const normalizedModel = String(nextSettings?.model || "").trim();
    if (!providerModels.length) {
      setFeedback({ message: "No image edit models available for the selected provider.", kind: "error" });
      return false;
    }
    const nextModel = providerModels.includes(normalizedModel)
      ? normalizedModel
      : providerModels.includes(preferredDefault)
        ? preferredDefault
        : providerModels[0] || preferredDefault;

    setSettings((prev) => ({
      ...prev,
      enabledModels: {
        ...prev.enabledModels,
        [normalizedProvider]: {
          ...(prev.enabledModels[normalizedProvider] || {}),
          [nextModel]: true
        }
      }
    }));

    setForms((prev) => ({
      ...prev,
      imageEditProvider: normalizedProvider,
      imageEditModel: nextModel
    }));
    setFeedback({ message: "Image edit settings saved.", kind: "ok" });
    return true;
  }

  async function fetchElevenLabsVoices(scopeOverride) {
    if (!settings.providerStatus?.elevenlabs) {
      throw new Error("ElevenLabs is not configured by the superadmin.");
    }

    const scope = String(scopeOverride || elevenlabsSettings.voiceScope || "my_generated").trim() || "my_generated";
    const response = await fetch(`/api/elevenlabs/voices?scope=${encodeURIComponent(scope)}`, {
      credentials: "same-origin"
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || "Voice loading error.");
    return {
      scope: data.scope || scope,
      voices: data.voices || []
    };
  }

  async function loadApprovedVoices(options = {}) {
    const { silent = false, throwOnError = false } = options;
    try {
      setLoading((prev) => ({ ...prev, voices: true }));
      const { scope, voices: fetchedVoices } = await fetchElevenLabsVoices(elevenlabsSettings.voiceScope);
      const nextApprovedVoices = uniqVoiceEntries(fetchedVoices);
      setApprovedVoices(nextApprovedVoices);

      setForms((prev) => {
        const hasCurrentVoice = nextApprovedVoices.some((voice) => voice.voice_id === prev.voiceId);
        return {
          ...prev,
          voiceId: hasCurrentVoice ? prev.voiceId : nextApprovedVoices?.[0]?.voice_id || prev.voiceId
        };
      });

      if (!silent) {
        setFeedback({
          message: `Approved voices synced (${scope}): ${nextApprovedVoices.length}`,
          kind: "ok"
        });
      }
    } catch (error) {
      setApprovedVoices([]);
      if (throwOnError) {
        throw error;
      }
      if (!silent) {
        setFeedback({ message: error.message, kind: "error" });
      }
    } finally {
      setLoading((prev) => ({ ...prev, voices: false }));
    }
  }

  function clearGenerationTaskPoll(stepKey) {
    const timerId = generationTaskPollersRef.current[stepKey];
    if (timerId) {
      window.clearTimeout(timerId);
      delete generationTaskPollersRef.current[stepKey];
    }
  }

  async function pollGenerationTask(stepKey, taskId, onSuccess) {
    try {
      const task = await apiCall(`/api/generation_tasks/${taskId}`, null, { method: "GET" });
      if (task.billingOverview) {
        setBillingOverview(task.billingOverview);
      }

      if (task.status === "queued" || task.status === "running") {
        clearGenerationTaskPoll(stepKey);
        generationTaskPollersRef.current[stepKey] = window.setTimeout(() => {
          pollGenerationTask(stepKey, taskId, onSuccess);
        }, task.status === "queued" ? 1000 : 2000);
        return;
      }

      clearGenerationTaskPoll(stepKey);
      setLoading((prev) => ({ ...prev, [stepKey]: false }));

      if (task.status === "succeeded") {
        onSuccess(task.result || {});
        return;
      }

      if (task.status === "failed") {
        setFeedback({
          message: extractApiErrorMessage(task, task.errorStatus || 422),
          kind: "error"
        });
        return;
      }

      setFeedback({ message: "Unexpected generation job status.", kind: "error" });
    } catch (error) {
      clearGenerationTaskPoll(stepKey);
      setLoading((prev) => ({ ...prev, [stepKey]: false }));
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  async function submitGenerationTask({ stepKey, endpoint, payload, startedMessage, onSuccess }) {
    clearGenerationTaskPoll(stepKey);
    setLoading((prev) => ({ ...prev, [stepKey]: true }));
    setFeedback({ message: startedMessage, kind: "info" });

    try {
      const submission = await apiCall(endpoint, payload);
      const taskId = submission?.jobId;
      if (!taskId) {
        throw new Error("Generation job was not created.");
      }

      generationTaskPollersRef.current[stepKey] = window.setTimeout(() => {
        pollGenerationTask(stepKey, taskId, onSuccess);
      }, 800);
      return true;
    } catch (error) {
      setLoading((prev) => ({ ...prev, [stepKey]: false }));
      setFeedback({ message: error.message, kind: "error" });
      return false;
    }
  }

  async function handlePublishInstagramReel(captionOverride = null) {
    const defaultInstagramAccount = getDefaultInstagramAccount(socialConnections);

    if (!socialFeatures?.meta_configured) {
      setFeedback({ message: "Meta publishing is not configured by the superadmin.", kind: "error" });
      return false;
    }
    if (!defaultInstagramAccount?.id) {
      setFeedback({ message: "Connect Meta and choose a default Instagram account first.", kind: "error" });
      return false;
    }
    if (!pipeline.video.url) {
      setFeedback({ message: "Generate a video before publishing it to Instagram.", kind: "error" });
      return false;
    }

    try {
      return await submitGenerationTask({
        stepKey: "publishInstagram",
        endpoint: "/api/publish-instagram-reel",
        payload: {
          socialAccountId: defaultInstagramAccount.id,
          videoUrl: pipeline.video.url,
          caption: String(captionOverride ?? forms.publishCaption).trim()
        },
        startedMessage: "Publishing reel to Instagram...",
        onSuccess: (data) => {
          setPublishResults((prev) => ({ ...prev, instagramReel: data }));
          const permalink = String(data?.permalink || "").trim();
          setFeedback({
            message: permalink ? "Instagram Reel published successfully. Open the permalink below to review it." : "Instagram Reel published successfully.",
            kind: "ok"
          });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
      return false;
    }
  }

  async function handleUploadTiktokVideo() {
    const defaultTiktokAccount = getDefaultTiktokAccount(socialConnections);

    if (!socialFeatures?.tiktok_configured) {
      setFeedback({ message: "TikTok uploading is not configured by the superadmin.", kind: "error" });
      return false;
    }
    if (!defaultTiktokAccount?.id) {
      setFeedback({ message: "Connect TikTok and choose a default TikTok account first.", kind: "error" });
      return false;
    }
    if (!pipeline.video.url) {
      setFeedback({ message: "Generate a video before uploading it to TikTok.", kind: "error" });
      return false;
    }
    if (!tiktokUploadScopeGranted) {
      setFeedback({ message: "Reconnect TikTok to grant the video.upload scope before uploading a draft.", kind: "error" });
      return false;
    }

    try {
      return await submitGenerationTask({
        stepKey: "publishTiktok",
        endpoint: "/api/upload-tiktok-video",
        payload: {
          socialAccountId: defaultTiktokAccount.id,
          videoUrl: pipeline.video.url
        },
        startedMessage: "Uploading draft to TikTok...",
        onSuccess: (data) => {
          setPublishResults((prev) => ({ ...prev, tiktok: data }));
          const uploadStatus = String(data?.uploadStatus || "").toUpperCase();
          setFeedback({
            message: data?.permalink
              ? "TikTok post is available. Open the link below to review it."
              : uploadStatus === "SEND_TO_USER_INBOX"
                ? "TikTok draft uploaded successfully. Open TikTok to finish posting it manually."
                : "TikTok upload completed. Review it inside TikTok.",
            kind: "ok",
            action: uploadStatus === "SEND_TO_USER_INBOX" ? { type: "tiktok_video_help" } : null
          });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
      return false;
    }
  }

  async function handleGenerateImage(event) {
    event.preventDefault();
    try {
      const provider = forms.imageProvider === "wavespeed" ? "wavespeed" : "fal";
      if (!settings.providerStatus?.[provider]) throw new Error(`${providerConfig[provider].label} is not configured by the superadmin.`);
      if (!forms.imagePrompt.trim()) throw new Error("Enter an image prompt.");
      const imageModelCatalog = getImageCatalogByProvider(provider, settings.providerModels);
      const model = imageModelCatalog.includes(forms.imageModel) ? forms.imageModel : imageModelCatalog[0];
      if (!model) throw new Error("No image generation model configured for this provider.");

      const requestPayload = {
        provider,
        model,
        prompt: forms.imagePrompt.trim(),
        aspectRatio: forms.imageAspectRatio.trim() || DEFAULT_ASPECT_RATIO,
        resolution: resolveManagedResolution(forms.imageResolution, resolveImageResolutionOptions(provider, model))
      };

      if (provider === "wavespeed") {
        requestPayload.imageSize = forms.wavespeedSizeOverride.trim();
        requestPayload.wavespeedOptions = {
          outputFormat: forms.wavespeedOutputFormat,
          resolution: resolveManagedResolution(forms.imageResolution, resolveImageResolutionOptions(provider, model)),
          enableSyncMode: forms.wavespeedEnableSyncMode,
          enableBase64Output: forms.wavespeedEnableBase64Output,
          strength: forms.wavespeedStrength,
          seed: forms.wavespeedSeed,
          image: forms.wavespeedReferenceImage,
          sizeOverride: forms.wavespeedSizeOverride
        };
      }

      await submitGenerationTask({
        stepKey: "image",
        endpoint: "/api/generate-image",
        payload: requestPayload,
        startedMessage: "Image generation in progress...",
        onSuccess: (data) => {
          if (data.billingOverview) setBillingOverview(data.billingOverview);

          setPipeline((prev) => ({
            ...prev,
            image: { approved: false, url: data.imageUrl },
            imageEdit: { approved: false, skipped: false, url: "" },
            copy: { approved: false, text: "" },
            audio: { approved: false, url: "" },
            video: { url: "" }
          }));
          setOperationCosts((prev) => ({
            ...prev,
            image: data.billing ? { ...data.billing, creditsUsed: data.creditsUsed } : null,
            imageEdit: null,
            copy: null,
            copyEnhance: null,
            ttsEstimate: null,
            tts: null,
            video: null
          }));
          setForms((prev) => ({
            ...prev,
            imageProvider: provider,
            imageModel: model,
            imageEditAspectRatio: forms.imageAspectRatio,
            imageEditPrompt: "",
            imageEditSizeOverride: "",
            generatedCopy: "",
            generatedPrimaryText: "",
            generatedHeadline: "",
            generatedDescription: "",
            generatedCaptionText: "",
            publishCaption: "",
            ttsText: "",
            videoAudioUrl: "",
            videoImageUrl: data.imageUrl
          }));
          setFeedback({
            message: `Image generated. Confirm it to continue to the next step.${formatBillingSuffix(data.billing)}`,
            kind: "ok"
          });
          setPublishResults({ instagramReel: null });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  async function handleUseUploadedInfluencerImage(imageUrl, options = {}) {
    try {
      const normalizedImageUrl = String(imageUrl || "").trim();
      if (!normalizedImageUrl) throw new Error("Upload an influencer image first.");
      const { enforceVideoAspectRatio = false, requiredAspectRatio = "9:16" } = options || {};

      if (enforceVideoAspectRatio) {
        const { width, height } = await loadImageDimensions(normalizedImageUrl);
        if (!isAspectRatioValid(width, height, requiredAspectRatio)) {
          throw new Error("We can't use this image because it doesn't match the required 9:16 aspect ratio for video generation.");
        }
      }

      setPipeline((prev) => ({
        ...prev,
        image: { approved: false, url: normalizedImageUrl },
        imageEdit: { approved: false, skipped: false, url: "" },
        copy: { approved: false, text: "" },
        audio: { approved: false, url: "" },
        video: { url: "" }
      }));
      setOperationCosts((prev) => ({
        ...prev,
        image: null,
        imageEdit: null,
        copy: null,
        copyEnhance: null,
        ttsEstimate: null,
        tts: null,
        video: null
      }));
      setForms((prev) => ({
        ...prev,
        imageEditAspectRatio: prev.imageAspectRatio,
        imageEditPrompt: "",
        imageEditSizeOverride: "",
        generatedCopy: "",
        generatedPrimaryText: "",
        generatedHeadline: "",
        generatedDescription: "",
        generatedCaptionText: "",
        publishCaption: "",
        ttsText: "",
        videoAudioUrl: "",
        videoImageUrl: normalizedImageUrl,
        ...(enforceVideoAspectRatio ? { videoAspectRatio: requiredAspectRatio } : {})
      }));
      setFeedback({ message: "Image uploaded. Confirm it to continue to the next step.", kind: "ok" });
      setPublishResults({ instagramReel: null });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  function handleApproveImage() {
    setFeedback({ message: "", kind: "info" });
    setPipeline((prev) => ({ ...prev, image: { ...prev.image, approved: true } }));
  }

  async function handleGenerateImageEdit(payload) {
    try {
      if (!pipeline.image.approved || !pipeline.image.url) throw new Error("Confirm Step 1 image before using image edit.");
      const provider = payload.provider || forms.imageEditProvider;
      if (!settings.providerStatus?.[provider]) throw new Error(`${providerConfig[provider].label} is not configured by the superadmin.`);
      if (!payload.model) throw new Error("Select an image edit model.");
      if (!String(payload.prompt || "").trim()) throw new Error("Enter an edit prompt.");

      const normalizedExtraImageDataUrls = uniq(payload.extraImageDataUrls || []);
      const requestPayload = {
        provider,
        model: payload.model,
        prompt: String(payload.prompt).trim(),
        baseImageUrl: pipeline.image.url,
        extraImageDataUrls: normalizedExtraImageDataUrls,
        extraImageDataUrl: normalizedExtraImageDataUrls[0] || String(payload.extraImageDataUrl || "").trim(),
        aspectRatio: payload.aspectRatio || forms.imageAspectRatio || DEFAULT_ASPECT_RATIO,
        imageSize: payload.imageSize || "",
        resolution: resolveManagedResolution(payload.resolution, resolveImageResolutionOptions(provider, payload.model))
      };

      if (provider === "wavespeed") {
        requestPayload.wavespeedOptions = {
          outputFormat: payload.outputFormat,
          resolution: payload.resolution,
          enableSyncMode: payload.enableSyncMode,
          enableBase64Output: payload.enableBase64Output,
          sizeOverride: payload.imageSize || ""
        };
      }

      await submitGenerationTask({
        stepKey: "imageEdit",
        endpoint: "/api/edit-image",
        payload: requestPayload,
        startedMessage: "Image edit in progress...",
        onSuccess: (data) => {
          if (data.billingOverview) setBillingOverview(data.billingOverview);

          setPipeline((prev) => ({
            ...prev,
            imageEdit: { approved: false, skipped: false, url: data.imageUrl }
          }));
          setOperationCosts((prev) => ({
            ...prev,
            imageEdit: data.billing ? { ...data.billing, creditsUsed: data.creditsUsed } : null,
            copy: null,
            copyEnhance: null,
            ttsEstimate: null,
            tts: null,
            video: null
          }));
          setForms((prev) => ({
            ...prev,
            videoImageUrl: data.imageUrl
          }));
          setFeedback({
            message: `Edited image generated. Confirm it to continue to copy.${formatBillingSuffix(data.billing)}`,
            kind: "ok"
          });
          setPublishResults({ instagramReel: null });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  function handleApproveImageEdit() {
    setFeedback({ message: "", kind: "info" });
    setPipeline((prev) => ({
      ...prev,
      image: { ...prev.image, approved: true, url: prev.imageEdit.url || prev.image.url },
      imageEdit: { ...prev.imageEdit, approved: true, skipped: false }
    }));
  }

  function handleSkipImageEdit() {
    setFeedback({ message: "", kind: "info" });
    setPipeline((prev) => ({
      ...prev,
      imageEdit: { approved: false, skipped: true, url: "" }
    }));
  }

  async function handleGenerateCopy(event) {
    event.preventDefault();
    try {
      if (!settings.providerStatus?.openrouter) throw new Error("OpenRouter is not configured by the superadmin.");
      if (!forms.copyModel) throw new Error("Select an OpenRouter model.");
      const boundedMaxSeconds = Math.min(30, Math.max(6, Number(forms.copyMaxSeconds) || 15));

      await submitGenerationTask({
        stepKey: "copy",
        endpoint: "/api/generate-copy",
        payload: {
          model: forms.copyModel,
          platform: forms.copyPlatform,
          contentType: forms.copyContentType,
          language: normalizeCopyLanguageCode(forms.copyLanguage),
          maxSeconds: boundedMaxSeconds,
          sourceUrl: forms.sourceUrl.trim(),
          sourceText: forms.sourceText.trim()
        },
        startedMessage: "Copy generation in progress...",
        onSuccess: (data) => {
          if (data.billingOverview) setBillingOverview(data.billingOverview);

          const result = data.result || {};
          const accompanyingText = result.accompanyingText || {};
          const videoScript = result.videoScript || {};
          const nextScript = String(videoScript.script || data.copy || "").trim();

          setForms((prev) => ({
            ...prev,
            copyContentType: result.contentType === "ad" ? "ad" : "organic",
            copyLanguage: prev.copyLanguageManual
              ? prev.copyLanguage
              : normalizeCopyLanguageCode(result.detectedLanguage || result.language || prev.copyLanguage || "en"),
            copyMaxSeconds: String(videoScript.maxSeconds || boundedMaxSeconds),
            generatedCopy: nextScript,
            generatedPrimaryText: String(accompanyingText.primaryText || ""),
            generatedHeadline: String(accompanyingText.headline || ""),
            generatedDescription: String(accompanyingText.description || ""),
            generatedCaptionText: String(accompanyingText.captionText || ""),
            publishCaption:
              String(accompanyingText.captionText || accompanyingText.primaryText || accompanyingText.headline || "").trim(),
            ttsText: nextScript
          }));
          setPipeline((prev) => ({
            ...prev,
            copy: { approved: false, text: "" },
            audio: { approved: false, url: "" },
            video: { url: "" }
          }));
          setOperationCosts((prev) => ({
            ...prev,
            copy: data.billing ? { ...data.billing, creditsUsed: data.creditsUsed } : null,
            copyEnhance: null,
            ttsEstimate: null,
            tts: null,
            video: null
          }));
          setFeedback({
            message: `Step 3 outputs generated. Review them and confirm to continue to TTS.${
              auth?.superadmin ? formatBillingSuffix(data.billing) : formatCreditsSuffix(data.creditsUsed)
            }`,
            kind: "ok"
          });
          setPublishResults({ instagramReel: null });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  async function handleEnhanceCopyText() {
    const baseScript = forms.generatedCopy.trim();
    if (!baseScript) {
      setFeedback({ message: "Enter or generate a script before enhancing it.", kind: "error" });
      return;
    }
    if (!settings.providerStatus?.openrouter) {
      setFeedback({
        message: "OpenRouter is not configured by the superadmin for Audio Enhancement v3.",
        kind: "error"
      });
      return;
    }

    const baseScriptTiming = analyzeScriptTiming(baseScript, forms.copyMaxSeconds);
    if (!baseScriptTiming.withinRange) {
      setFeedback({ message: buildScriptTimingValidationMessage(baseScriptTiming), kind: "error" });
      return;
    }

    try {
      const boundedMaxSeconds = Math.min(30, Math.max(6, Number(forms.copyMaxSeconds) || 15));

      await submitGenerationTask({
        stepKey: "enhance",
        endpoint: "/api/enhance-tts-script",
        payload: {
          model: forms.copyModel || activeModels.openrouter[0] || "openai/gpt-4o-mini",
          platform: forms.copyPlatform,
          contentType: forms.copyContentType,
          language: normalizeCopyLanguageCode(forms.copyLanguage),
          maxSeconds: boundedMaxSeconds,
          script: baseScript
        },
        startedMessage: "Audio Enhancement v3 in progress...",
        onSuccess: (enhancedPayload) => {
          if (enhancedPayload.billingOverview) setBillingOverview(enhancedPayload.billingOverview);
          const enhancedScript = String(enhancedPayload?.enhancedScript || "").trim() || baseScript;
          const enhancedTiming = analyzeScriptTiming(enhancedScript, forms.copyMaxSeconds);
          if (!enhancedTiming.withinRange) {
            throw new Error(buildScriptTimingValidationMessage(enhancedTiming, "Enhanced video script"));
          }

          setForms((prev) => ({
            ...prev,
            generatedCopy: enhancedScript,
            ttsText: enhancedScript
          }));
          setPipeline((prev) => ({
            ...prev,
            copy: { approved: false, text: "" },
            audio: { approved: false, url: "" },
            video: { url: "" }
          }));
          setOperationCosts((prev) => ({
            ...prev,
            copyEnhance: enhancedPayload?.billing ? { ...enhancedPayload.billing, creditsUsed: enhancedPayload.creditsUsed } : null,
            tts: null,
            video: null
          }));
          const enhancementChangedText = enhancedScript !== baseScript;
          setFeedback({
            message: `${
              enhancementChangedText
                ? "Audio tags applied. Review the script and confirm to continue to TTS."
                : "No safe tag-only enhancement was produced. The original script was preserved."
            }${auth?.superadmin ? formatBillingSuffix(enhancedPayload?.billing) : formatCreditsSuffix(enhancedPayload?.creditsUsed)}`,
            kind: "ok"
          });
          setPublishResults({ instagramReel: null });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  async function handleApproveCopy() {
    if (!forms.generatedCopy.trim()) {
      setFeedback({ message: "Enter a valid script before confirming.", kind: "error" });
      return null;
    }

    const baseScriptTiming = analyzeScriptTiming(forms.generatedCopy, forms.copyMaxSeconds);
    if (!baseScriptTiming.withinRange) {
      setFeedback({ message: buildScriptTimingValidationMessage(baseScriptTiming), kind: "error" });
      return null;
    }

    let approvedScript = forms.generatedCopy.trim();

    setPipeline((prev) => ({ ...prev, copy: { approved: true, text: approvedScript } }));
    setForms((prev) => ({
      ...prev,
      generatedCopy: approvedScript,
      ttsText: approvedScript
    }));
    setOperationCosts((prev) => ({
      ...prev,
      copyEnhance: null,
      ttsEstimate: {
        provider: "elevenlabs",
        operation: "tts_estimate",
        amount: Number(approvedScript.length || 0),
        currency: "CHARS",
        source: "step3_text_length"
      }
    }));
    setFeedback({
      message: "Text confirmed. Step 4 is ready for voice generation.",
      kind: "ok"
    });
    setPublishResults({ instagramReel: null });
    return approvedScript;
  }

  async function handleGenerateTts(event) {
    event.preventDefault();
    try {
      if (!settings.providerStatus?.elevenlabs) throw new Error("ElevenLabs is not configured by the superadmin.");
      if (!forms.voiceId) throw new Error("Select an ElevenLabs voice.");
      if (!forms.ttsText.trim()) throw new Error("Enter text for TTS.");
      const ttsScriptTiming = analyzeScriptTiming(forms.ttsText, forms.copyMaxSeconds);
      if (!ttsScriptTiming.withinRange) {
        throw new Error(buildScriptTimingValidationMessage(ttsScriptTiming, "Text for TTS"));
      }

      await submitGenerationTask({
        stepKey: "tts",
        endpoint: "/api/generate-tts",
        payload: {
          voiceId: forms.voiceId,
          text: forms.ttsText.trim(),
          modelId: forms.ttsModel,
          stability: Number(forms.stability),
          similarityBoost: Number(forms.similarityBoost)
        },
        startedMessage: "Audio generation in progress...",
        onSuccess: (data) => {
          if (data.billingOverview) setBillingOverview(data.billingOverview);

          setPipeline((prev) => ({ ...prev, audio: { approved: false, url: normalizeInternalAssetUrl(data.audioUrl) } }));
          setOperationCosts((prev) => ({
            ...prev,
            ttsEstimate: prev.ttsEstimate,
            tts: data.billing ? { ...data.billing, creditsUsed: data.creditsUsed } : null,
            video: null
          }));
          setFeedback({
            message: `Audio generated. Confirm it to unlock video generation.${
              auth?.superadmin ? formatBillingSuffix(data.billing) : formatCreditsSuffix(data.creditsUsed)
            }`,
            kind: "ok"
          });
          setPublishResults({ instagramReel: null });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  function handleApproveAudio() {
    setFeedback({ message: "", kind: "info" });
    setPipeline((prev) => ({ ...prev, audio: { ...prev.audio, approved: true } }));
  }

  async function handleOptimizeVideoPrompt() {
    const imageUrl = forms.videoImageUrl.trim();
    const scriptText = forms.ttsText.trim();
    const userPrompt = forms.videoPrompt.trim();

    if (!imageUrl || !scriptText) {
      setFeedback({
        message: "Complete the earlier steps first so Image URL and Text for voice generation are available.",
        kind: "error"
      });
      return;
    }

    if (!settings.providerStatus?.openrouter) {
      setFeedback({
        message: "OpenRouter is not configured by the superadmin for Aurora prompt optimization.",
        kind: "error"
      });
      return;
    }

    try {
      await submitGenerationTask({
        stepKey: "videoPromptOptimize",
        endpoint: "/api/optimize-video-prompt",
        payload: {
          imageUrl,
          userPrompt,
          scriptText
        },
        startedMessage: "Optimizing the Aurora video prompt...",
        onSuccess: (data) => {
          const optimizedPrompt = String(data.textPrompt || "").trim();
          if (!optimizedPrompt) {
            throw new Error("Aurora prompt optimization returned an empty prompt.");
          }

          const normalizedGuidanceScale = normalizeFalGuidanceScaleClient(data.guidanceScale);
          setForms((prev) => ({
            ...prev,
            videoPrompt: optimizedPrompt,
            videoGuidanceScale: normalizedGuidanceScale
          }));
          setFeedback({
            message: auth?.superadmin
              ? `Video prompt optimized. Suggested guidance scale applied: ${normalizedGuidanceScale}.`
              : "Video prompt optimized. Suggested guidance scale has been applied.",
            kind: "ok"
          });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  async function handleGenerateVideo(event) {
    event.preventDefault();
    try {
      if (!settings.providerStatus?.fal) throw new Error("fal.ai is not configured by the superadmin.");
      if (!forms.videoImageUrl.trim() || !forms.videoAudioUrl.trim()) throw new Error("Image URL and audio URL are required to create the video.");
      if (!forms.videoModel) throw new Error("Video model is not configured.");

      await submitGenerationTask({
        stepKey: "video",
        endpoint: "/api/generate-video",
        payload: {
          model: normalizeFalVideoModelId(forms.videoModel),
          imageUrl: forms.videoImageUrl.trim(),
          audioUrl: forms.videoAudioUrl.trim(),
          aspectRatio: forms.videoAspectRatio.trim() || DEFAULT_ASPECT_RATIO,
          videoResolution: forms.videoResolution,
          videoPrompt: forms.videoPrompt.trim(),
          expectedMaxSeconds: forms.copyMaxSeconds,
          guidanceScale: auth?.superadmin ? Number(normalizeFalGuidanceScaleClient(forms.videoGuidanceScale)) : undefined
        },
        startedMessage: "Video generation in progress...",
        onSuccess: (data) => {
          if (data.billingOverview) setBillingOverview(data.billingOverview);

          setPipeline((prev) => ({ ...prev, video: { url: data.videoUrl } }));
          setPublishResults({ instagramReel: null });
          setOperationCosts((prev) => ({
            ...prev,
            video: data.billing || null
          }));
          setFeedback({
            message: `Video generated. Download it with the dedicated button.${formatBillingSuffix(data.billing)}`,
            kind: "ok"
          });
        }
      });
    } catch (error) {
      setFeedback({ message: error.message, kind: "error" });
    }
  }

  if (route === "/") {
    return <MarketingLandingPage onLogin={() => navigate("/generation")} />;
  }

  return (
    <div className="mx-auto max-w-[1460px] px-3 pb-20 pt-6 text-slate-100 lg:px-6">
      <HeaderNav route={route} navigate={navigate} account={account} settings={settings} auth={auth} billingOverview={billingOverview} />

      {route === "/generation" && (
        <GenerationPage
          pipeline={pipeline}
          operationCosts={operationCosts}
          forms={forms}
          setForms={setForms}
          activeStep={wizardUi.activeStep}
          setActiveStep={(value) =>
            setWizardUi((prev) => ({
              ...prev,
              activeStep: typeof value === "function" ? value(prev.activeStep) : value
            }))
          }
          wizardImageMode={wizardUi.wizardImageMode}
          setWizardImageMode={(value) =>
            setWizardUi((prev) => ({
              ...prev,
              wizardImageMode: typeof value === "function" ? value(prev.wizardImageMode) : value
            }))
          }
          wizardEditIntent={wizardUi.wizardEditIntent}
          setWizardEditIntent={(value) =>
            setWizardUi((prev) => ({
              ...prev,
              wizardEditIntent: typeof value === "function" ? value(prev.wizardEditIntent) : value
            }))
          }
          wizardStarted={wizardUi.wizardStarted}
          setWizardStarted={(value) =>
            setWizardUi((prev) => ({
              ...prev,
              wizardStarted: typeof value === "function" ? value(prev.wizardStarted) : value
            }))
          }
          wizardExpanded={wizardUi.wizardExpanded}
          setWizardExpanded={(value) =>
            setWizardUi((prev) => ({
              ...prev,
              wizardExpanded: typeof value === "function" ? value(prev.wizardExpanded) : value
            }))
          }
          approvedVoices={approvedVoices}
          settings={settings}
          auth={auth}
          activeModels={activeModels}
          loading={loading}
          socialConnections={socialConnections}
          socialFeatures={socialFeatures}
          publishResults={publishResults}
          onGenerateImage={handleGenerateImage}
          onUseUploadedInfluencerImage={handleUseUploadedInfluencerImage}
          onApproveImage={handleApproveImage}
          onGenerateImageEdit={handleGenerateImageEdit}
          onApproveImageEdit={handleApproveImageEdit}
          onSkipImageEdit={handleSkipImageEdit}
          onGenerateCopy={handleGenerateCopy}
          onEnhanceCopy={handleEnhanceCopyText}
          onApproveCopy={handleApproveCopy}
          onGenerateTts={handleGenerateTts}
          onApproveAudio={handleApproveAudio}
          onOptimizeVideoPrompt={handleOptimizeVideoPrompt}
          onGenerateVideo={handleGenerateVideo}
          onPublishInstagramReel={handlePublishInstagramReel}
          onUploadTiktokVideo={handleUploadTiktokVideo}
          onOpenConnections={() => navigate("/connections")}
          onClearFeedback={() => setFeedback({ message: "", kind: "info" })}
          onSetFeedback={setFeedback}
        />
      )}

      {route === "/settings" && (
        <SettingsPage
          settings={settings}
          activeModels={activeModels}
          forms={forms}
          auth={auth}
        />
      )}

      {route === "/account" && (
        <AccountPage
          account={account}
          setAccount={setAccount}
          accountLoading={accountLoading}
          passwordForm={passwordForm}
          setPasswordForm={setPasswordForm}
          onSaveProfile={handleSaveProfile}
          onUpdatePassword={handleUpdatePassword}
          navigate={navigate}
        />
      )}

      {route === "/connections" && (
        <ConnectionsPage
          socialConnections={socialConnections}
          socialFeatures={socialFeatures}
          socialLoading={socialLoading}
          onConnectMeta={handleConnectMeta}
          onRefreshMeta={handleRefreshMeta}
          onConnectTiktok={handleConnectTiktok}
          onRefreshTiktok={handleRefreshTiktok}
          onDisconnectConnection={handleDisconnectSocialConnection}
          onSelectDefaultAccount={handleSelectDefaultSocialAccount}
        />
      )}

      {route === "/billing" && (
        <BillingPage
          billingOverview={billingOverview}
          accountSubscription={accountSubscription}
          recentCreditActivity={recentCreditActivity}
          recentProviderUsage={recentProviderUsage}
          accountLoading={accountLoading}
          pricingCatalog={pricingCatalog}
          onToggleSubscriptionRenewal={handleToggleSubscriptionRenewal}
          onOpenBillingPortal={handleOpenBillingPortal}
          onCreateCheckoutSession={handleCreateCheckoutSession}
        />
      )}

      {route === "/inspiration" && <InspirationPage settings={settings} />}

      {feedback.message && (
        <div
          className={cn(
            "fixed bottom-4 left-1/2 z-50 w-[calc(100%-1.5rem)] max-w-3xl -translate-x-1/2 rounded-xl border px-4 py-3 pr-12 text-sm shadow-xl",
            feedback.kind === "error"
              ? "border-red-500/70 bg-red-500/15 text-red-100"
              : feedback.kind === "ok"
                ? "border-emerald-500/70 bg-emerald-500/15 text-emerald-100"
                : "border-cyan-500/70 bg-cyan-500/15 text-cyan-100"
          )}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pr-4">
            <span>{feedback.message}</span>
            {feedback.action?.type === "tiktok_video_help" ? (
              <button
                type="button"
                onClick={() => setShowTiktokVideoHelpModal(true)}
                className="inline-flex items-center rounded-full border border-emerald-300/25 bg-emerald-50/10 px-3 py-1 text-xs font-semibold text-emerald-50 transition hover:bg-emerald-50/20"
              >
                Where is my video?
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setFeedback({ message: "", kind: "info" })}
            className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-md border border-current/30 bg-black/20 text-base leading-none transition hover:bg-black/35"
            aria-label="Close feedback"
            title="Close"
          >
            ×
          </button>
        </div>
      )}
      {showTiktokVideoHelpModal ? (
        <div className="fixed inset-0 z-[90] overflow-y-auto" aria-labelledby="tiktok-video-help-title" role="dialog" aria-modal="true">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div
              className="fixed inset-0 bg-slate-950/85 backdrop-blur-sm transition-opacity"
              aria-hidden="true"
              onClick={() => setShowTiktokVideoHelpModal(false)}
            />

            <div className="relative transform overflow-hidden rounded-[1.75rem] border border-slate-700 bg-[linear-gradient(180deg,rgba(15,23,42,.98),rgba(2,6,23,.99))] text-left shadow-[0_30px_90px_rgba(2,6,23,.7)] transition-all sm:my-8 sm:w-full sm:max-w-3xl">
              <div className="border-b border-slate-700/80 px-6 py-5 sm:px-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300">TikTok Upload Help</p>
                    <h3 id="tiktok-video-help-title" className="mt-2 text-[1.5rem] font-semibold tracking-tight text-slate-100">
                      Where is my video?
                    </h3>
                  </div>
                  <button
                    type="button"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-600 bg-slate-900/80 text-slate-200 transition hover:bg-slate-800"
                    onClick={() => setShowTiktokVideoHelpModal(false)}
                    aria-label="Close TikTok upload help"
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-[2]" aria-hidden="true">
                      <path d="M6 6l12 12" />
                      <path d="M18 6 6 18" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-6 sm:px-7">
                <div className="grid gap-6 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
                  <div className="flex items-center justify-center rounded-[1.35rem] border border-slate-700 bg-slate-950/70 p-3">
                    <img
                      src={TIKTOK_VIDEO_HELP_IMAGE}
                      alt="Example of the TikTok Inbox system notifications area where the uploaded video may appear."
                      className="h-auto max-h-[75vh] w-auto max-w-full rounded-[1rem] object-contain"
                    />
                  </div>

                  <div className="space-y-4 text-sm leading-7 text-slate-300">
                    <p>
                      TikTok&apos;s upload flow sends the video to your TikTok inbox, not directly to your public profile.
                    </p>
                    <p>
                      Open the TikTok mobile app, switch to the connected account, go to <span className="font-semibold text-slate-100">Inbox</span>, and look for a <span className="font-semibold text-slate-100">System notifications</span> item from TikTok Platform.
                    </p>
                    <p>
                      Tap that notification to open the editing flow and continue posting manually. The upload may not appear in the Drafts grid until TikTok opens that flow on the device.
                    </p>
                    <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-xs leading-6 text-cyan-100">
                      If you do not see the notification immediately, close and reopen the TikTok mobile app and make sure you are on the same connected account.
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end border-t border-slate-700/80 px-6 py-4 sm:px-7">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-600 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-slate-800"
                  onClick={() => setShowTiktokVideoHelpModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

async function apiCall(url, payload, options = {}) {
  const response = await fetch(url, {
    method: options.method || "POST",
    credentials: "same-origin",
    headers: buildJsonHeaders(options.headers || {}),
    ...(options.method === "GET" ? {} : { body: JSON.stringify(payload || {}) })
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(extractApiErrorMessage(data, response.status));
  return data;
}

function extractApiErrorMessage(data, status) {
  const directMessage =
    data?.error ||
    data?.message ||
    data?.details?.error ||
    data?.details?.message ||
    data?.details?.detail;
  if (typeof directMessage === "string" && directMessage.trim()) return directMessage.trim();

  if (Array.isArray(data?.details?.detail)) {
    const nested = data.details.detail
      .map((entry) => (typeof entry === "string" ? entry : entry?.msg || entry?.message || entry?.detail || ""))
      .find((entry) => typeof entry === "string" && entry.trim());
    if (nested) return nested.trim();
  }

  if (Array.isArray(data?.detail)) {
    const nested = data.detail
      .map((entry) => (typeof entry === "string" ? entry : entry?.msg || entry?.message || entry?.detail || ""))
      .find((entry) => typeof entry === "string" && entry.trim());
    if (nested) return nested.trim();
  }

  if (typeof data?.details?.raw === "string" && data.details.raw.trim()) return data.details.raw.trim();
  return `API error (${status})`;
}

function buildJsonHeaders(extraHeaders = {}) {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
  return {
    "Content-Type": "application/json",
    ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    ...extraHeaders
  };
}
